import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import {STAGE_VIEW_MODES} from '../../reducers/stage-view-mode';
import {
    LEGACY_LOCAL_3D_SCENE_STORAGE_KEYS,
    LOCAL_SCRATCH3D_PROJECT_STORAGE_KEY,
    createScratch3DProjectPayload,
    editorStateFromPayload,
    sceneStateFromPayload
} from '../../lib/scratch3d/project-model';
import Stage3DBlockWorkspace, {THREE_D_BLOCK_PALETTE} from './stage-3d-block-workspace.jsx';
import styles from './stage-3d.css';

const GRID_SIZE = 50;
const INNER_BOUNDARY_SIZE = 30;
const CELL_UNIT = 10;
const TILE_WIDTH = 28;
const TILE_HEIGHT = 14;
const MIN_ZOOM = 0.45;
const DEFAULT_MAX_ZOOM = 2.6;
const MAX_ZOOM_VISIBLE_GRID_SIZE = 10;
const MAX_ZOOM_VIEW_PADDING = 1.08;
const Z_HEIGHT = 12;
const HISTORY_LIMIT = 30;
const TERRAIN_BLOCK_HEIGHT = CELL_UNIT;
const BRUSH_SIZE_OPTIONS = [1, 3, 5];
const MAX_TERRAIN_HEIGHT_LEVELS = 8;
const MAX_SCRIPT_STEPS_PER_RUN = 3000;
const RUNTIME_WARNING_COOLDOWN_MS = 700;
const NON_PASSIVE_WHEEL_EVENT_OPTIONS = {passive: false};
const BEST_VIEW_PADDING_RATIO = 0.14;
const BEST_VIEW_SINGLE_OBJECT_MIN_CELLS = 10;
const BEST_VIEW_CLUSTER_MIN_CELLS = 6;
const BEST_VIEW_SINGLE_OBJECT_MAX_VISUAL_ZOOM = 1.8;
const BEST_VIEW_CLUSTER_MAX_VISUAL_ZOOM = 1.65;

const EDIT_TOOLS = {
    SELECT: 'select',
    BOX_SELECT: 'boxSelect',
    PLACE: 'place',
    PAINT: 'paint',
    RAISE_TERRAIN: 'raiseTerrain',
    LOWER_TERRAIN: 'lowerTerrain',
    ERASE: 'erase',
    REPLACE: 'replace',
    FILL: 'fill',
    MOVE_SELECTION: 'moveSelection',
    PASTE: 'paste'
};

const EDIT_TOOL_LABELS = {
    [EDIT_TOOLS.SELECT]: '选择',
    [EDIT_TOOLS.BOX_SELECT]: '框选',
    [EDIT_TOOLS.PLACE]: '叠放',
    [EDIT_TOOLS.PAINT]: '铺地',
    [EDIT_TOOLS.RAISE_TERRAIN]: '升高',
    [EDIT_TOOLS.LOWER_TERRAIN]: '降低',
    [EDIT_TOOLS.ERASE]: '拆除',
    [EDIT_TOOLS.REPLACE]: '换材质',
    [EDIT_TOOLS.FILL]: '填充',
    [EDIT_TOOLS.MOVE_SELECTION]: '移动',
    [EDIT_TOOLS.PASTE]: '粘贴'
};

const COLLISION_MODES = {
    PASS_THROUGH: 'passThrough',
    SOLID: 'solid',
    TRIGGER: 'trigger',
    DECORATION: 'decoration'
};

const COLLISION_MODE_LABELS = {
    [COLLISION_MODES.PASS_THROUGH]: '可穿透',
    [COLLISION_MODES.SOLID]: '实体阻挡',
    [COLLISION_MODES.TRIGGER]: '触发器',
    [COLLISION_MODES.DECORATION]: '仅装饰'
};

const COLLISION_MODE_DESCRIPTIONS = {
    [COLLISION_MODES.PASS_THROUGH]: '可经过，可被侦测',
    [COLLISION_MODES.SOLID]: '阻挡角色移动',
    [COLLISION_MODES.TRIGGER]: '可经过，后续触发事件',
    [COLLISION_MODES.DECORATION]: '不参与碰撞和侦测'
};

const BLOCKING_COLLISION_MODES = [COLLISION_MODES.SOLID];

const OBJECT_STATES = [
    {value: 'normal', label: '普通'},
    {value: 'open', label: '打开'},
    {value: 'closed', label: '关闭'},
    {value: 'locked', label: '锁住'},
    {value: 'active', label: '启动'},
    {value: 'inactive', label: '停止'},
    {value: 'collected', label: '已收集'}
];

const OBJECT_VARIABLES = [
    {value: 'score', label: '分数'},
    {value: 'energy', label: '能量'},
    {value: 'count', label: '数量'},
    {value: 'flag', label: '开关'}
];

const DEFAULT_OBJECT_VARIABLES = OBJECT_VARIABLES.reduce((variables, variable) => ({
    ...variables,
    [variable.value]: 0
}), {});

const OBJECT_MESSAGES = [
    {value: 'startQuest', label: '开始任务'},
    {value: 'openSwitch', label: '打开机关'},
    {value: 'collected', label: '收集完成'},
    {value: 'levelClear', label: '通关成功'}
];

const SCRATCH3D_XMLNS = 'http://www.w3.org/1999/xhtml';

const scratch3DFieldXml = (name, value) => `<field name="${name}">${value}</field>`;
const scratch3DNumberValueXml = (name, value) => (
    `<value name="${name}"><shadow type="math_number"><field name="NUM">${value}</field></shadow></value>`
);
const scratch3DBlockValueXml = (name, blockXml) => `<value name="${name}">${blockXml}</value>`;
const scratch3DStatementXml = (name, blockXml) => `<statement name="${name}">${blockXml}</statement>`;
const scratch3DBlockXml = (type, body = '', next = '', attrs = '') => (
    `<block type="${type}"${attrs}>${body}${next ? `<next>${next}</next>` : ''}</block>`
);
const scratch3DWorkspaceXml = blocks => `<xml xmlns="${SCRATCH3D_XMLNS}">${blocks}</xml>`;
const scratch3DWorkspaceHasBlocks = blockWorkspaceXml => /<block[\s>]/.test(String(blockWorkspaceXml || ''));

const INTERACTION_TEMPLATES = [
    {
        id: 'send-open-switch-on-click',
        label: '点击后发送“打开机关”',
        description: '适合按钮、角色或道具：点击自己后通知其他对象执行机关逻辑。',
        buildWorkspaceXml: () => scratch3DWorkspaceXml(
            scratch3DBlockXml(
                'scratch3d_event_when_click_self',
                '',
                scratch3DBlockXml(
                    'scratch3d_event_send_message_to_object',
                    scratch3DFieldXml('MESSAGE', 'openSwitch') +
                        scratch3DFieldXml('OBJECT', '*')
                ),
                ' x="40" y="40"'
            )
        )
    },
    {
        id: 'door-open-on-message',
        label: '门：收到“打开机关”后打开',
        description: '门收到消息后播放开门动画，状态切到打开，并变为可穿透。',
        kinds: ['door'],
        collisionMode: COLLISION_MODES.SOLID,
        stateValue: 'closed',
        buildWorkspaceXml: () => scratch3DWorkspaceXml(
            scratch3DBlockXml(
                'scratch3d_event_when_receive_message',
                scratch3DFieldXml('MESSAGE', 'openSwitch'),
                scratch3DBlockXml(
                    'scratch3d_looks_play_animation',
                    scratch3DFieldXml('ANIMATION', 'open'),
                    scratch3DBlockXml(
                        'scratch3d_looks_wait_animation_done',
                        '',
                        scratch3DBlockXml(
                            'scratch3d_looks_set_state',
                            scratch3DFieldXml('STATE', 'open'),
                            scratch3DBlockXml(
                                'scratch3d_looks_set_collision',
                                scratch3DFieldXml('COLLISION', COLLISION_MODES.PASS_THROUGH)
                            )
                        )
                    )
                ),
                ' x="40" y="40"'
            )
        )
    },
    {
        id: 'collectible-click',
        label: '物品：点击后收集',
        description: '点击物品后分数加 1，状态变为已收集，隐藏自己并广播收集完成。',
        kinds: ['crate', 'flower', 'grass', 'bush', 'image'],
        collisionMode: COLLISION_MODES.PASS_THROUGH,
        buildWorkspaceXml: () => scratch3DWorkspaceXml(
            scratch3DBlockXml(
                'scratch3d_event_when_click_self',
                '',
                scratch3DBlockXml(
                    'scratch3d_variable_change',
                    scratch3DFieldXml('VARIABLE', 'score') + scratch3DNumberValueXml('VALUE', 1),
                    scratch3DBlockXml(
                        'scratch3d_looks_set_state',
                        scratch3DFieldXml('STATE', 'collected'),
                        scratch3DBlockXml(
                            'scratch3d_looks_hide',
                            '',
                            scratch3DBlockXml(
                                'scratch3d_event_broadcast_message',
                                scratch3DFieldXml('MESSAGE', 'collected')
                            )
                        )
                    )
                ),
                ' x="40" y="40"'
            )
        )
    },
    {
        id: 'trigger-open-switch',
        label: '触发器：碰到对象后开机关',
        description: '把当前对象变成触发器，被角色经过时广播“打开机关”。',
        collisionMode: COLLISION_MODES.TRIGGER,
        stateValue: 'active',
        buildWorkspaceXml: () => scratch3DWorkspaceXml(
            scratch3DBlockXml(
                'scratch3d_event_when_touching_object',
                scratch3DFieldXml('OBJECT', '*'),
                scratch3DBlockXml(
                    'scratch3d_event_broadcast_message',
                    scratch3DFieldXml('MESSAGE', 'openSwitch')
                ),
                ' x="40" y="40"'
            )
        )
    },
    {
        id: 'role-walk-on-message',
        label: '角色：收到“开始任务”后前进',
        description: '角色收到开始任务消息后播放行走动画，向前走 4 格后停止动画。',
        kinds: ['role'],
        buildWorkspaceXml: () => scratch3DWorkspaceXml(
            scratch3DBlockXml(
                'scratch3d_event_when_receive_message',
                scratch3DFieldXml('MESSAGE', 'startQuest'),
                scratch3DBlockXml(
                    'scratch3d_looks_play_animation',
                    scratch3DFieldXml('ANIMATION', 'walk'),
                    scratch3DBlockXml(
                        'scratch3d_control_repeat',
                        scratch3DNumberValueXml('TIMES', 4) +
                            scratch3DStatementXml(
                                'SUBSTACK',
                                scratch3DBlockXml(
                                    'scratch3d_motion_move_forward',
                                    scratch3DNumberValueXml('STEPS', 1),
                                    scratch3DBlockXml(
                                        'scratch3d_control_wait',
                                        scratch3DNumberValueXml('SECONDS', 0.2)
                                    )
                                )
                            ),
                        scratch3DBlockXml('scratch3d_looks_stop_animation')
                    )
                ),
                ' x="40" y="40"'
            )
        )
    }
];

const COMMON_ANIMATIONS = [
    {id: 'idle', label: '默认', type: 'costume', duration: 0, loop: true},
    {id: 'walk', label: '行走', type: 'runtimeTransform', duration: 700, loop: true},
    {id: 'jump', label: '跳起', type: 'runtimeTransform', duration: 650, loop: false},
    {id: 'sway', label: '摇晃', type: 'runtimeTransform', duration: 1200, loop: true},
    {id: 'spin', label: '旋转', type: 'runtimeTransform', duration: 900, loop: true},
    {id: 'blink', label: '闪烁', type: 'runtimeTransform', duration: 800, loop: true},
    {id: 'grow', label: '生长', type: 'runtimeTransform', duration: 900, loop: false},
    {id: 'open', label: '打开', type: 'runtimeTransform', duration: 600, loop: false},
    {id: 'close', label: '关闭', type: 'runtimeTransform', duration: 600, loop: false}
];

const WORKBENCH_TABS = {
    SCENE: 'scene',
    ASSETS: 'assets',
    PROPERTIES: 'properties',
    CODE: 'code'
};

const WORKBENCH_TAB_LABELS = {
    [WORKBENCH_TABS.SCENE]: '场景',
    [WORKBENCH_TABS.ASSETS]: '素材',
    [WORKBENCH_TABS.PROPERTIES]: '属性',
    [WORKBENCH_TABS.CODE]: '编程'
};

const SCRIPT_EVENTS = [
    {value: 'start', label: '当开始运行'},
    {value: 'click', label: '当点击自己'}
];

const SCRIPT_ACTIONS = [
    {value: 'playAnimation', label: '播放动画'},
    {value: 'stopAnimation', label: '停止动画'},
    {value: 'setState', label: '设置状态'},
    {value: 'moveBy', label: '移动'},
    {value: 'moveTo', label: '移到坐标'},
    {value: 'jump', label: '跳起'},
    {value: 'show', label: '显示'},
    {value: 'hide', label: '隐藏'}
];

const SCRIPT_CONDITIONS = [
    {value: 'always', label: '总是执行'},
    {value: 'state', label: '当状态是'},
    {value: 'touching', label: '当碰到对象'},
    {value: 'near', label: '当距离小于'}
];

const SCRIPT_DIRECTIONS = [
    {value: 'rightUp', label: '右上', delta: {x: 1, y: 0}},
    {value: 'leftUp', label: '左上', delta: {x: 0, y: 1}},
    {value: 'rightDown', label: '右下', delta: {x: 0, y: -1}},
    {value: 'leftDown', label: '左下', delta: {x: -1, y: 0}},
    {value: 'up', label: '上', delta: {x: 1, y: 1}},
    {value: 'down', label: '下', delta: {x: -1, y: -1}}
];

const POSITION_AXES = ['x', 'y', 'z'];

const DEFAULT_STATE = {
    enabled: false,
    type: 'Scene2D5',
    schemaVersion: 1,
    gridSize: GRID_SIZE,
    cellSize: CELL_UNIT,
    outerBounds: GRID_SIZE,
    innerBounds: INNER_BOUNDARY_SIZE,
    background: '#dbeafe',
    customAssets: [],
    programming: {
        variables: OBJECT_VARIABLES,
        messages: OBJECT_MESSAGES
    },
    weather: {
        type: 'sunny',
        temperature: 24,
        windForce: 'medium',
        lighting: 'medium',
        ultraviolet: 'medium'
    },
    objects: {}
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const clone = value => JSON.parse(JSON.stringify(value));

const createObjectProgramState = (program = {}) => ({
    version: Number(program.version || program.blockScriptVersion || 1) || 1,
    source: program.source || 'blocks',
    blockWorkspaceXml: typeof program.blockWorkspaceXml === 'string' ? program.blockWorkspaceXml : '',
    scripts: Array.isArray(program.scripts) ? clone(program.scripts) : []
});

const normalizeObjectProgram = object => createObjectProgramState({
    ...(object && object.program ? object.program : {}),
    blockScriptVersion: object && object.blockScriptVersion,
    blockWorkspaceXml: object && typeof object.blockWorkspaceXml === 'string' ?
        object.blockWorkspaceXml :
        object && object.program && object.program.blockWorkspaceXml,
    scripts: Array.isArray(object && object.scripts) ?
        object.scripts :
        object && object.program && object.program.scripts
});

const normalizeProgrammingDefinitions = (items, fallbackItems) => {
    const seen = new Set();
    const normalized = (Array.isArray(items) ? items : [])
        .map(item => {
            if (typeof item === 'string') {
                return {value: item, label: item};
            }
            if (!item || typeof item !== 'object') return null;
            const value = String(item.value || item.id || '').trim();
            const label = String(item.label || item.name || value).trim();
            return value && label ? {value, label} : null;
        })
        .filter(Boolean)
        .filter(item => {
            if (seen.has(item.value)) return false;
            seen.add(item.value);
            return true;
        });
    return normalized.length ? normalized : clone(fallbackItems);
};

const normalizeProgrammingState = programming => ({
    variables: normalizeProgrammingDefinitions(
        programming && programming.variables,
        OBJECT_VARIABLES
    ),
    messages: normalizeProgrammingDefinitions(
        programming && programming.messages,
        OBJECT_MESSAGES
    )
});

const normalizeAnimation = animation => {
    if (!animation || typeof animation !== 'object') {
        return null;
    }
    return {
        id: animation.id || animation.animationId || 'idle',
        playing: Boolean(animation.playing),
        startedAt: Number(animation.startedAt || Date.now()),
        loop: animation.loop,
        height: Number(animation.height || 1)
    };
};

const defaultCollisionModeForKind = kind => {
    if (kind === 'terrain' || kind === 'ground') return COLLISION_MODES.DECORATION;
    if (kind === 'grass' || kind === 'flower' || kind === 'bush') return COLLISION_MODES.PASS_THROUGH;
    return COLLISION_MODES.SOLID;
};

const normalizeCollisionMode = (object = {}, kind = object.kind || object.type || 'ground') => {
    const mode = object.collisionMode || (object.behavior && object.behavior.collisionMode);
    if (Object.keys(COLLISION_MODE_LABELS).includes(mode)) {
        return mode;
    }
    if (object.collision || (object.behavior && object.behavior.collidable)) {
        return COLLISION_MODES.SOLID;
    }
    if (object.behavior && object.behavior.triggerEnabled) {
        return COLLISION_MODES.TRIGGER;
    }
    return defaultCollisionModeForKind(kind);
};

const normalizeSceneObject = (name, object = {}) => {
    const stateValue = typeof object.state === 'string' ?
        object.state :
        ((object.state && object.state.value) || 'normal');
    const kind = object.kind || object.type || 'ground';
    const programmable = object.programmable !== false && kind !== 'terrain';
    const collisionMode = normalizeCollisionMode(object, kind);
    const collidable = collisionMode === COLLISION_MODES.SOLID;
    const triggerEnabled = collisionMode === COLLISION_MODES.TRIGGER;
    const sensorEnabled = collisionMode !== COLLISION_MODES.DECORATION;
    const program = normalizeObjectProgram(object);
    return {
        ...object,
        id: object.id || name,
        name: object.name || name,
        kind,
        position: {
            x: Number((object.position && object.position.x) || object.x || 0),
            y: Number((object.position && object.position.y) || object.y || 0),
            z: Number((object.position && object.position.z) || object.z || 0)
        },
        rotation: object.rotation || {x: 0, y: 0, z: 0},
        scale: object.scale || {x: 1, y: 1, z: 1},
        visible: object.visible !== false,
        programmable,
        interactive: object.interactive !== false && programmable,
        collision: collidable,
        collisionMode,
        behavior: {
            programmable,
            clickable: object.clickable !== false && programmable,
            ...(object.behavior || {}),
            collisionMode,
            collidable,
            sensorEnabled,
            triggerEnabled
        },
        state: {
            ...(typeof object.state === 'object' ? object.state : {}),
            value: stateValue
        },
        variables: {
            ...DEFAULT_OBJECT_VARIABLES,
            ...((object.variables && typeof object.variables === 'object') ? object.variables : {})
        },
        program,
        scripts: program.scripts,
        blockWorkspaceXml: program.blockWorkspaceXml,
        blockScriptVersion: program.version,
        animations: Array.isArray(object.animations) ? object.animations : [],
        currentCostume: object.currentCostume || object.costumeId || 'default',
        animation: normalizeAnimation(object.animation),
        animationState: object.animationState || (object.animation && object.animation.id) || 'idle'
    };
};

const normalizeSceneObjects = objects => Object.keys(objects || {}).reduce((normalized, name) => {
    normalized[name] = normalizeSceneObject(name, objects[name]);
    return normalized;
}, {});

const WEATHER_TYPES = [
    {value: 'sunny', label: '晴朗'},
    {value: 'rainy', label: '雨天'},
    {value: 'cloudy', label: '阴天'},
    {value: 'windy', label: '大风'}
];

const STRENGTH_OPTIONS = [
    {value: 'low', label: '弱 / 小'},
    {value: 'medium', label: '中'},
    {value: 'high', label: '强 / 大'},
    {value: 'extreme', label: '超强 / 超大'}
];

const WEATHER_PROPERTY_KEYS = ['type', 'temperature', 'windForce', 'lighting', 'ultraviolet'];

const normalizeSceneState = state => {
    const normalized = {
        ...clone(DEFAULT_STATE),
        ...(state ? clone(state) : {}),
        weather: {
            ...clone(DEFAULT_STATE.weather),
            ...((state && state.weather) ? clone(state.weather) : {})
        },
        programming: normalizeProgrammingState(state && state.programming),
        customAssets: Array.isArray(state && state.customAssets) ? clone(state.customAssets) : [],
        objects: normalizeSceneObjects((state && state.objects) ? clone(state.objects) : {}),
        scripts: Array.isArray(state && state.scripts) ? clone(state.scripts) : []
    };
    delete normalized.goals;
    return normalized;
};

const BUILTIN_ASSET_GROUPS = [
    {
        id: 'terrain',
        label: '地形编辑',
        assets: [
            {
                id: 'grass-block',
                label: '草地方块',
                kind: 'terrain',
                color: '#69b34c',
                topColor: '#7fca54',
                sideColor: '#7a5230',
                size: 1
            },
            {
                id: 'dirt-block',
                label: '泥土方块',
                kind: 'terrain',
                color: '#8b5e34',
                topColor: '#9a6a3b',
                sideColor: '#714827',
                size: 1
            },
            {
                id: 'stone-block',
                label: '石头方块',
                kind: 'terrain',
                color: '#8f9aa3',
                topColor: '#aeb7bf',
                sideColor: '#6f7a82',
                size: 1
            },
            {
                id: 'sand-block',
                label: '沙地方块',
                kind: 'terrain',
                color: '#e7cf86',
                topColor: '#f4df9d',
                sideColor: '#c8ad64',
                size: 1
            },
            {
                id: 'water-block',
                label: '水面方块',
                kind: 'terrain',
                color: '#38bdf8',
                topColor: '#67d4ff',
                sideColor: '#168ac2',
                size: 1
            },
            {
                id: 'wood-block',
                label: '木板方块',
                kind: 'terrain',
                color: '#b7793a',
                topColor: '#d0944f',
                sideColor: '#8f572b',
                size: 1
            },
            {
                id: 'path-block',
                label: '石砖路面',
                kind: 'terrain',
                color: '#9ca3af',
                topColor: '#cbd5e1',
                sideColor: '#6b7280',
                size: 1
            },
            {
                id: 'flowerbed-block',
                label: '花坛土块',
                kind: 'terrain',
                color: '#7c4a2d',
                topColor: '#9b5f38',
                sideColor: '#5f341f',
                size: 1
            }
        ]
    },
    {
        id: 'ground-assets',
        label: '地面素材库',
        assets: [
            {
                id: 'oak-tree',
                label: '树木',
                kind: 'tree',
                color: '#28a745',
                size: 1.15,
                animations: ['idle', 'sway']
            },
            {
                id: 'grass-tuft',
                label: '草丛',
                kind: 'grass',
                color: '#43a047',
                size: 0.85,
                animations: ['idle', 'sway', 'grow']
            },
            {
                id: 'red-flower',
                label: '红花',
                kind: 'flower',
                color: '#ef4444',
                accentColor: '#facc15',
                size: 0.75,
                animations: ['idle', 'sway', 'grow']
            },
            {
                id: 'bush',
                label: '灌木',
                kind: 'bush',
                color: '#2f9e44',
                size: 0.95,
                animations: ['idle', 'sway', 'grow']
            },
            {
                id: 'role',
                label: '角色',
                kind: 'role',
                color: '#4E97FF',
                size: 1,
                animations: ['idle', 'walk', 'jump', 'spin', 'blink']
            },
            {
                id: 'crate',
                label: '箱子',
                kind: 'crate',
                color: '#b7793a',
                topColor: '#d59b58',
                sideColor: '#8f572b',
                size: 0.9,
                animations: ['idle', 'open', 'close', 'blink']
            },
            {
                id: 'door',
                label: '门',
                kind: 'door',
                color: '#9a5b2f',
                size: 1.05,
                animations: ['idle', 'open', 'close']
            },
            {
                id: 'house',
                label: '房屋',
                kind: 'house',
                color: '#ef7d57',
                size: 1.35,
                animations: ['idle', 'blink']
            },
            {
                id: 'park-bench',
                label: '公园长椅',
                kind: 'bench',
                color: '#8b5e34',
                topColor: '#b7793a',
                sideColor: '#5f3a1d',
                size: 1,
                animations: ['idle', 'blink']
            },
            {
                id: 'park-lamp',
                label: '路灯',
                kind: 'lamp',
                color: '#475569',
                accentColor: '#fde68a',
                size: 1.1,
                animations: ['idle', 'blink']
            },
            {
                id: 'park-fountain',
                label: '喷泉',
                kind: 'fountain',
                color: '#60a5fa',
                topColor: '#93c5fd',
                sideColor: '#64748b',
                size: 1.25,
                animations: ['idle', 'spin', 'blink']
            },
            {
                id: 'park-sign',
                label: '指示牌',
                kind: 'sign',
                color: '#16a34a',
                topColor: '#22c55e',
                sideColor: '#854d0e',
                size: 0.9,
                animations: ['idle', 'blink']
            },
            {
                id: 'recycle-bin',
                label: '回收桶',
                kind: 'trashBin',
                color: '#2563eb',
                topColor: '#60a5fa',
                sideColor: '#1d4ed8',
                size: 0.85,
                animations: ['idle', 'blink']
            }
        ]
    }
];

const ASSETS = BUILTIN_ASSET_GROUPS.reduce((items, group) => items.concat(group.assets), []);

const assetDefinitionById = assetId => ASSETS.find(asset => asset.id === assetId) || ASSETS[0];
const assetDefinitionForTemplateObject = object => {
    const asset = ASSETS.find(item => item.id === object.assetId);
    if (asset) return asset;

    const fallbackByKind = {
        ground: 'grass-block',
        tree: 'oak-tree',
        role: 'role',
        house: 'house',
        door: 'door',
        crate: 'crate'
    };
    return assetDefinitionById(fallbackByKind[object.kind] || object.assetId);
};

const SCENE_TEMPLATE_CATEGORIES = [
    {id: 'all', label: '全部'},
    {id: 'basic', label: '基础'},
    {id: 'building', label: '建筑'},
    {id: 'teaching', label: '教学'}
];

const blockWorkspaceXmlFromTemplate = templateId => {
    const template = INTERACTION_TEMPLATES.find(item => item.id === templateId);
    return template ? template.buildWorkspaceXml() : '';
};

const sceneTemplateObject = (assetId, position, options = {}) => {
    const asset = assetDefinitionById(assetId);
    const kind = options.kind || asset.kind;
    const collisionMode = options.collisionMode || defaultCollisionModeForKind(kind);
    const programmable = options.programmable !== undefined ? options.programmable : kind !== 'terrain';
    const blockWorkspaceXml = options.blockWorkspaceXml || '';
    const program = createObjectProgramState({
        ...(options.program || {}),
        source: 'blocks',
        blockWorkspaceXml,
        version: 1
    });

    return {
        kind,
        assetId,
        color: options.color || asset.color,
        topColor: options.topColor || asset.topColor,
        sideColor: options.sideColor || asset.sideColor,
        accentColor: options.accentColor || asset.accentColor,
        size: options.size || asset.size || 1,
        position,
        scale: options.scale || {x: 1, y: 1, z: 1},
        layer: options.layer || 0,
        visible: options.visible !== false,
        programmable,
        interactive: options.interactive !== undefined ? options.interactive : programmable,
        collision: collisionMode === COLLISION_MODES.SOLID,
        collisionMode,
        behavior: {
            programmable,
            clickable: programmable,
            collisionMode,
            collidable: collisionMode === COLLISION_MODES.SOLID,
            sensorEnabled: collisionMode !== COLLISION_MODES.DECORATION,
            triggerEnabled: collisionMode === COLLISION_MODES.TRIGGER,
            ...(options.behavior || {})
        },
        state: {value: options.stateValue || 'normal'},
        variables: {
            ...DEFAULT_OBJECT_VARIABLES,
            ...(options.variables || {})
        },
        facingDirection: options.facingDirection,
        animations: options.animations || asset.animations || [],
        animationState: options.animationState || 'idle',
        program,
        scripts: program.scripts,
        blockWorkspaceXml: program.blockWorkspaceXml,
        blockScriptVersion: program.version
    };
};

const createMoveForwardWaitBlockXml = (seconds = 0.25) => scratch3DBlockXml(
    'scratch3d_motion_move_forward',
    scratch3DNumberValueXml('STEPS', 1),
    scratch3DBlockXml(
        'scratch3d_control_wait',
        scratch3DNumberValueXml('SECONDS', seconds)
    )
);

const createRepeatMoveForwardBlockXml = (times, seconds, next = '') => scratch3DBlockXml(
    'scratch3d_control_repeat',
    scratch3DNumberValueXml('TIMES', times) +
        scratch3DStatementXml(
            'SUBSTACK',
            createMoveForwardWaitBlockXml(seconds)
        ),
    next
);

const createRoleDoorDemoWorkspaceXml = () => scratch3DWorkspaceXml(
    scratch3DBlockXml(
        'scratch3d_event_when_start',
        '',
        scratch3DBlockXml(
            'scratch3d_motion_face_direction',
            scratch3DFieldXml('DIRECTION', 'rightUp'),
            scratch3DBlockXml(
                'scratch3d_looks_play_animation',
                scratch3DFieldXml('ANIMATION', 'walk'),
                createRepeatMoveForwardBlockXml(
                    3,
                    0.25,
                    scratch3DBlockXml(
                        'scratch3d_control_wait_until',
                        scratch3DBlockValueXml(
                            'CONDITION',
                            scratch3DBlockXml(
                                'scratch3d_sensing_object_state_is',
                                scratch3DFieldXml('OBJECT', '大门1') +
                                    scratch3DFieldXml('STATE', 'open')
                            )
                        ),
                        createRepeatMoveForwardBlockXml(
                            5,
                            0.25,
                            scratch3DBlockXml(
                                'scratch3d_looks_stop_animation',
                                '',
                                scratch3DBlockXml(
                                    'scratch3d_event_broadcast_message',
                                    scratch3DFieldXml('MESSAGE', 'levelClear')
                                )
                            )
                        )
                    )
                )
            )
        ),
        ' x="40" y="40"'
    )
);

const createTriggerSwitchWorkspaceXml = targetName => scratch3DWorkspaceXml(
    scratch3DBlockXml(
        'scratch3d_event_when_touching_object',
        scratch3DFieldXml('OBJECT', targetName),
        scratch3DBlockXml(
            'scratch3d_looks_play_animation',
            scratch3DFieldXml('ANIMATION', 'blink'),
            scratch3DBlockXml(
                'scratch3d_looks_set_state',
                scratch3DFieldXml('STATE', 'active'),
                scratch3DBlockXml(
                    'scratch3d_event_broadcast_message',
                    scratch3DFieldXml('MESSAGE', 'openSwitch')
                )
            )
        ),
        ' x="40" y="40"'
    )
);

const createOpenDoorButtonWorkspaceXml = targetName => scratch3DWorkspaceXml(
    scratch3DBlockXml(
        'scratch3d_event_when_click_self',
        '',
        scratch3DBlockXml(
            'scratch3d_event_send_message_to_object',
            scratch3DFieldXml('MESSAGE', 'openSwitch') +
                scratch3DFieldXml('OBJECT', targetName)
        ),
        ' x="40" y="40"'
    )
);

const createRewardChestWorkspaceXml = () => scratch3DWorkspaceXml(
    scratch3DBlockXml(
        'scratch3d_event_when_receive_message',
        scratch3DFieldXml('MESSAGE', 'levelClear'),
        scratch3DBlockXml(
            'scratch3d_looks_show',
            '',
            scratch3DBlockXml(
                'scratch3d_looks_set_state',
                scratch3DFieldXml('STATE', 'active'),
                scratch3DBlockXml(
                    'scratch3d_looks_play_animation',
                    scratch3DFieldXml('ANIMATION', 'blink')
                )
            )
        ),
        ' x="40" y="40"'
    ) +
    scratch3DBlockXml(
        'scratch3d_event_when_click_self',
        '',
        scratch3DBlockXml(
            'scratch3d_variable_change',
            scratch3DFieldXml('VARIABLE', 'score') + scratch3DNumberValueXml('VALUE', 1),
            scratch3DBlockXml(
                'scratch3d_looks_set_state',
                scratch3DFieldXml('STATE', 'collected'),
                scratch3DBlockXml(
                    'scratch3d_looks_hide',
                    '',
                    scratch3DBlockXml(
                        'scratch3d_event_broadcast_message',
                        scratch3DFieldXml('MESSAGE', 'collected')
                    )
                )
            )
        ),
        ' x="40" y="190"'
    )
);

const createCompletionFlowerWorkspaceXml = () => scratch3DWorkspaceXml(
    scratch3DBlockXml(
        'scratch3d_event_when_receive_message',
        scratch3DFieldXml('MESSAGE', 'collected'),
        scratch3DBlockXml(
            'scratch3d_looks_show',
            '',
            scratch3DBlockXml(
                'scratch3d_looks_play_animation',
                scratch3DFieldXml('ANIMATION', 'grow'),
                scratch3DBlockXml(
                    'scratch3d_looks_set_state',
                    scratch3DFieldXml('STATE', 'active')
                )
            )
        ),
        ' x="40" y="40"'
    )
);

const createParkGuideWorkspaceXml = () => scratch3DWorkspaceXml(
    scratch3DBlockXml(
        'scratch3d_event_when_start',
        '',
        scratch3DBlockXml(
            'scratch3d_control_wait',
            scratch3DNumberValueXml('SECONDS', 0.5),
            scratch3DBlockXml(
                'scratch3d_motion_face_direction',
                scratch3DFieldXml('DIRECTION', 'rightUp'),
                scratch3DBlockXml(
                    'scratch3d_looks_play_animation',
                    scratch3DFieldXml('ANIMATION', 'walk'),
                    createRepeatMoveForwardBlockXml(
                        5,
                        0.22,
                        scratch3DBlockXml(
                            'scratch3d_looks_stop_animation',
                            '',
                            scratch3DBlockXml(
                                'scratch3d_event_broadcast_message',
                                scratch3DFieldXml('MESSAGE', 'startQuest')
                            )
                        )
                    )
                )
            )
        ),
        ' x="40" y="40"'
    )
);

const createParkFountainWorkspaceXml = () => scratch3DWorkspaceXml(
    scratch3DBlockXml(
        'scratch3d_event_when_start',
        '',
        scratch3DBlockXml(
            'scratch3d_looks_play_animation',
            scratch3DFieldXml('ANIMATION', 'spin')
        ),
        ' x="40" y="40"'
    ) +
    scratch3DBlockXml(
        'scratch3d_event_when_receive_message',
        scratch3DFieldXml('MESSAGE', 'startQuest'),
        scratch3DBlockXml(
            'scratch3d_looks_set_state',
            scratch3DFieldXml('STATE', 'active'),
            scratch3DBlockXml(
                'scratch3d_looks_play_animation',
                scratch3DFieldXml('ANIMATION', 'blink')
            )
        ),
        ' x="40" y="170"'
    )
);

const createParkLampWorkspaceXml = () => scratch3DWorkspaceXml(
    scratch3DBlockXml(
        'scratch3d_event_when_receive_message',
        scratch3DFieldXml('MESSAGE', 'startQuest'),
        scratch3DBlockXml(
            'scratch3d_looks_set_state',
            scratch3DFieldXml('STATE', 'active'),
            scratch3DBlockXml(
                'scratch3d_looks_play_animation',
                scratch3DFieldXml('ANIMATION', 'blink')
            )
        ),
        ' x="40" y="40"'
    )
);

const createParkFlowerWorkspaceXml = () => scratch3DWorkspaceXml(
    scratch3DBlockXml(
        'scratch3d_event_when_receive_message',
        scratch3DFieldXml('MESSAGE', 'levelClear'),
        scratch3DBlockXml(
            'scratch3d_looks_play_animation',
            scratch3DFieldXml('ANIMATION', 'grow')
        ),
        ' x="40" y="40"'
    )
);

const createParkSignWorkspaceXml = () => scratch3DWorkspaceXml(
    scratch3DBlockXml(
        'scratch3d_event_when_click_self',
        '',
        scratch3DBlockXml(
            'scratch3d_looks_play_animation',
            scratch3DFieldXml('ANIMATION', 'blink'),
            scratch3DBlockXml(
                'scratch3d_event_broadcast_message',
                scratch3DFieldXml('MESSAGE', 'levelClear')
            )
        ),
        ' x="40" y="40"'
    )
);

const createDemoTerrainObjects = () => {
    const objects = {};
    let index = 1;
    for (let x = -5; x <= 6; x++) {
        for (let y = -3; y <= 3; y++) {
            const isPath = y === 0 && x >= -3 && x <= 5;
            const isWater = y === -3 && x >= 1 && x <= 4;
            const assetId = isWater ? 'water-block' : (isPath ? 'stone-block' : 'grass-block');
            objects[`演示地形${index}`] = sceneTemplateObject(assetId, {x, y, z: 0});
            index++;
        }
    }
    return objects;
};

const createInteractionDemoObjects = () => ({
    ...createDemoTerrainObjects(),
    角色1: sceneTemplateObject('role', {x: -3, y: 0, z: TERRAIN_BLOCK_HEIGHT}, {
        facingDirection: 'rightUp',
        stateValue: 'normal',
        blockWorkspaceXml: createRoleDoorDemoWorkspaceXml()
    }),
    机关按钮1: sceneTemplateObject('red-flower', {x: 0, y: 0, z: TERRAIN_BLOCK_HEIGHT}, {
        collisionMode: COLLISION_MODES.TRIGGER,
        stateValue: 'active',
        blockWorkspaceXml: createTriggerSwitchWorkspaceXml('角色1')
    }),
    手动开关1: sceneTemplateObject('crate', {x: -1, y: 2, z: TERRAIN_BLOCK_HEIGHT}, {
        collisionMode: COLLISION_MODES.PASS_THROUGH,
        blockWorkspaceXml: createOpenDoorButtonWorkspaceXml('大门1')
    }),
    大门1: sceneTemplateObject('door', {x: 3, y: 0, z: TERRAIN_BLOCK_HEIGHT}, {
        collisionMode: COLLISION_MODES.SOLID,
        stateValue: 'closed',
        blockWorkspaceXml: blockWorkspaceXmlFromTemplate('door-open-on-message')
    }),
    宝箱1: sceneTemplateObject('crate', {x: 5, y: 0, z: TERRAIN_BLOCK_HEIGHT}, {
        collisionMode: COLLISION_MODES.PASS_THROUGH,
        stateValue: 'inactive',
        visible: false,
        blockWorkspaceXml: createRewardChestWorkspaceXml()
    }),
    完成提示花1: sceneTemplateObject('red-flower', {x: 5, y: 1, z: TERRAIN_BLOCK_HEIGHT}, {
        collisionMode: COLLISION_MODES.PASS_THROUGH,
        stateValue: 'inactive',
        visible: false,
        blockWorkspaceXml: createCompletionFlowerWorkspaceXml()
    }),
    树木1: sceneTemplateObject('oak-tree', {x: -4, y: 2, z: TERRAIN_BLOCK_HEIGHT}, {
        collisionMode: COLLISION_MODES.SOLID
    }),
    树木2: sceneTemplateObject('oak-tree', {x: 6, y: -2, z: TERRAIN_BLOCK_HEIGHT}, {
        collisionMode: COLLISION_MODES.SOLID
    }),
    草丛1: sceneTemplateObject('grass-tuft', {x: -2, y: -2, z: TERRAIN_BLOCK_HEIGHT}, {
        collisionMode: COLLISION_MODES.PASS_THROUGH
    }),
    红花1: sceneTemplateObject('red-flower', {x: 2, y: 2, z: TERRAIN_BLOCK_HEIGHT}, {
        collisionMode: COLLISION_MODES.PASS_THROUGH
    }),
    房屋1: sceneTemplateObject('house', {x: -5, y: -2, z: TERRAIN_BLOCK_HEIGHT}, {
        collisionMode: COLLISION_MODES.SOLID
    }),
    箱子1: sceneTemplateObject('crate', {x: 1, y: -2, z: TERRAIN_BLOCK_HEIGHT}, {
        collisionMode: COLLISION_MODES.SOLID
    })
});

const createParkTerrainObjects = () => {
    const objects = {};
    let index = 1;
    for (let x = -8; x <= 8; x++) {
        for (let y = -6; y <= 6; y++) {
            const isMainPath = x === 0 || y === 0 || (Math.abs(x) <= 1 && Math.abs(y) <= 5);
            const isPond = x >= 4 && x <= 7 && y >= -5 && y <= -3;
            const isFlowerBed = (x >= -7 && x <= -5 && y >= 3 && y <= 5) ||
                (x >= 5 && x <= 7 && y >= 3 && y <= 5);
            const isPlayArea = x >= -7 && x <= -4 && y >= -5 && y <= -3;
            const assetId = isPond ?
                'water-block' :
                (isMainPath ?
                    'path-block' :
                    (isFlowerBed ? 'flowerbed-block' : (isPlayArea ? 'sand-block' : 'grass-block')));
            objects[`公园地形${index}`] = sceneTemplateObject(assetId, {x, y, z: 0});
            index++;
        }
    }
    return objects;
};

const createParkTemplateObjects = () => {
    const objects = createParkTerrainObjects();
    const addObject = (name, assetId, x, y, options = {}) => {
        objects[name] = sceneTemplateObject(assetId, {x, y, z: TERRAIN_BLOCK_HEIGHT}, options);
    };

    addObject('导览角色1', 'role', -7, -1, {
        facingDirection: 'rightUp',
        blockWorkspaceXml: createParkGuideWorkspaceXml()
    });
    addObject('中心喷泉1', 'park-fountain', 0, 0, {
        collisionMode: COLLISION_MODES.SOLID,
        blockWorkspaceXml: createParkFountainWorkspaceXml()
    });
    addObject('公园指示牌1', 'park-sign', -7, 0, {
        collisionMode: COLLISION_MODES.PASS_THROUGH,
        blockWorkspaceXml: createParkSignWorkspaceXml()
    });
    addObject('回收桶1', 'recycle-bin', -6, 1, {
        collisionMode: COLLISION_MODES.SOLID
    });
    addObject('回收桶2', 'recycle-bin', 6, -1, {
        collisionMode: COLLISION_MODES.SOLID
    });

    [
        [-2, 1],
        [2, 1],
        [-2, -1],
        [2, -1],
        [-6, -4],
        [6, 4]
    ].forEach(([x, y], index) => {
        addObject(`公园长椅${index + 1}`, 'park-bench', x, y, {
            collisionMode: COLLISION_MODES.SOLID
        });
    });

    [
        [-4, 0],
        [4, 0],
        [0, -4],
        [0, 4],
        [-7, -2],
        [7, 2]
    ].forEach(([x, y], index) => {
        addObject(`路灯${index + 1}`, 'park-lamp', x, y, {
            collisionMode: COLLISION_MODES.SOLID,
            blockWorkspaceXml: createParkLampWorkspaceXml()
        });
    });

    [
        [-8, -6],
        [-8, -3],
        [-8, 5],
        [-5, 6],
        [-2, 6],
        [3, 6],
        [8, 6],
        [8, 3],
        [8, -2],
        [8, -6],
        [-3, -6],
        [3, -6]
    ].forEach(([x, y], index) => {
        addObject(`公园树木${index + 1}`, 'oak-tree', x, y, {
            collisionMode: COLLISION_MODES.SOLID
        });
    });

    [
        [-7, 3],
        [-6, 4],
        [-5, 5],
        [5, 3],
        [6, 4],
        [7, 5],
        [-5, -4],
        [-4, -3],
        [4, 4],
        [5, 5]
    ].forEach(([x, y], index) => {
        addObject(`公园红花${index + 1}`, 'red-flower', x, y, {
            collisionMode: COLLISION_MODES.PASS_THROUGH,
            blockWorkspaceXml: createParkFlowerWorkspaceXml()
        });
    });

    [
        [-7, 2],
        [-6, 3],
        [-4, 5],
        [4, 5],
        [6, 3],
        [7, 2],
        [-3, 2],
        [3, -2]
    ].forEach(([x, y], index) => {
        addObject(`公园灌木${index + 1}`, 'bush', x, y, {
            collisionMode: COLLISION_MODES.PASS_THROUGH
        });
    });

    return objects;
};

const SCENE_TEMPLATES = [
    {
        id: 'blank',
        label: '空白',
        description: '清空当前场景，回到可自由搭建的空白编辑器。',
        category: 'basic',
        tags: ['空白', '自由搭建', '起步'],
        background: '#dbeafe',
        objects: {},
        defaultSelectedObject: null
    },
    {
        id: 'yard',
        label: '庭院',
        description: '少量素材的基础摆放示例，适合快速查看场景布局。',
        category: 'basic',
        tags: ['庭院', '树木', '角色', '基础'],
        background: '#dbeafe',
        objects: {
            地块1: {
                kind: 'ground',
                assetId: 'ground',
                color: '#8fd17f',
                size: 1,
                position: {x: 0, y: 0, z: 0},
                scale: {x: 1, y: 1, z: 1},
                layer: 0
            },
            树木1: {
                kind: 'tree',
                assetId: 'tree',
                color: '#28a745',
                size: 1.15,
                position: {x: 3, y: 2, z: 0},
                scale: {x: 1, y: 1, z: 1},
                layer: 0
            },
            角色1: {
                kind: 'role',
                assetId: 'role',
                color: '#4E97FF',
                size: 1,
                position: {x: -2, y: -1, z: 0},
                scale: {x: 1, y: 1, z: 1},
                layer: 0
            }
        },
        defaultSelectedObject: '角色1'
    },
    {
        id: 'village',
        label: '村庄',
        description: '房屋、树木和角色组成的小型场景示例。',
        category: 'building',
        tags: ['村庄', '房屋', '角色', '建筑'],
        background: '#e0f2fe',
        objects: {
            房屋1: {
                kind: 'house',
                assetId: 'house',
                color: '#ef7d57',
                size: 1.35,
                position: {x: -4, y: -2, z: 0},
                scale: {x: 1, y: 1, z: 1},
                layer: 0
            },
            房屋2: {
                kind: 'house',
                assetId: 'house',
                color: '#f59e0b',
                size: 1.35,
                position: {x: 2, y: -3, z: 0},
                scale: {x: 1, y: 1, z: 1},
                layer: 0
            },
            树木1: {
                kind: 'tree',
                assetId: 'tree',
                color: '#22c55e',
                size: 1.15,
                position: {x: 4, y: 2, z: 0},
                scale: {x: 1, y: 1, z: 1},
                layer: 0
            },
            角色1: {
                kind: 'role',
                assetId: 'role',
                color: '#4E97FF',
                size: 1,
                position: {x: 0, y: 3, z: 0},
                scale: {x: 1, y: 1, z: 1},
                layer: 0
            }
        },
        defaultSelectedObject: '角色1'
    },
    {
        id: 'park-demo',
        label: '公园样例',
        description: '包含石砖路、花坛、喷泉、长椅、路灯和导览角色的公园场景，可直接运行观察互动效果。',
        category: 'teaching',
        tags: ['公园', '喷泉', '长椅', '路灯', '导览', '教学'],
        background: '#dbeafe',
        objects: createParkTemplateObjects(),
        defaultSelectedObject: '导览角色1'
    },
    {
        id: 'interaction-demo',
        label: '机关门互动示例',
        description: '绿旗运行后，角色触发开关、门打开、宝箱出现；可点开对象编程拆解积木。',
        category: 'teaching',
        tags: ['机关', '门', '宝箱', '教学', '积木'],
        background: '#dbeafe',
        objects: createInteractionDemoObjects(),
        defaultSelectedObject: '角色1'
    }
];

class Stage3D extends React.PureComponent {
    constructor (props) {
        super(props);
        this.state = {
            sceneState: normalizeSceneState(props.vm.runtime.scratch3DState),
            selectedObject: null,
            hoveredObject: null,
            selectedAssetId: ASSETS[0].id,
            activeTool: EDIT_TOOLS.PAINT,
            brushSize: BRUSH_SIZE_OPTIONS[0],
            selectionBounds: null,
            areaClipboard: null,
            activeWorkbenchTab: WORKBENCH_TABS.SCENE,
            activeCodeCategory: THREE_D_BLOCK_PALETTE[0].id,
            draggingCodeBlock: null,
            sceneTemplateBrowserOpen: false,
            sceneTemplateCategory: SCENE_TEMPLATE_CATEGORIES[0].id,
            sceneTemplateSearch: '',
            localSaveStatus: '',
            localSaveStatusKind: 'info',
            scriptDraft: {
                event: 'start',
                action: 'playAnimation',
                animationId: 'idle',
                stateValue: 'normal',
                condition: 'always',
                conditionState: 'normal',
                conditionTarget: '',
                distance: 3,
            direction: 'rightUp',
            steps: 1,
            height: 1,
            targetX: 0,
                targetY: 0,
                targetZ: 0,
                repeatTimes: 1,
                waitSeconds: 0
            },
            undoStack: [],
            redoStack: []
        };

        this.pan = {x: 0, y: -GRID_SIZE * TILE_HEIGHT / 2};
        this.zoom = 0.92;
        this.isPanning = false;
        this.lastPointer = null;
        this.dragDistance = 0;
        this.suppressNextClick = false;
        this.imageCache = {};
        this.editDraftState = null;
        this.editStartState = null;
        this.editChanged = false;
        this.editChangedCells = 0;
        this.editTouchedCells = null;
        this.isTerrainEditing = false;
        this.isFilling = false;
        this.fillStartGrid = null;
        this.fillPreviewGrid = null;
        this.isBoxSelecting = false;
        this.selectionStartGrid = null;
        this.selectionPreviewGrid = null;
        this.hasFitInitialView = false;
        this.animationFrame = null;
        this.scriptTimers = [];
        this.scriptRunToken = 0;
        this.scriptStepCountByToken = new Map();
        this.lastRuntimeWarningAt = 0;
        this.is3DRunActive = false;
        this.runPreviewBaseSceneState = null;
        this.activeTouchEventKeys = new Set();
        this.blockWorkspaceRef = React.createRef();

        this.setCanvas = this.setCanvas.bind(this);
        this.setImportInput = this.setImportInput.bind(this);
        this.handle3DUpdate = this.handle3DUpdate.bind(this);
        this.ensureRuntimeSceneState = this.ensureRuntimeSceneState.bind(this);
        this.resize = this.resize.bind(this);
        this.draw = this.draw.bind(this);
        this.handleWheel = this.handleWheel.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.selectAsset = this.selectAsset.bind(this);
        this.deleteSelectedObject = this.deleteSelectedObject.bind(this);
        this.undo = this.undo.bind(this);
        this.redo = this.redo.bind(this);
        this.loadSceneTemplate = this.loadSceneTemplate.bind(this);
        this.openSceneTemplateBrowser = this.openSceneTemplateBrowser.bind(this);
        this.closeSceneTemplateBrowser = this.closeSceneTemplateBrowser.bind(this);
        this.updateSceneTemplateSearch = this.updateSceneTemplateSearch.bind(this);
        this.selectSceneTemplateCategory = this.selectSceneTemplateCategory.bind(this);
        this.updateWeather = this.updateWeather.bind(this);
        this.updateSelectedPosition = this.updateSelectedPosition.bind(this);
        this.updateSelectedColor = this.updateSelectedColor.bind(this);
        this.updateSelectedScale = this.updateSelectedScale.bind(this);
        this.updateSelectedState = this.updateSelectedState.bind(this);
        this.updateSelectedVariable = this.updateSelectedVariable.bind(this);
        this.updateSelectedCollisionMode = this.updateSelectedCollisionMode.bind(this);
        this.saveSceneToLocalDraft = this.saveSceneToLocalDraft.bind(this);
        this.loadSceneFromLocalDraft = this.loadSceneFromLocalDraft.bind(this);
        this.addProgrammingVariable = this.addProgrammingVariable.bind(this);
        this.renameProgrammingVariable = this.renameProgrammingVariable.bind(this);
        this.deleteProgrammingVariable = this.deleteProgrammingVariable.bind(this);
        this.addProgrammingMessage = this.addProgrammingMessage.bind(this);
        this.renameProgrammingMessage = this.renameProgrammingMessage.bind(this);
        this.deleteProgrammingMessage = this.deleteProgrammingMessage.bind(this);
        this.applyInteractionTemplate = this.applyInteractionTemplate.bind(this);
        this.playSelectedAnimation = this.playSelectedAnimation.bind(this);
        this.stopSelectedAnimation = this.stopSelectedAnimation.bind(this);
        this.selectWorkbenchTab = this.selectWorkbenchTab.bind(this);
        this.updateScriptDraft = this.updateScriptDraft.bind(this);
        this.updateSelectedBlocksXml = this.updateSelectedBlocksXml.bind(this);
        this.addObjectScript = this.addObjectScript.bind(this);
        this.handleImportClick = this.handleImportClick.bind(this);
        this.handleImportChange = this.handleImportChange.bind(this);
        this.selectTool = this.selectTool.bind(this);
        this.resetBestView = this.resetBestView.bind(this);
        this.refreshControlsHost = this.refreshControlsHost.bind(this);
        this.runAnimationFrame = this.runAnimationFrame.bind(this);
        this.handleProjectRunStart = this.handleProjectRunStart.bind(this);
        this.handleProjectRunStop = this.handleProjectRunStop.bind(this);
        this.insertBlockToWorkspace = this.insertBlockToWorkspace.bind(this);
        this.updateBlockWorkspaceDragPreview = this.updateBlockWorkspaceDragPreview.bind(this);
        this.clearBlockWorkspaceDragPreview = this.clearBlockWorkspaceDragPreview.bind(this);
        this.selectCodeCategory = this.selectCodeCategory.bind(this);
        this.handleCodeBlockDragStart = this.handleCodeBlockDragStart.bind(this);
        this.handleCodeBlockDragMove = this.handleCodeBlockDragMove.bind(this);
        this.handleCodeBlockDragEnd = this.handleCodeBlockDragEnd.bind(this);
    }

    componentDidMount () {
        this.props.vm.runtime.on('SCRATCH3D_UPDATE', this.handle3DUpdate);
        this.props.vm.runtime.on('PROJECT_START', this.handleProjectRunStart);
        this.props.vm.runtime.on('PROJECT_STOP_ALL', this.handleProjectRunStop);
        window.addEventListener('resize', this.resize);
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('mouseup', this.handleMouseUp);
        window.requestAnimationFrame(this.refreshControlsHost);
        this.ensureRuntimeSceneState();
        this.resize();
        this.updateAnimationLoop();
    }

    componentDidUpdate (previousProps) {
        if (previousProps.stage3DWorkspaceMode !== this.props.stage3DWorkspaceMode) {
            if (this.props.stage3DWorkspaceMode === 'code') {
                this.selectWorkbenchTab(WORKBENCH_TABS.CODE);
            } else if (this.props.stage3DWorkspaceMode === 'build' &&
                    this.state.activeWorkbenchTab === WORKBENCH_TABS.CODE) {
                this.selectWorkbenchTab(WORKBENCH_TABS.ASSETS);
            }
        }
        this.resize();
        this.refreshControlsHost();
        this.updateAnimationLoop();
    }

    componentWillUnmount () {
        this.clearScheduledScripts();
        this.props.vm.runtime.removeListener('SCRATCH3D_UPDATE', this.handle3DUpdate);
        this.props.vm.runtime.removeListener('PROJECT_START', this.handleProjectRunStart);
        this.props.vm.runtime.removeListener('PROJECT_STOP_ALL', this.handleProjectRunStop);
        window.removeEventListener('resize', this.resize);
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
        window.removeEventListener('mousemove', this.handleCodeBlockDragMove);
        window.removeEventListener('mouseup', this.handleCodeBlockDragEnd);
        if (this.canvas) {
            this.canvas.removeEventListener('wheel', this.handleWheel, NON_PASSIVE_WHEEL_EVENT_OPTIONS);
        }
        this.clearBlockWorkspaceDragPreview();
        if (this.animationFrame) {
            window.cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
    }

    clearScheduledScripts () {
        this.scriptTimers.forEach(timer => {
            const timerId = typeof timer === 'object' ? timer.id : timer;
            window.clearTimeout(timerId);
            if (timer && typeof timer.resolve === 'function') {
                timer.resolve();
            }
        });
        this.scriptTimers = [];
        this.scriptStepCountByToken.clear();
        this.scriptRunToken++;
    }

    refreshControlsHost () {
        const controlsHost = document.getElementById('scratch-stage-3d-editor-panel');
        const codeHost = document.getElementById('scratch-stage-3d-code-workspace');
        const quickToolbarHost = document.getElementById('scratch-stage-3d-quick-toolbar');
        if (
            controlsHost !== this.controlsHost ||
            codeHost !== this.codeHost ||
            quickToolbarHost !== this.quickToolbarHost
        ) {
            this.controlsHost = controlsHost;
            this.codeHost = codeHost;
            this.quickToolbarHost = quickToolbarHost;
            this.forceUpdate();
        }
    }

    updateAnimationLoop () {
        if ((this.hasActiveAnimations() || this.is3DRunActive) && !this.animationFrame) {
            this.animationFrame = window.requestAnimationFrame(this.runAnimationFrame);
        }
    }

    runAnimationFrame () {
        this.animationFrame = null;
        if (this.is3DRunActive) {
            this.processTouchingEventScripts();
        }
        this.draw();
        this.updateAnimationLoop();
    }

    setCanvas (canvas) {
        if (this.canvas && this.canvas !== canvas) {
            this.canvas.removeEventListener('wheel', this.handleWheel, NON_PASSIVE_WHEEL_EVENT_OPTIONS);
        }
        this.canvas = canvas;
        if (this.canvas) {
            this.canvas.addEventListener('wheel', this.handleWheel, NON_PASSIVE_WHEEL_EVENT_OPTIONS);
            window.requestAnimationFrame(this.resize);
        }
    }

    setImportInput (input) {
        this.importInput = input;
    }

    handle3DUpdate (sceneState) {
        if (this.isSyncingSceneState) return;
        const nextSceneState = normalizeSceneState(sceneState);
        const objects = nextSceneState.objects || {};
        if (!sceneState) {
            this.syncRuntimeSceneState(nextSceneState, false);
        }
        this.setState(previousState => ({
            sceneState: nextSceneState,
            selectedObject: objects[previousState.selectedObject] ? previousState.selectedObject : null,
            hoveredObject: objects[previousState.hoveredObject] &&
                    objects[previousState.hoveredObject].visible !== false ?
                previousState.hoveredObject :
                null,
            undoStack: [],
            redoStack: []
        }), () => {
            window.requestAnimationFrame(() => {
                this.resetBestView();
            });
        });
    }

    ensureRuntimeSceneState () {
        if (this.props.vm.runtime.scratch3DState) return;
        this.syncRuntimeSceneState(this.state.sceneState, false);
    }

    resize () {
        if (!this.canvas) return;

        const rect = this.canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        const width = Math.max(1, Math.round(rect.width * dpr));
        const height = Math.max(1, Math.round(rect.height * dpr));

        const sizeChanged = this.canvas.width !== width || this.canvas.height !== height;
        if (sizeChanged) {
            this.canvas.width = width;
            this.canvas.height = height;
        }
        this.dpr = dpr;

        if (!this.hasFitInitialView && width > 1 && height > 1) {
            this.hasFitInitialView = true;
            this.resetBestView();
            return;
        }

        this.draw();
    }

    resetBestView () {
        if (!this.canvas) return;

        const contentBounds = this.sceneContentBoundsForBestView();
        if (contentBounds) {
            this.fitViewToBounds(contentBounds);
            this.draw();
            return;
        }

        this.resetFullGridView();
        this.draw();
    }

    resetFullGridView () {
        const gridWidth = GRID_SIZE * TILE_WIDTH;
        const gridHeight = GRID_SIZE * TILE_HEIGHT;
        const zoomByWidth = this.canvas.width / (gridWidth * 1.1);
        const zoomByHeight = this.canvas.height / (gridHeight * 1.35);

        this.zoom = clamp(Math.min(zoomByWidth, zoomByHeight), MIN_ZOOM, this.maximumZoom());
        this.pan = {x: 0, y: 0};
    }

    maximumZoom () {
        if (!this.canvas) return DEFAULT_MAX_ZOOM;

        const targetWidth = MAX_ZOOM_VISIBLE_GRID_SIZE * TILE_WIDTH;
        const targetHeight = MAX_ZOOM_VISIBLE_GRID_SIZE * TILE_HEIGHT;
        const zoomByWidth = this.canvas.width / (targetWidth * MAX_ZOOM_VIEW_PADDING);
        const zoomByHeight = this.canvas.height / (targetHeight * MAX_ZOOM_VIEW_PADDING);

        return Math.max(DEFAULT_MAX_ZOOM, Math.min(zoomByWidth, zoomByHeight));
    }

    fitViewToBounds (bounds) {
        const width = Math.max(1, bounds.maxX - bounds.minX);
        const height = Math.max(1, bounds.maxY - bounds.minY);
        const paddingX = Math.min(this.canvas.width * BEST_VIEW_PADDING_RATIO, 180 * (this.dpr || 1));
        const paddingY = Math.min(this.canvas.height * BEST_VIEW_PADDING_RATIO, 150 * (this.dpr || 1));
        const availableWidth = Math.max(1, this.canvas.width - paddingX * 2);
        const availableHeight = Math.max(1, this.canvas.height - paddingY * 2);
        const dpr = this.dpr || 1;
        const maxVisualZoom = bounds.objectCount <= 1 ?
            BEST_VIEW_SINGLE_OBJECT_MAX_VISUAL_ZOOM :
            BEST_VIEW_CLUSTER_MAX_VISUAL_ZOOM;
        const zoom = Math.min(availableWidth / width, availableHeight / height);
        const centerX = (bounds.minX + bounds.maxX) / 2;
        const centerY = (bounds.minY + bounds.maxY) / 2;

        this.zoom = clamp(zoom, MIN_ZOOM, Math.min(this.maximumZoom(), maxVisualZoom * dpr));
        this.pan = {
            x: -centerX * this.zoom,
            y: -centerY * this.zoom
        };
    }

    sceneContentBoundsForBestView () {
        const objects = this.getSortedObjects();
        if (!objects.length) return null;

        const visibleObjects = objects.filter(object => object.visible !== false);
        const fitObjects = visibleObjects.length ? visibleObjects : objects;
        const objectBounds = fitObjects.map(object => this.objectIsoBounds(object));
        const bounds = objectBounds.reduce((merged, item) => ({
            minX: Math.min(merged.minX, item.minX),
            minY: Math.min(merged.minY, item.minY),
            maxX: Math.max(merged.maxX, item.maxX),
            maxY: Math.max(merged.maxY, item.maxY)
        }), objectBounds[0]);
        const minCells = fitObjects.length <= 1 ?
            BEST_VIEW_SINGLE_OBJECT_MIN_CELLS :
            BEST_VIEW_CLUSTER_MIN_CELLS;
        return {
            ...this.expandIsoBounds(bounds, minCells * TILE_WIDTH, minCells * TILE_HEIGHT),
            objectCount: fitObjects.length
        };
    }

    expandIsoBounds (bounds, minWidth, minHeight) {
        const width = bounds.maxX - bounds.minX;
        const height = bounds.maxY - bounds.minY;
        const extraX = Math.max(0, minWidth - width) / 2;
        const extraY = Math.max(0, minHeight - height) / 2;
        return {
            minX: bounds.minX - extraX,
            minY: bounds.minY - extraY,
            maxX: bounds.maxX + extraX,
            maxY: bounds.maxY + extraY
        };
    }

    objectIsoBounds (object) {
        const grid = this.objectToGrid(object);
        const center = this.gridToIsoPoint(grid);
        const size = this.objectSize(object);
        const extents = this.objectIsoExtents(object, size);
        return {
            minX: center.x - extents.left,
            minY: center.y - extents.top,
            maxX: center.x + extents.right,
            maxY: center.y + extents.bottom
        };
    }

    objectIsoExtents (object, size) {
        if (object.kind === 'terrain') {
            return {
                left: size * 1.1,
                right: size * 1.1,
                top: size * 1.35,
                bottom: size * 0.85
            };
        }
        if (object.kind === 'ground') {
            return {
                left: size * 1.1,
                right: size * 1.1,
                top: size * 0.65,
                bottom: size * 0.65
            };
        }
        if (object.kind === 'tree') {
            return {
                left: size * 0.95,
                right: size * 0.95,
                top: size * 1.55,
                bottom: size * 0.75
            };
        }
        if (object.kind === 'house') {
            return {
                left: size * 1.4,
                right: size * 1.4,
                top: size * 2.15,
                bottom: size * 1.1
            };
        }
        if (object.kind === 'door') {
            return {
                left: size * 0.85,
                right: size * 0.85,
                top: size * 1.45,
                bottom: size * 0.45
            };
        }
        if (object.kind === 'lamp') {
            return {
                left: size * 0.7,
                right: size * 0.7,
                top: size * 2.4,
                bottom: size * 0.5
            };
        }
        if (object.kind === 'fountain') {
            return {
                left: size * 1.35,
                right: size * 1.35,
                top: size * 1.65,
                bottom: size * 0.95
            };
        }
        if (object.kind === 'bench') {
            return {
                left: size * 1.35,
                right: size * 1.35,
                top: size * 1.1,
                bottom: size * 0.65
            };
        }
        if (object.kind === 'image') {
            return {
                left: size * 1.25,
                right: size * 1.25,
                top: size * 2.1,
                bottom: size * 0.45
            };
        }
        return {
            left: size * 1.15,
            right: size * 1.15,
            top: size * 1.65,
            bottom: size * 0.85
        };
    }

    handleWheel (event) {
        if (event.cancelable !== false) {
            event.preventDefault();
        }
        event.stopPropagation();
        if (!this.canvas) return;

        const rect = this.canvas.getBoundingClientRect();
        const pointer = {
            x: (event.clientX - rect.left) * (this.dpr || 1),
            y: (event.clientY - rect.top) * (this.dpr || 1)
        };
        const before = this.screenToWorld(pointer);
        const factor = event.deltaY > 0 ? 0.9 : 1.1;
        this.zoom = clamp(this.zoom * factor, MIN_ZOOM, this.maximumZoom());
        const after = this.screenToWorld(pointer);

        this.pan.x += (after.x - before.x) * this.zoom;
        this.pan.y += (after.y - before.y) * this.zoom;
        this.draw();
    }

    isPointerInsideCanvas (event) {
        if (!this.canvas) return false;
        const rect = this.canvas.getBoundingClientRect();
        return event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom;
    }

    setHoveredObject (objectName) {
        if (this.state.hoveredObject === objectName) return;
        this.setState({hoveredObject: objectName}, this.draw);
    }

    clearHoveredObject () {
        this.setHoveredObject(null);
    }

    updateHoveredObject (event) {
        const hoverableTools = [
            EDIT_TOOLS.SELECT,
            EDIT_TOOLS.PLACE,
            EDIT_TOOLS.ERASE
        ];
        if (!this.canvas ||
                !hoverableTools.includes(this.state.activeTool) ||
                !this.isPointerInsideCanvas(event)) {
            this.clearHoveredObject();
            return;
        }

        const hit = this.hitTestObject(event);
        this.setHoveredObject(hit ? hit.name : null);
    }

    handleMouseLeave () {
        if (!this.isPanning &&
                !this.isTerrainEditing &&
                !this.isFilling &&
                !this.isBoxSelecting) {
            this.clearHoveredObject();
        }
    }

    handleMouseDown (event) {
        if (event.button !== 0) return;

        if (this.activateSceneWorkbench()) {
            this.suppressNextClick = true;
            return;
        }

        const activeTool = this.state.activeTool;
        if (activeTool === EDIT_TOOLS.BOX_SELECT) {
            const grid = this.eventToGrid(event);
            if (!this.isInsideGrid(grid)) return;
            this.suppressNextClick = true;
            this.clearHoveredObject();
            this.isBoxSelecting = true;
            this.selectionStartGrid = grid;
            this.selectionPreviewGrid = grid;
            this.draw();
            return;
        }

        if (activeTool === EDIT_TOOLS.MOVE_SELECTION || activeTool === EDIT_TOOLS.PASTE) {
            return;
        }

        const selectedAsset = this.getAssetById(this.state.selectedAssetId);
        if ((activeTool === EDIT_TOOLS.PLACE && this.isTerrainAsset(selectedAsset)) ||
                activeTool === EDIT_TOOLS.PAINT ||
                activeTool === EDIT_TOOLS.RAISE_TERRAIN ||
                activeTool === EDIT_TOOLS.LOWER_TERRAIN ||
                activeTool === EDIT_TOOLS.ERASE ||
                activeTool === EDIT_TOOLS.REPLACE) {
            const grid = this.eventToGrid(event);
            if (!this.isInsideGrid(grid)) return;
            this.suppressNextClick = true;
            this.clearHoveredObject();
            this.beginTerrainEdit();
            this.applyActiveToolAtGrid(grid);
            this.isTerrainEditing = true;
            return;
        }

        if (activeTool === EDIT_TOOLS.FILL) {
            const selectedAsset = this.getAssetById(this.state.selectedAssetId);
            const grid = this.eventToGrid(event);
            if (!this.isTerrainAsset(selectedAsset) || !this.isInsideGrid(grid)) return;
            this.suppressNextClick = true;
            this.clearHoveredObject();
            this.isFilling = true;
            this.fillStartGrid = grid;
            this.fillPreviewGrid = grid;
            this.draw();
            return;
        }

        this.isPanning = true;
        this.clearHoveredObject();
        this.lastPointer = {x: event.clientX, y: event.clientY};
        this.dragDistance = 0;
    }

    handleMouseMove (event) {
        if (this.isBoxSelecting) {
            this.clearHoveredObject();
            const grid = this.eventToGrid(event);
            if (this.isInsideGrid(grid)) {
                this.selectionPreviewGrid = grid;
                this.draw();
            }
            return;
        }

        if (this.isTerrainEditing) {
            this.clearHoveredObject();
            const grid = this.eventToGrid(event);
            if (this.isInsideGrid(grid)) {
                this.applyActiveToolAtGrid(grid);
            }
            return;
        }

        if (this.isFilling) {
            this.clearHoveredObject();
            const grid = this.eventToGrid(event);
            if (this.isInsideGrid(grid)) {
                this.fillPreviewGrid = grid;
                this.draw();
            }
            return;
        }

        if (!this.isPanning || !this.lastPointer) {
            this.updateHoveredObject(event);
            return;
        }
        this.clearHoveredObject();
        const dpr = this.dpr || 1;
        const deltaX = event.clientX - this.lastPointer.x;
        const deltaY = event.clientY - this.lastPointer.y;
        this.dragDistance += Math.abs(deltaX) + Math.abs(deltaY);
        this.pan.x += deltaX * dpr;
        this.pan.y += deltaY * dpr;
        this.lastPointer = {x: event.clientX, y: event.clientY};
        this.draw();
    }

    handleMouseUp () {
        if (this.isBoxSelecting) {
            const bounds = this.boundsFromGrids(this.selectionStartGrid, this.selectionPreviewGrid);
            const sceneState = normalizeSceneState(this.state.sceneState);
            const selectedNames = this.objectNamesInBounds(sceneState, bounds);
            this.isBoxSelecting = false;
            this.selectionStartGrid = null;
            this.selectionPreviewGrid = null;
            this.setState({
                selectedObject: null,
                selectionBounds: bounds,
                localSaveStatus: selectedNames.length ?
                    `已框选 ${selectedNames.length} 个对象` :
                    '选区内没有对象',
                localSaveStatusKind: selectedNames.length ? 'success' : 'warning'
            }, this.draw);
            return;
        }

        if (this.isTerrainEditing) {
            this.commitTerrainEdit();
            this.isTerrainEditing = false;
            return;
        }

        if (this.isFilling) {
            const startGrid = this.fillStartGrid;
            const endGrid = this.fillPreviewGrid || startGrid;
            this.isFilling = false;
            this.fillStartGrid = null;
            this.fillPreviewGrid = null;
            if (startGrid && endGrid) {
                this.fillTerrainRect(startGrid, endGrid);
            }
            this.draw();
            return;
        }

        if (this.dragDistance > 5) {
            this.suppressNextClick = true;
        }
        this.isPanning = false;
        this.lastPointer = null;
    }

    handleClick (event) {
        if (!this.canvas) return;
        if (this.suppressNextClick) {
            this.suppressNextClick = false;
            return;
        }

        const activeTool = this.state.activeTool;
        if (activeTool === EDIT_TOOLS.MOVE_SELECTION || activeTool === EDIT_TOOLS.PASTE) {
            const grid = this.eventToGrid(event);
            if (!this.isInsideGrid(grid)) return;
            if (activeTool === EDIT_TOOLS.MOVE_SELECTION) {
                this.moveSelectionToGrid(grid);
            } else {
                this.pasteClipboardAtGrid(grid);
            }
            return;
        }

        if (activeTool !== EDIT_TOOLS.SELECT && activeTool !== EDIT_TOOLS.PLACE) {
            return;
        }

        const selectedAsset = this.getAssetById(this.state.selectedAssetId);
        const hit = this.hitTestObject(event);
        if (hit && (
            activeTool === EDIT_TOOLS.SELECT ||
            (activeTool === EDIT_TOOLS.PLACE &&
                !this.isTerrainAsset(selectedAsset) &&
                hit.kind !== 'terrain') ||
            (activeTool !== EDIT_TOOLS.PLACE && selectedAsset.kind === 'terrain')
        )) {
            this.setState({selectedObject: hit.name});
            if (this.is3DRunActive) {
                this.runObjectScripts('click', hit.name);
            }
            return;
        }

        const grid = (
            hit &&
            activeTool === EDIT_TOOLS.PLACE &&
            (
                this.isTerrainAsset(selectedAsset) ||
                (!this.isTerrainAsset(selectedAsset) && this.isTerrainObject(hit))
            )
        ) ? this.objectToGrid(hit) : this.eventToGrid(event);
        if (!this.isInsideGrid(grid)) {
            this.setState({selectedObject: null});
            return;
        }

        if (activeTool === EDIT_TOOLS.PLACE) {
            this.placeSelectedAsset(grid);
        } else {
            this.setState({selectedObject: null});
        }
    }

    hitTestObject (event) {
        if (!this.canvas) return null;
        const rect = this.canvas.getBoundingClientRect();
        const point = {
            x: (event.clientX - rect.left) * (this.dpr || 1),
            y: (event.clientY - rect.top) * (this.dpr || 1)
        };
        const objects = this.getSortedObjects()
            .filter(object => object.visible !== false)
            .slice()
            .reverse();
        return objects.find(object => {
            const screen = this.worldToScreen(this.objectToGrid(object));
            const size = this.objectSize(object) * this.zoom;
            return point.x >= screen.x - size &&
                point.x <= screen.x + size &&
                point.y >= screen.y - (size * 1.6) &&
                point.y <= screen.y + size;
        });
    }

    draw () {
        if (!this.canvas) return;

        const context = this.canvas.getContext('2d');
        const width = this.canvas.width;
        const height = this.canvas.height;
        const sceneState = this.state.sceneState || DEFAULT_STATE;

        context.clearRect(0, 0, width, height);
        context.fillStyle = sceneState.background || DEFAULT_STATE.background;
        context.fillRect(0, 0, width, height);

        context.save();
        this.drawGrid(context);
        this.drawBoundary(context, GRID_SIZE, '#667085', [7, 5]);
        this.drawBoundary(context, INNER_BOUNDARY_SIZE, '#12b886', [5, 5]);
        this.drawFillPreview(context);
        this.getSortedObjects().forEach(object => this.drawObject(context, object));
        this.drawSelectionOverlay(context);
        context.restore();
    }

    drawFillPreview (context) {
        if (!this.isFilling || !this.fillStartGrid || !this.fillPreviewGrid) return;

        const minX = Math.min(this.fillStartGrid.x, this.fillPreviewGrid.x) - 0.5;
        const maxX = Math.max(this.fillStartGrid.x, this.fillPreviewGrid.x) + 0.5;
        const minY = Math.min(this.fillStartGrid.y, this.fillPreviewGrid.y) - 0.5;
        const maxY = Math.max(this.fillStartGrid.y, this.fillPreviewGrid.y) + 0.5;
        const points = [
            this.worldToScreen({x: minX, y: minY, z: 0}),
            this.worldToScreen({x: maxX, y: minY, z: 0}),
            this.worldToScreen({x: maxX, y: maxY, z: 0}),
            this.worldToScreen({x: minX, y: maxY, z: 0})
        ];

        context.save();
        context.fillStyle = 'rgba(78, 151, 255, 0.14)';
        context.strokeStyle = '#4E97FF';
        context.lineWidth = Math.max(2, 2 * this.zoom);
        context.setLineDash([6 * this.zoom, 4 * this.zoom]);
        context.beginPath();
        context.moveTo(points[0].x, points[0].y);
        points.slice(1).forEach(point => context.lineTo(point.x, point.y));
        context.closePath();
        context.fill();
        context.stroke();
        context.restore();
    }

    drawSelectionOverlay (context) {
        const bounds = this.isBoxSelecting ?
            this.boundsFromGrids(this.selectionStartGrid, this.selectionPreviewGrid) :
            this.normalizeSelectionBounds(this.state.selectionBounds);
        if (!bounds) return;

        const points = this.pointsForGridBounds(bounds);

        context.save();
        context.fillStyle = 'rgba(255, 191, 0, 0.12)';
        context.strokeStyle = '#ffbf00';
        context.lineWidth = Math.max(2, 2 * this.zoom);
        context.setLineDash([8 * this.zoom, 4 * this.zoom]);
        context.beginPath();
        context.moveTo(points[0].x, points[0].y);
        points.slice(1).forEach(point => context.lineTo(point.x, point.y));
        context.closePath();
        context.fill();
        context.stroke();
        context.restore();
    }

    drawGrid (context) {
        context.lineWidth = Math.max(1, this.zoom);
        context.strokeStyle = 'rgba(71, 85, 105, 0.22)';

        for (let x = 0; x <= GRID_SIZE; x++) {
            const from = this.gridIndexToScreen(x, 0, 0);
            const to = this.gridIndexToScreen(x, GRID_SIZE, 0);
            this.drawLine(context, from, to);
        }

        for (let y = 0; y <= GRID_SIZE; y++) {
            const from = this.gridIndexToScreen(0, y, 0);
            const to = this.gridIndexToScreen(GRID_SIZE, y, 0);
            this.drawLine(context, from, to);
        }
    }

    drawBoundary (context, size, color, dash) {
        const start = (GRID_SIZE - size) / 2;
        const end = start + size;
        const points = [
            this.gridIndexToScreen(start, start, 0),
            this.gridIndexToScreen(end, start, 0),
            this.gridIndexToScreen(end, end, 0),
            this.gridIndexToScreen(start, end, 0)
        ];

        context.save();
        context.strokeStyle = color;
        context.lineWidth = Math.max(2, 2 * this.zoom);
        context.setLineDash(dash.map(value => value * this.zoom));
        context.beginPath();
        context.moveTo(points[0].x, points[0].y);
        points.slice(1).forEach(point => context.lineTo(point.x, point.y));
        context.closePath();
        context.stroke();
        context.restore();
    }

    drawObjectInteractionRing (context, size, mode) {
        const isSelected = mode === 'selected';
        const isErasePreview = mode === 'erase';
        const stroke = isSelected ? '#ffbf00' : (isErasePreview ? '#ff4d4f' : '#4E97FF');
        const fill = isSelected ?
            'rgba(255, 191, 0, 0.12)' :
            (isErasePreview ? 'rgba(255, 77, 79, 0.14)' : 'rgba(78, 151, 255, 0.10)');

        context.save();
        context.strokeStyle = stroke;
        context.fillStyle = fill;
        context.lineWidth = Math.max(2, (isSelected ? 3 : 2) * this.zoom);
        if (!isSelected) {
            context.setLineDash([6 * this.zoom, 4 * this.zoom]);
        }
        context.beginPath();
        context.ellipse(0, size * 0.06, size * 1.28, size * 0.6, 0, 0, Math.PI * 2);
        context.fill();
        context.stroke();
        context.restore();
    }

    drawObject (context, object) {
        if (object.visible === false) return;

        const grid = this.objectToGrid(object);
        const screen = this.worldToScreen(grid);
        const size = this.objectSize(object) * this.zoom;
        const color = object.color || '#4E97FF';
        const selected = object.name === this.state.selectedObject;
        const hovered = !selected && object.name === this.state.hoveredObject;
        const erasePreview = hovered && this.state.activeTool === EDIT_TOOLS.ERASE;
        const animationTransform = this.animationTransform(object, size);

        context.save();
        context.globalAlpha *= animationTransform.opacity;
        context.translate(screen.x + animationTransform.x, screen.y + animationTransform.y);
        context.rotate(((Number((object.rotation || {}).z) || 0) * Math.PI / 180) + animationTransform.rotation);
        context.scale(animationTransform.scaleX, animationTransform.scaleY);

        if (selected || hovered) {
            this.drawObjectInteractionRing(
                context,
                size,
                selected ? 'selected' : (erasePreview ? 'erase' : 'hover')
            );
        }

        if (object.kind === 'terrain') {
            this.drawTerrainBlock(context, object, size);
        } else if (object.kind === 'ground') {
            this.drawGroundObject(context, color, size);
        } else if (object.kind === 'tree') {
            this.drawTreeObject(context, color, size);
        } else if (object.kind === 'grass') {
            this.drawGrassObject(context, color, size);
        } else if (object.kind === 'flower') {
            this.drawFlowerObject(context, object, size);
        } else if (object.kind === 'bush') {
            this.drawBushObject(context, color, size);
        } else if (object.kind === 'house') {
            this.drawHouseObject(context, color, size);
        } else if (object.kind === 'role') {
            this.drawRoleObject(context, color, size);
        } else if (object.kind === 'door') {
            this.drawDoorObject(context, color, size);
        } else if (object.kind === 'bench') {
            this.drawBenchObject(context, object, size);
        } else if (object.kind === 'lamp') {
            this.drawLampObject(context, object, size);
        } else if (object.kind === 'fountain') {
            this.drawFountainObject(context, object, size);
        } else if (object.kind === 'sign') {
            this.drawSignObject(context, object, size);
        } else if (object.kind === 'trashBin') {
            this.drawTrashBinObject(context, object, size);
        } else if (object.kind === 'image') {
            this.drawImageObject(context, object, size);
        } else {
            this.drawBlockObject(context, color, size);
        }

        context.restore();
    }

    hasActiveAnimations () {
        return this.getSortedObjects().some(object => {
            const animation = object.animation;
            if (!animation || !animation.playing) return false;
            const definition = this.animationDefinitionForObject(object, animation.id);
            if (!definition || definition.loop) return true;
            const duration = Math.max(1, Number(definition.duration || 0));
            return Date.now() - Number(animation.startedAt || 0) <= duration;
        });
    }

    animationDefinitionForObject (object, animationId) {
        const objectAnimations = Array.isArray(object.animations) ? object.animations : [];
        const animation = objectAnimations.find(item => item.id === animationId || item === animationId);
        const commonAnimation = COMMON_ANIMATIONS.find(item => item.id === animationId);
        if (typeof animation === 'string') return commonAnimation || {id: animation, label: animation};
        return animation || commonAnimation || null;
    }

    animationProgress (object, definition) {
        const animation = object.animation;
        if (!animation || !animation.playing || !definition) return 0;
        const duration = Math.max(1, Number(definition.duration || 1000));
        const elapsed = Math.max(0, Date.now() - Number(animation.startedAt || Date.now()));
        if (definition.loop || animation.loop) {
            return (elapsed % duration) / duration;
        }
        return Math.min(1, elapsed / duration);
    }

    animationTransform (object, size) {
        const animation = object.animation;
        const definition = animation ? this.animationDefinitionForObject(object, animation.id) : null;
        const progress = this.animationProgress(object, definition);
        const transform = {x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1, opacity: 1};
        if (!animation || !animation.playing || !definition) return transform;

        if (animation.id === 'walk') {
            transform.y = -Math.abs(Math.sin(progress * Math.PI * 2)) * size * 0.12;
        } else if (animation.id === 'jump') {
            const jumpHeight = Math.max(1, Number(animation.height) || 1);
            transform.y = -Math.sin(progress * Math.PI) * size * 0.85 * jumpHeight;
        } else if (animation.id === 'sway' || animation.id === 'shake') {
            transform.rotation = Math.sin(progress * Math.PI * 2) * 0.08;
        } else if (animation.id === 'spin') {
            transform.rotation = progress * Math.PI * 2;
        } else if (animation.id === 'blink') {
            transform.opacity = 0.35 + Math.abs(Math.sin(progress * Math.PI * 2)) * 0.65;
        } else if (animation.id === 'grow') {
            const scale = 0.55 + Math.sin(progress * Math.PI / 2) * 0.45;
            transform.scaleX = scale;
            transform.scaleY = scale;
        } else if (animation.id === 'open' && object.kind === 'door') {
            transform.scaleX = 1 - (Math.sin(progress * Math.PI / 2) * 0.55);
        } else if (animation.id === 'close' && object.kind === 'door') {
            transform.scaleX = 0.45 + (Math.sin(progress * Math.PI / 2) * 0.55);
        }
        return transform;
    }

    drawTerrainBlock (context, object, size) {
        const topColor = object.topColor || object.color || '#8fd17f';
        const sideColor = object.sideColor || this.shade(topColor, -36);
        const leftColor = this.shade(sideColor, -18);
        const blockHeight = size * 0.9;

        context.fillStyle = sideColor;
        context.beginPath();
        context.moveTo(0, -size * 0.18);
        context.lineTo(size, -size * 0.7);
        context.lineTo(size, -size * 0.7 + blockHeight);
        context.lineTo(0, -size * 0.18 + blockHeight);
        context.closePath();
        context.fill();

        context.fillStyle = leftColor;
        context.beginPath();
        context.moveTo(0, -size * 0.18);
        context.lineTo(-size, -size * 0.7);
        context.lineTo(-size, -size * 0.7 + blockHeight);
        context.lineTo(0, -size * 0.18 + blockHeight);
        context.closePath();
        context.fill();

        context.fillStyle = topColor;
        context.beginPath();
        context.moveTo(0, -size * 1.22);
        context.lineTo(size, -size * 0.7);
        context.lineTo(0, -size * 0.18);
        context.lineTo(-size, -size * 0.7);
        context.closePath();
        context.fill();

        context.strokeStyle = 'rgba(15, 23, 42, 0.22)';
        context.lineWidth = Math.max(1, this.zoom);
        context.stroke();
    }

    drawGroundObject (context, color, size) {
        context.fillStyle = color;
        context.beginPath();
        context.moveTo(0, -size * 0.5);
        context.lineTo(size, 0);
        context.lineTo(0, size * 0.5);
        context.lineTo(-size, 0);
        context.closePath();
        context.fill();

        context.strokeStyle = this.shade(color, -24);
        context.lineWidth = Math.max(1, this.zoom);
        context.stroke();
    }

    drawTreeObject (context, color, size) {
        context.fillStyle = '#8b5e34';
        context.fillRect(-size * 0.14, -size * 0.2, size * 0.28, size * 0.78);

        context.fillStyle = this.shade(color, -18);
        context.beginPath();
        context.ellipse(0, -size * 0.75, size * 0.62, size * 0.7, 0, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = color;
        context.beginPath();
        context.ellipse(-size * 0.18, -size * 0.96, size * 0.42, size * 0.48, 0, 0, Math.PI * 2);
        context.fill();
    }

    drawGrassObject (context, color, size) {
        context.strokeStyle = color;
        context.lineWidth = Math.max(1.4, size * 0.12);
        [-0.48, -0.24, 0, 0.24, 0.48].forEach((offset, index) => {
            context.beginPath();
            context.moveTo(offset * size, size * 0.18);
            context.lineTo((offset + (index % 2 ? 0.12 : -0.12)) * size, -size * 0.72);
            context.stroke();
        });
    }

    drawFlowerObject (context, object, size) {
        const color = object.color || '#ef4444';
        const accentColor = object.accentColor || '#facc15';

        context.strokeStyle = '#2f9e44';
        context.lineWidth = Math.max(1.2, size * 0.1);
        context.beginPath();
        context.moveTo(0, size * 0.2);
        context.lineTo(0, -size * 0.7);
        context.stroke();

        context.fillStyle = color;
        for (let index = 0; index < 5; index++) {
            const angle = (Math.PI * 2 / 5) * index;
            context.beginPath();
            context.ellipse(
                Math.cos(angle) * size * 0.18,
                -size * 0.82 + Math.sin(angle) * size * 0.18,
                size * 0.16,
                size * 0.11,
                angle,
                0,
                Math.PI * 2
            );
            context.fill();
        }

        context.fillStyle = accentColor;
        context.beginPath();
        context.arc(0, -size * 0.82, size * 0.11, 0, Math.PI * 2);
        context.fill();
    }

    drawBushObject (context, color, size) {
        context.fillStyle = this.shade(color, -16);
        context.beginPath();
        context.ellipse(-size * 0.24, -size * 0.24, size * 0.46, size * 0.38, 0, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = color;
        context.beginPath();
        context.ellipse(size * 0.18, -size * 0.28, size * 0.52, size * 0.44, 0, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = this.shade(color, 18);
        context.beginPath();
        context.ellipse(0, -size * 0.54, size * 0.38, size * 0.34, 0, 0, Math.PI * 2);
        context.fill();
    }

    drawHouseObject (context, color, size) {
        this.drawBlockObject(context, this.shade(color, 12), size);

        context.fillStyle = '#7c3f2d';
        context.beginPath();
        context.moveTo(0, -size * 1.9);
        context.lineTo(size * 1.1, -size * 1.05);
        context.lineTo(0, -size * 0.3);
        context.lineTo(-size * 1.1, -size * 1.05);
        context.closePath();
        context.fill();

        context.fillStyle = '#fff2cc';
        context.fillRect(-size * 0.18, -size * 0.2, size * 0.36, size * 0.58);
    }

    drawRoleObject (context, color, size) {
        context.fillStyle = this.shade(color, -22);
        context.beginPath();
        context.ellipse(0, size * 0.12, size * 0.42, size * 0.18, 0, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = color;
        context.beginPath();
        context.arc(0, -size * 0.78, size * 0.32, 0, Math.PI * 2);
        context.fill();
        context.fillRect(-size * 0.24, -size * 0.5, size * 0.48, size * 0.72);
    }

    drawDoorObject (context, color, size) {
        context.fillStyle = this.shade(color, -22);
        context.beginPath();
        context.ellipse(0, size * 0.15, size * 0.46, size * 0.16, 0, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = color;
        context.fillRect(-size * 0.38, -size * 1.25, size * 0.76, size * 1.42);
        context.strokeStyle = this.shade(color, -38);
        context.lineWidth = Math.max(1, this.zoom);
        context.strokeRect(-size * 0.38, -size * 1.25, size * 0.76, size * 1.42);

        context.fillStyle = '#facc15';
        context.beginPath();
        context.arc(size * 0.2, -size * 0.55, size * 0.06, 0, Math.PI * 2);
        context.fill();
    }

    drawBenchObject (context, object, size) {
        const color = object.color || '#8b5e34';
        const topColor = object.topColor || this.shade(color, 18);
        const sideColor = object.sideColor || this.shade(color, -28);

        context.fillStyle = 'rgba(15, 23, 42, 0.18)';
        context.beginPath();
        context.ellipse(0, size * 0.24, size * 0.95, size * 0.2, 0, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = sideColor;
        context.fillRect(-size * 0.72, -size * 0.62, size * 1.44, size * 0.18);
        context.fillRect(-size * 0.8, -size * 0.18, size * 1.6, size * 0.2);

        context.fillStyle = topColor;
        context.fillRect(-size * 0.78, -size * 0.75, size * 1.56, size * 0.14);
        context.fillRect(-size * 0.86, -size * 0.3, size * 1.72, size * 0.16);

        context.strokeStyle = this.shade(sideColor, -16);
        context.lineWidth = Math.max(1, this.zoom);
        [-0.58, 0.58].forEach(offset => {
            context.beginPath();
            context.moveTo(offset * size, -size * 0.18);
            context.lineTo((offset - 0.08) * size, size * 0.32);
            context.stroke();
        });
    }

    drawLampObject (context, object, size) {
        const color = object.color || '#475569';
        const accentColor = object.accentColor || '#fde68a';

        context.fillStyle = 'rgba(15, 23, 42, 0.18)';
        context.beginPath();
        context.ellipse(0, size * 0.24, size * 0.42, size * 0.15, 0, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = this.shade(color, -14);
        context.fillRect(-size * 0.08, -size * 1.65, size * 0.16, size * 1.85);
        context.fillStyle = color;
        context.fillRect(-size * 0.22, size * 0.12, size * 0.44, size * 0.12);

        context.fillStyle = 'rgba(253, 230, 138, 0.24)';
        context.beginPath();
        context.arc(0, -size * 1.82, size * 0.52, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = accentColor;
        context.beginPath();
        context.ellipse(0, -size * 1.82, size * 0.28, size * 0.22, 0, 0, Math.PI * 2);
        context.fill();

        context.strokeStyle = this.shade(color, -22);
        context.lineWidth = Math.max(1, this.zoom);
        context.strokeRect(-size * 0.22, -size * 1.96, size * 0.44, size * 0.28);
    }

    drawFountainObject (context, object, size) {
        const waterColor = object.color || '#60a5fa';
        const bowlColor = object.sideColor || '#64748b';
        const topColor = object.topColor || '#93c5fd';

        context.fillStyle = 'rgba(15, 23, 42, 0.18)';
        context.beginPath();
        context.ellipse(0, size * 0.26, size * 1.02, size * 0.28, 0, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = this.shade(bowlColor, -12);
        context.beginPath();
        context.ellipse(0, 0, size * 0.92, size * 0.36, 0, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = topColor;
        context.beginPath();
        context.ellipse(0, -size * 0.12, size * 0.78, size * 0.28, 0, 0, Math.PI * 2);
        context.fill();

        context.strokeStyle = waterColor;
        context.lineWidth = Math.max(1.2, size * 0.08);
        [-0.32, 0, 0.32].forEach(offset => {
            context.beginPath();
            context.moveTo(offset * size, -size * 0.22);
            context.quadraticCurveTo(offset * size * 0.45, -size * 1.2, 0, -size * 1.42);
            context.stroke();
        });

        context.fillStyle = 'rgba(255, 255, 255, 0.64)';
        context.beginPath();
        context.arc(0, -size * 1.42, size * 0.08, 0, Math.PI * 2);
        context.fill();
    }

    drawSignObject (context, object, size) {
        const color = object.color || '#16a34a';
        const sideColor = object.sideColor || '#854d0e';

        context.fillStyle = 'rgba(15, 23, 42, 0.16)';
        context.beginPath();
        context.ellipse(0, size * 0.22, size * 0.44, size * 0.13, 0, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = sideColor;
        context.fillRect(-size * 0.07, -size * 0.78, size * 0.14, size * 1.0);
        context.fillStyle = color;
        context.fillRect(-size * 0.52, -size * 1.12, size * 1.04, size * 0.42);

        context.strokeStyle = this.shade(color, -32);
        context.lineWidth = Math.max(1, this.zoom);
        context.strokeRect(-size * 0.52, -size * 1.12, size * 1.04, size * 0.42);

        context.strokeStyle = 'rgba(255, 255, 255, 0.85)';
        context.beginPath();
        context.moveTo(-size * 0.28, -size * 0.91);
        context.lineTo(size * 0.24, -size * 0.91);
        context.moveTo(size * 0.12, -size * 1.03);
        context.lineTo(size * 0.28, -size * 0.91);
        context.lineTo(size * 0.12, -size * 0.79);
        context.stroke();
    }

    drawTrashBinObject (context, object, size) {
        const color = object.color || '#2563eb';
        const topColor = object.topColor || this.shade(color, 18);
        const sideColor = object.sideColor || this.shade(color, -18);

        context.fillStyle = 'rgba(15, 23, 42, 0.16)';
        context.beginPath();
        context.ellipse(0, size * 0.18, size * 0.36, size * 0.12, 0, 0, Math.PI * 2);
        context.fill();

        context.fillStyle = sideColor;
        context.fillRect(-size * 0.28, -size * 0.66, size * 0.56, size * 0.82);
        context.fillStyle = color;
        context.fillRect(-size * 0.22, -size * 0.66, size * 0.44, size * 0.82);
        context.fillStyle = topColor;
        context.fillRect(-size * 0.34, -size * 0.78, size * 0.68, size * 0.14);

        context.strokeStyle = 'rgba(255, 255, 255, 0.7)';
        context.lineWidth = Math.max(1, this.zoom);
        context.beginPath();
        context.moveTo(-size * 0.08, -size * 0.44);
        context.lineTo(size * 0.08, -size * 0.44);
        context.moveTo(0, -size * 0.52);
        context.lineTo(0, -size * 0.28);
        context.stroke();
    }

    drawImageObject (context, object, size) {
        const image = this.getImage(object.image || object.dataUrl);
        if (!image || !image.complete || !image.naturalWidth) {
            this.drawBlockObject(context, object.color || '#4E97FF', size);
            return;
        }

        const ratio = image.naturalWidth / image.naturalHeight;
        const width = ratio >= 1 ? size * 2 : size * 2 * ratio;
        const height = ratio >= 1 ? size * 2 / ratio : size * 2;

        context.fillStyle = 'rgba(15, 23, 42, 0.18)';
        context.beginPath();
        context.ellipse(0, size * 0.16, width * 0.42, size * 0.18, 0, 0, Math.PI * 2);
        context.fill();

        context.drawImage(image, -width / 2, -height + size * 0.12, width, height);
    }

    drawBlockObject (context, color, size) {
        context.fillStyle = this.shade(color, -22);
        context.beginPath();
        context.moveTo(0, -size);
        context.lineTo(size, -size * 0.48);
        context.lineTo(size, size * 0.5);
        context.lineTo(0, size);
        context.closePath();
        context.fill();

        context.fillStyle = this.shade(color, -36);
        context.beginPath();
        context.moveTo(0, -size);
        context.lineTo(-size, -size * 0.48);
        context.lineTo(-size, size * 0.5);
        context.lineTo(0, size);
        context.closePath();
        context.fill();

        context.fillStyle = color;
        context.beginPath();
        context.moveTo(0, -size * 1.55);
        context.lineTo(size, -size);
        context.lineTo(0, -size * 0.45);
        context.lineTo(-size, -size);
        context.closePath();
        context.fill();
    }

    drawLine (context, from, to) {
        context.beginPath();
        context.moveTo(from.x, from.y);
        context.lineTo(to.x, to.y);
        context.stroke();
    }

    getSortedObjects () {
        const objects = (this.state.sceneState && this.state.sceneState.objects) || {};
        return Object.keys(objects)
            .map(name => ({
                name,
                ...objects[name]
            }))
            .sort((a, b) => {
                const aGrid = this.objectToGrid(a);
                const bGrid = this.objectToGrid(b);
                const aLayer = aGrid.x + aGrid.y + aGrid.z + (a.layer || 0);
                const bLayer = bGrid.x + bGrid.y + bGrid.z + (b.layer || 0);
                if (aLayer !== bLayer) return aLayer - bLayer;
                return a.name.localeCompare(b.name);
            });
    }

    objectToGrid (object) {
        const position = object.position || {};
        return {
            x: Number(position.x || object.x || 0),
            y: Number(position.y || object.y || 0),
            z: Number(position.z || object.z || 0)
        };
    }

    objectSize (object) {
        const scale = object.scale || {};
        const base = Number(object.size || 1);
        const maxScale = Math.max(
            Number(scale.x || 1),
            Number(scale.y || 1),
            Number(scale.z || 1)
        );
        return Math.max(12, base * maxScale * 18);
    }

    objectScalePercent (object) {
        const scale = object && object.scale ? object.scale : {};
        const maxScale = Math.max(
            Number(scale.x || 1),
            Number(scale.y || 1),
            Number(scale.z || 1)
        );
        return Math.round(maxScale * 100);
    }

    objectWithScalePercent (object, percent) {
        const normalizedPercent = clamp(Math.round(Number(percent) || 100), 10, 300);
        const scale = normalizedPercent / 100;
        return {
            ...object,
            scale: {
                x: scale,
                y: scale,
                z: scale
            }
        };
    }

    getAssetList () {
        const sceneState = normalizeSceneState(this.state.sceneState);
        return [
            ...ASSETS,
            ...sceneState.customAssets.map(asset => ({
                kind: 'image',
                size: 1.25,
                color: '#4E97FF',
                ...asset
            }))
        ];
    }

    getAssetGroups () {
        const sceneState = normalizeSceneState(this.state.sceneState);
        const customAssets = sceneState.customAssets.map(asset => ({
            kind: 'image',
            size: 1.25,
            color: '#4E97FF',
            ...asset
        }));
        return customAssets.length ?
            [
                ...BUILTIN_ASSET_GROUPS,
                {
                    id: 'custom',
                    label: '导入素材',
                    assets: customAssets
                }
            ] :
            BUILTIN_ASSET_GROUPS;
    }

    getAssetById (assetId) {
        return this.getAssetList().find(item => item.id === assetId) || ASSETS[0];
    }

    isTerrainAsset (asset) {
        return asset && asset.kind === 'terrain';
    }

    isTerrainObject (object) {
        return object && object.kind === 'terrain';
    }

    placementSurfaceZAtGrid (sceneState, grid, ignoredObjectName = null) {
        const objects = (sceneState && sceneState.objects) || {};
        return Object.keys(objects).reduce((surfaceZ, name) => {
            const object = objects[name];
            if (
                name === ignoredObjectName ||
                !this.isTerrainObject(object) ||
                object.visible === false ||
                !this.objectAtGrid(object, grid)
            ) {
                return surfaceZ;
            }

            const objectGrid = this.objectToGrid(object);
            return Math.max(surfaceZ, Math.round(Number(objectGrid.z) || 0) + TERRAIN_BLOCK_HEIGHT);
        }, 0);
    }

    alignNonTerrainObjectsToSurface (sceneState, objects, grid) {
        const sceneWithObjects = {
            ...sceneState,
            objects
        };

        Object.keys(objects).forEach(name => {
            const object = objects[name];
            if (this.isTerrainObject(object) || !this.objectAtGrid(object, grid)) {
                return;
            }

            const surfaceZ = this.placementSurfaceZAtGrid(sceneWithObjects, grid, name);
            const objectGrid = this.objectToGrid(object);
            if (Math.round(Number(objectGrid.z) || 0) === surfaceZ) {
                return;
            }

            objects[name] = {
                ...object,
                position: {
                    ...(object.position || {}),
                    z: surfaceZ
                }
            };
        });
    }

    animationOptionsForAsset (asset) {
        const animationIds = Array.isArray(asset && asset.animations) && asset.animations.length ?
            asset.animations :
            ['idle', 'jump', 'spin', 'blink'];
        return animationIds
            .map(animation => {
                if (typeof animation === 'string') {
                    return COMMON_ANIMATIONS.find(item => item.id === animation) || {
                        id: animation,
                        label: animation,
                        type: 'runtimeTransform',
                        duration: 800,
                        loop: false
                    };
                }
                return animation;
            })
            .filter(Boolean);
    }

    animationOptionsForObject (object) {
        const objectAnimations = Array.isArray(object && object.animations) && object.animations.length ?
            object.animations :
            this.animationOptionsForAsset(this.getAssetById(object && object.assetId));
        return objectAnimations
            .map(animation => (typeof animation === 'string' ?
                (COMMON_ANIMATIONS.find(item => item.id === animation) || {id: animation, label: animation}) :
                animation))
            .filter(Boolean);
    }

    gridKey (grid) {
        return `${grid.x}:${grid.y}`;
    }

    objectAtGrid (object, grid) {
        const objectGrid = this.objectToGrid(object);
        return Math.round(objectGrid.x) === grid.x &&
            Math.round(objectGrid.y) === grid.y;
    }

    objectAtGridAndZ (object, grid) {
        const objectGrid = this.objectToGrid(object);
        return this.objectAtGrid(object, grid) &&
            Math.round(objectGrid.z || 0) === Math.round(grid.z || 0);
    }

    objectBlocksGrid (object, grid) {
        if (!object || object.visible === false) return false;
        if (!this.objectIsBlocking(object)) return false;
        const objectGrid = this.objectToGrid(object);
        return Math.round(objectGrid.x) === Math.round(grid.x) &&
            Math.round(objectGrid.y) === Math.round(grid.y) &&
            Math.round(objectGrid.z || 0) === Math.round(grid.z || 0);
    }

    objectIsBlocking (object) {
        if (!object || object.visible === false) return false;
        const collisionMode = object.collisionMode || normalizeCollisionMode(object);
        return BLOCKING_COLLISION_MODES.includes(collisionMode);
    }

    objectParticipatesInSensing (object) {
        if (!object || object.visible === false) return false;
        const collisionMode = object.collisionMode || normalizeCollisionMode(object);
        return collisionMode !== COLLISION_MODES.DECORATION;
    }

    objectsShareGridCell (object, target) {
        const objectGrid = this.objectToGrid(object);
        const targetGrid = this.objectToGrid(target);
        return Math.round(objectGrid.x) === Math.round(targetGrid.x) &&
            Math.round(objectGrid.y) === Math.round(targetGrid.y) &&
            Math.round(objectGrid.z || 0) === Math.round(targetGrid.z || 0);
    }

    objectsTouchOnBlockingFaces (object, target) {
        if (!this.objectIsBlocking(object) && !this.objectIsBlocking(target)) return false;

        const objectGrid = this.objectToGrid(object);
        const targetGrid = this.objectToGrid(target);
        if (Math.round(objectGrid.z || 0) !== Math.round(targetGrid.z || 0)) return false;

        return SCRIPT_DIRECTIONS.some(direction => (
            Math.round(objectGrid.x + direction.delta.x) === Math.round(targetGrid.x) &&
            Math.round(objectGrid.y + direction.delta.y) === Math.round(targetGrid.y)
        ));
    }

    objectWithCollisionMode (object, collisionMode) {
        const nextCollisionMode = Object.keys(COLLISION_MODE_LABELS).includes(collisionMode) ?
            collisionMode :
            normalizeCollisionMode(object);
        const collidable = nextCollisionMode === COLLISION_MODES.SOLID;
        return {
            ...object,
            collision: collidable,
            collisionMode: nextCollisionMode,
            behavior: {
                ...(object.behavior || {}),
                collisionMode: nextCollisionMode,
                collidable,
                sensorEnabled: nextCollisionMode !== COLLISION_MODES.DECORATION,
                triggerEnabled: nextCollisionMode === COLLISION_MODES.TRIGGER
            }
        };
    }

    blockingObjectNameAtGrid (sceneState, grid, movingObjectName = null) {
        const objects = (sceneState && sceneState.objects) || {};
        return Object.keys(objects).find(name => (
            name !== movingObjectName &&
            this.objectBlocksGrid(objects[name], grid)
        )) || null;
    }

    gridIsBlockedForObject (sceneState, grid, movingObjectName) {
        return Boolean(this.blockingObjectNameAtGrid(sceneState, grid, movingObjectName));
    }

    normalizedObjectGrid (position) {
        return {
            x: clamp(Math.round(Number(position && position.x) || 0), -GRID_SIZE / 2, GRID_SIZE / 2 - 1),
            y: clamp(Math.round(Number(position && position.y) || 0), -GRID_SIZE / 2, GRID_SIZE / 2 - 1),
            z: Math.round(Number(position && position.z) || 0)
        };
    }

    resolveObjectTarget (sceneState, objectName, targetName) {
        const objects = (sceneState && sceneState.objects) || {};
        const normalizedTarget = String(targetName || '').trim();
        if (normalizedTarget === '自己' || normalizedTarget === 'self') {
            return objects[objectName] ? {name: objectName, ...objects[objectName]} : null;
        }

        const anyTarget = !normalizedTarget ||
            normalizedTarget === '*' ||
            normalizedTarget === '对象' ||
            normalizedTarget === '任意对象' ||
            normalizedTarget === '任意实体';
        if (anyTarget) {
            const targetNameValue = Object.keys(objects)
                .filter(name => name !== objectName)
                .find(name => objects[name] && objects[name].visible !== false);
            return targetNameValue ? {name: targetNameValue, ...objects[targetNameValue]} : null;
        }

        return objects[normalizedTarget] ?
            {name: normalizedTarget, ...objects[normalizedTarget]} :
            null;
    }

    resolveMessageTargetNames (sceneState, objectName, targetName) {
        const objects = (sceneState && sceneState.objects) || {};
        const normalizedTarget = String(targetName || '').trim();
        if (normalizedTarget === '自己' || normalizedTarget === 'self') {
            return objects[objectName] ? [objectName] : [];
        }

        const anyTarget = !normalizedTarget ||
            normalizedTarget === '*' ||
            normalizedTarget === '对象' ||
            normalizedTarget === '任意对象' ||
            normalizedTarget === '任意实体';
        if (anyTarget) {
            return Object.keys(objects).filter(name => (
                name !== objectName &&
                objects[name] &&
                objects[name].visible !== false
            ));
        }

        return objects[normalizedTarget] ? [normalizedTarget] : [];
    }

    directionForDelta (dx, dy) {
        if (dx === 0 && dy === 0) return SCRIPT_DIRECTIONS[0];
        return SCRIPT_DIRECTIONS.reduce((best, direction) => {
            const score = (dx * direction.delta.x) + (dy * direction.delta.y);
            return score > best.score ? {direction, score} : best;
        }, {direction: SCRIPT_DIRECTIONS[0], score: -Infinity}).direction;
    }

    normalizeAxis (axis, fallback = 'x') {
        const normalizedAxis = String(axis || '').toLowerCase();
        return POSITION_AXES.includes(normalizedAxis) ? normalizedAxis : fallback;
    }

    nextPositionByAxis (sceneState, objectName, object, axis, value, isDelta) {
        const normalizedAxis = this.normalizeAxis(axis);
        const position = this.normalizedObjectGrid(object.position || {});
        const numericValue = Number(value);
        const nextValue = Number.isNaN(numericValue) ? 0 : numericValue;
        const candidate = {
            ...position,
            [normalizedAxis]: isDelta ? position[normalizedAxis] + nextValue : nextValue
        };
        const normalizedCandidate = this.normalizedObjectGrid(candidate);

        if (!this.isTerrainObject(object) && normalizedAxis !== 'z') {
            normalizedCandidate.z = this.placementSurfaceZAtGrid(sceneState, normalizedCandidate, objectName);
        }

        if (this.gridIsBlockedForObject(sceneState, normalizedCandidate, objectName)) {
            return position;
        }
        return normalizedCandidate;
    }

    nextMovePosition (sceneState, objectName, object, direction, steps) {
        const start = object.position || {x: 0, y: 0, z: 0};
        const position = {
            x: Math.round(Number(start.x) || 0),
            y: Math.round(Number(start.y) || 0),
            z: Math.round(Number(start.z) || 0)
        };
        const moveSteps = Math.max(1, Number(steps) || 1);

        for (let index = 0; index < moveSteps; index++) {
            const candidateGrid = {
                x: clamp(Math.round(position.x + direction.delta.x), -GRID_SIZE / 2, GRID_SIZE / 2 - 1),
                y: clamp(Math.round(position.y + direction.delta.y), -GRID_SIZE / 2, GRID_SIZE / 2 - 1),
                z: position.z
            };
            const candidate = {
                ...candidateGrid,
                z: this.placementSurfaceZAtGrid(sceneState, candidateGrid, objectName)
            };
            if (
                (candidate.x === position.x && candidate.y === position.y) ||
                this.gridIsBlockedForObject(sceneState, candidate, objectName)
            ) {
                break;
            }
            position.x = candidate.x;
            position.y = candidate.y;
            position.z = candidate.z;
        }

        return position;
    }

    selectAsset (assetId) {
        const asset = this.getAssetById(assetId);
        this.setState({
            selectedAssetId: assetId,
            selectedObject: null,
            selectionBounds: null,
            activeTool: this.isTerrainAsset(asset) ? EDIT_TOOLS.PAINT : EDIT_TOOLS.PLACE
        });
    }

    selectTool (activeTool) {
        this.setState({activeTool});
        this.activateSceneWorkbench();
    }

    normalizeBrushSize (brushSize) {
        const normalizedSize = Math.round(Number(brushSize) || BRUSH_SIZE_OPTIONS[0]);
        return BRUSH_SIZE_OPTIONS.includes(normalizedSize) ?
            normalizedSize :
            BRUSH_SIZE_OPTIONS[0];
    }

    selectBrushSize (brushSize) {
        this.setState({
            brushSize: this.normalizeBrushSize(brushSize)
        });
        this.activateSceneWorkbench();
    }

    selectSelectionMoveTool () {
        if (!this.selectionObjectCount()) return;
        this.setState({
            activeTool: EDIT_TOOLS.MOVE_SELECTION,
            localSaveStatus: '在场景中点击目标格子以移动选区',
            localSaveStatusKind: 'info'
        });
        this.activateSceneWorkbench();
    }

    selectPasteTool () {
        if (!this.clipboardObjectCount()) return;
        this.setState({
            activeTool: EDIT_TOOLS.PASTE,
            localSaveStatus: '在场景中点击目标格子以粘贴区域',
            localSaveStatusKind: 'info'
        });
        this.activateSceneWorkbench();
    }

    clearSelectionBounds () {
        this.setState({
            selectedObject: null,
            selectionBounds: null
        }, this.draw);
    }

    isCodeWorkbenchActive () {
        return this.props.stage3DWorkspaceMode === 'code' ||
            this.state.activeWorkbenchTab === WORKBENCH_TABS.CODE;
    }

    activateSceneWorkbench () {
        if (!this.isCodeWorkbenchActive()) return false;
        this.selectWorkbenchTab(WORKBENCH_TABS.SCENE);
        return true;
    }

    selectWorkbenchTab (activeWorkbenchTab) {
        this.setState({activeWorkbenchTab});
        if (this.props.onSet3DWorkspaceMode) {
            const nextWorkspaceMode = activeWorkbenchTab === WORKBENCH_TABS.CODE ? 'code' : 'build';
            if (this.props.stage3DWorkspaceMode !== nextWorkspaceMode) {
                this.props.onSet3DWorkspaceMode(nextWorkspaceMode);
            }
        }
    }

    updateScriptDraft (field, value) {
        const numericFields = ['steps', 'height', 'distance', 'targetX', 'targetY', 'targetZ', 'repeatTimes', 'waitSeconds'];
        this.setState(previousState => ({
            scriptDraft: {
                ...previousState.scriptDraft,
                [field]: numericFields.includes(field) ? Number(value) : value
            }
        }));
    }

    updateSelectedBlocksXml (blockWorkspaceXml) {
        const selected = this.state.selectedObject;
        if (!selected) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const objects = {...sceneState.objects};
        if (!objects[selected]) return;

        const program = createObjectProgramState({
            ...(objects[selected].program || {}),
            source: 'blocks',
            blockWorkspaceXml,
            version: 1
        });
        objects[selected] = {
            ...objects[selected],
            program,
            blockWorkspaceXml: program.blockWorkspaceXml,
            blockScriptVersion: program.version,
            scripts: program.scripts
        };

        this.updateSceneState({
            ...sceneState,
            objects
        }, selected, false);
    }

    insertBlockToWorkspace (blockXml, dropPosition) {
        if (!this.blockWorkspaceRef.current || !this.blockWorkspaceRef.current.insertBlock) return;
        this.blockWorkspaceRef.current.insertBlock(blockXml, dropPosition);
    }

    updateBlockWorkspaceDragPreview (drag, event) {
        const workspace = this.blockWorkspaceRef.current;
        if (!drag || !workspace || !workspace.containsClientPoint) return;

        const pointerPosition = {
            clientX: event.clientX,
            clientY: event.clientY
        };
        const dropPosition = {
            clientX: event.clientX - (drag.grabOffsetX || 0),
            clientY: event.clientY - (drag.grabOffsetY || 0)
        };
        const isOverWorkspace = workspace.containsClientPoint(
            pointerPosition.clientX,
            pointerPosition.clientY
        );

        if (isOverWorkspace && workspace.updateExternalBlockPreview) {
            workspace.updateExternalBlockPreview(drag.block.xml, dropPosition);
        } else if (workspace.clearExternalBlockPreview) {
            workspace.clearExternalBlockPreview();
        }
    }

    clearBlockWorkspaceDragPreview () {
        const workspace = this.blockWorkspaceRef.current;
        if (workspace && workspace.clearExternalBlockPreview) {
            workspace.clearExternalBlockPreview();
        }
    }

    selectCodeCategory (categoryId) {
        this.setState({activeCodeCategory: categoryId});
    }

    handleCodeBlockDragStart (block, group, event) {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();

        const rect = event.currentTarget && event.currentTarget.getBoundingClientRect ?
            event.currentTarget.getBoundingClientRect() :
            {left: event.clientX, top: event.clientY};
        const grabOffsetX = event.clientX - rect.left;
        const grabOffsetY = event.clientY - rect.top;

        this.setState({
            draggingCodeBlock: {
                block,
                group,
                startX: event.clientX,
                startY: event.clientY,
                currentX: event.clientX,
                currentY: event.clientY,
                grabOffsetX,
                grabOffsetY,
                hasMoved: false
            }
        });
        window.addEventListener('mousemove', this.handleCodeBlockDragMove);
        window.addEventListener('mouseup', this.handleCodeBlockDragEnd);
    }

    handleCodeBlockDragMove (event) {
        const drag = this.state.draggingCodeBlock;
        if (!drag) return;
        event.preventDefault();
        this.updateBlockWorkspaceDragPreview(drag, event);

        this.setState(previousState => {
            if (!previousState.draggingCodeBlock) return null;

            const drag = previousState.draggingCodeBlock;
            const distance = Math.abs(event.clientX - drag.startX) + Math.abs(event.clientY - drag.startY);
            return {
                draggingCodeBlock: {
                    ...drag,
                    currentX: event.clientX,
                    currentY: event.clientY,
                    hasMoved: drag.hasMoved || distance > 4
                }
            };
        });
    }

    handleCodeBlockDragEnd (event) {
        const drag = this.state.draggingCodeBlock;
        window.removeEventListener('mousemove', this.handleCodeBlockDragMove);
        window.removeEventListener('mouseup', this.handleCodeBlockDragEnd);
        if (!drag) return;

        event.preventDefault();
        event.stopPropagation();

        const pointerPosition = {
            clientX: event.clientX,
            clientY: event.clientY
        };
        const dropPosition = {
            clientX: event.clientX - (drag.grabOffsetX || 0),
            clientY: event.clientY - (drag.grabOffsetY || 0)
        };
        const isOverWorkspace = this.blockWorkspaceRef.current &&
            this.blockWorkspaceRef.current.containsClientPoint &&
            this.blockWorkspaceRef.current.containsClientPoint(pointerPosition.clientX, pointerPosition.clientY);
        const shouldInsert = !drag.hasMoved || isOverWorkspace;

        this.clearBlockWorkspaceDragPreview();
        this.setState({draggingCodeBlock: null}, () => {
            if (shouldInsert) {
                this.insertBlockToWorkspace(drag.block.xml, isOverWorkspace ? dropPosition : null);
            }
        });
    }

    renderCodeBlockParts (block) {
        const parts = block.parts || [{text: block.label}];
        return parts.map((part, index) => {
            if (part.value) {
                return (
                    <span
                        className={classNames(styles.scratchBlockValue, {
                            [styles.scratchBlockDropdown]: part.dropdown
                        })}
                        key={`${block.label}-value-${index}`}
                    >
                        {part.value}
                    </span>
                );
            }

            return (
                <span
                    className={styles.scratchBlockText}
                    key={`${block.label}-text-${index}`}
                >
                    {part.text}
                </span>
            );
        });
    }

    placeSelectedAsset (grid) {
        const asset = this.getAssetById(this.state.selectedAssetId);
        if (this.isTerrainAsset(asset)) {
            const nextState = this.applyTerrainAssetToScene(
                normalizeSceneState(this.state.sceneState),
                asset,
                grid,
                false,
                true
            );
            if (nextState.changed) {
                this.updateSceneState(nextState.sceneState, nextState.selectedObject);
                this.setOperationStatus('已叠放 1 个地形方块', 'success');
            } else if (nextState.reason) {
                this.setOperationStatus(nextState.reason, 'warning');
            }
            return;
        }

        const name = this.nextObjectName(asset.label);
        const sceneState = normalizeSceneState(this.state.sceneState);
        const collisionMode = defaultCollisionModeForKind(asset.kind);
        const surfaceZ = this.placementSurfaceZAtGrid(sceneState, grid);
        const placementGrid = this.normalizedObjectGrid({
            ...grid,
            z: surfaceZ
        });
        const blockingObjectName = this.blockingObjectNameAtGrid(sceneState, placementGrid);
        if (blockingObjectName) {
            this.setState({selectedObject: blockingObjectName});
            return;
        }
        const nextState = {
            ...sceneState,
            enabled: true,
            objects: {
                ...(sceneState.objects || {}),
                [name]: {
                    kind: asset.kind,
                    assetId: asset.id,
                    color: asset.color,
                    topColor: asset.topColor,
                    sideColor: asset.sideColor,
                    accentColor: asset.accentColor,
                    programmable: asset.kind !== 'terrain',
                    interactive: asset.kind !== 'terrain',
                    collision: collisionMode === COLLISION_MODES.SOLID,
                    collisionMode,
                    behavior: {
                        programmable: asset.kind !== 'terrain',
                        clickable: asset.kind !== 'terrain',
                        collisionMode,
                        collidable: collisionMode === COLLISION_MODES.SOLID,
                        sensorEnabled: collisionMode !== COLLISION_MODES.DECORATION,
                        triggerEnabled: collisionMode === COLLISION_MODES.TRIGGER
                    },
                    image: asset.dataUrl || asset.image,
                    size: asset.size,
                    position: placementGrid,
                    scale: {
                        x: 1,
                        y: 1,
                        z: 1
                    },
                    layer: 0,
                    state: {value: 'normal'},
                    variables: this.defaultVariablesForScene(sceneState),
                    program: createObjectProgramState(),
                    scripts: [],
                    blockWorkspaceXml: '',
                    blockScriptVersion: 1,
                    animations: this.animationOptionsForAsset(asset),
                    currentCostume: 'default',
                    animationState: 'idle',
                    animation: null
                }
            }
        };

        this.updateSceneState(nextState, name);
    }

    beginTerrainEdit () {
        this.editStartState = normalizeSceneState(this.state.sceneState);
        this.editDraftState = normalizeSceneState(this.state.sceneState);
        this.editChanged = false;
        this.editChangedCells = 0;
        this.editTouchedCells = new Set();
    }

    applyActiveToolAtGrid (grid) {
        if (!this.editDraftState || !this.editTouchedCells) return;

        const asset = this.getAssetById(this.state.selectedAssetId);
        let nextSceneState = this.editDraftState;
        let selectedObject = null;
        let changed = false;
        let fallbackReason = '';

        this.brushCellsForGrid(grid).forEach(cell => {
            const touchKey = this.gridKey(cell);
            if (this.editTouchedCells.has(touchKey)) return;
            this.editTouchedCells.add(touchKey);

            let result = null;

            if (this.state.activeTool === EDIT_TOOLS.PLACE && this.isTerrainAsset(asset)) {
                result = this.applyTerrainAssetToScene(nextSceneState, asset, cell, false, true);
            } else if (this.state.activeTool === EDIT_TOOLS.PAINT && this.isTerrainAsset(asset)) {
                result = this.applyTerrainAssetToScene(nextSceneState, asset, cell, true);
            } else if (this.state.activeTool === EDIT_TOOLS.RAISE_TERRAIN && this.isTerrainAsset(asset)) {
                result = this.applyTerrainAssetToScene(nextSceneState, asset, cell, false, true);
            } else if (this.state.activeTool === EDIT_TOOLS.LOWER_TERRAIN) {
                result = this.lowerTerrainAtGrid(nextSceneState, cell);
            } else if (this.state.activeTool === EDIT_TOOLS.REPLACE && this.isTerrainAsset(asset)) {
                result = this.applyTerrainAssetToScene(nextSceneState, asset, cell, true);
            } else if (this.state.activeTool === EDIT_TOOLS.ERASE) {
                result = this.eraseTopObjectAtGrid(nextSceneState, cell);
            }

            if (!result || !result.changed) {
                fallbackReason = fallbackReason || (result && result.reason);
                return;
            }
            changed = true;
            this.editChangedCells += 1;
            nextSceneState = result.sceneState;
            selectedObject = result.selectedObject || selectedObject;
        });

        if (!changed) {
            if (fallbackReason) {
                this.setOperationStatus(fallbackReason, 'warning');
            }
            return;
        }

        this.editChanged = true;
        this.editDraftState = nextSceneState;
        this.syncRuntimeSceneState(this.editDraftState);
        this.setState({
            sceneState: this.editDraftState,
            selectedObject,
            redoStack: []
        });
    }

    commitTerrainEdit () {
        if (!this.editChanged || !this.editStartState) {
            this.editDraftState = null;
            this.editStartState = null;
            this.editTouchedCells = null;
            this.editChanged = false;
            this.editChangedCells = 0;
            return;
        }

        const historyState = this.editStartState;
        const statusMessage = this.describeTerrainEditCompletion(this.state.activeTool, this.editChangedCells);
        this.setState(previousState => ({
            undoStack: [...previousState.undoStack, historyState].slice(-HISTORY_LIMIT),
            redoStack: [],
            localSaveStatus: statusMessage,
            localSaveStatusKind: 'success'
        }));

        this.editDraftState = null;
        this.editStartState = null;
        this.editTouchedCells = null;
        this.editChanged = false;
        this.editChangedCells = 0;
    }

    describeTerrainEditCompletion (tool, changedCells) {
        const count = Math.max(1, Number(changedCells) || 1);
        if (tool === EDIT_TOOLS.RAISE_TERRAIN) return `已升高 ${count} 个地形格`;
        if (tool === EDIT_TOOLS.LOWER_TERRAIN) return `已降低 ${count} 个地形格`;
        if (tool === EDIT_TOOLS.PLACE) return `已叠放 ${count} 个地形方块`;
        if (tool === EDIT_TOOLS.PAINT) return `已铺设 ${count} 个地形格`;
        if (tool === EDIT_TOOLS.REPLACE) return `已更换 ${count} 个地形格材质`;
        if (tool === EDIT_TOOLS.ERASE) return `已拆除 ${count} 个对象`;
        return `已编辑 ${count} 个地形格`;
    }

    brushCellsForGrid (grid) {
        const brushSize = this.normalizeBrushSize(this.state.brushSize);
        const radius = Math.floor(brushSize / 2);
        const cells = [];

        for (let x = grid.x - radius; x <= grid.x + radius; x++) {
            for (let y = grid.y - radius; y <= grid.y + radius; y++) {
                const cell = {x, y, z: grid.z || 0};
                if (this.isInsideGrid(cell)) {
                    cells.push(cell);
                }
            }
        }

        return cells;
    }

    boundsFromGrids (startGrid, endGrid) {
        if (!startGrid || !endGrid) return null;
        return this.normalizeSelectionBounds({
            minX: Math.min(startGrid.x, endGrid.x),
            maxX: Math.max(startGrid.x, endGrid.x),
            minY: Math.min(startGrid.y, endGrid.y),
            maxY: Math.max(startGrid.y, endGrid.y)
        });
    }

    normalizeSelectionBounds (bounds) {
        if (!bounds) return null;
        const minX = clamp(Math.min(
            Math.round(Number(bounds.minX) || 0),
            Math.round(Number(bounds.maxX) || 0)
        ), -GRID_SIZE / 2, GRID_SIZE / 2 - 1);
        const maxX = clamp(Math.max(
            Math.round(Number(bounds.minX) || 0),
            Math.round(Number(bounds.maxX) || 0)
        ), -GRID_SIZE / 2, GRID_SIZE / 2 - 1);
        const minY = clamp(Math.min(
            Math.round(Number(bounds.minY) || 0),
            Math.round(Number(bounds.maxY) || 0)
        ), -GRID_SIZE / 2, GRID_SIZE / 2 - 1);
        const maxY = clamp(Math.max(
            Math.round(Number(bounds.minY) || 0),
            Math.round(Number(bounds.maxY) || 0)
        ), -GRID_SIZE / 2, GRID_SIZE / 2 - 1);

        return {minX, maxX, minY, maxY};
    }

    pointsForGridBounds (bounds) {
        return [
            this.worldToScreen({x: bounds.minX - 0.5, y: bounds.minY - 0.5, z: 0}),
            this.worldToScreen({x: bounds.maxX + 0.5, y: bounds.minY - 0.5, z: 0}),
            this.worldToScreen({x: bounds.maxX + 0.5, y: bounds.maxY + 0.5, z: 0}),
            this.worldToScreen({x: bounds.minX - 0.5, y: bounds.maxY + 0.5, z: 0})
        ];
    }

    objectIsInBounds (object, bounds) {
        if (!object || !bounds) return false;
        const grid = this.objectToGrid(object);
        return Math.round(grid.x) >= bounds.minX &&
            Math.round(grid.x) <= bounds.maxX &&
            Math.round(grid.y) >= bounds.minY &&
            Math.round(grid.y) <= bounds.maxY;
    }

    objectNamesInBounds (sceneState, bounds) {
        const normalizedBounds = this.normalizeSelectionBounds(bounds);
        if (!normalizedBounds) return [];
        const objects = (sceneState && sceneState.objects) || {};
        return Object.keys(objects)
            .filter(name => this.objectIsInBounds(objects[name], normalizedBounds))
            .sort((a, b) => a.localeCompare(b));
    }

    selectionObjectCount () {
        const sceneState = normalizeSceneState(this.state.sceneState);
        return this.objectNamesInBounds(sceneState, this.state.selectionBounds).length;
    }

    clipboardObjectCount () {
        const clipboard = this.state.areaClipboard;
        return clipboard && Array.isArray(clipboard.objects) ? clipboard.objects.length : 0;
    }

    applyTerrainAssetToScene (sceneState, asset, grid, replaceExisting, stackOnTop = false) {
        const objects = {...(sceneState.objects || {})};
        const terrainNames = this.terrainNamesAtGrid(sceneState, grid);
        const existingName = terrainNames[0] || null;
        const existing = existingName ? objects[existingName] : null;
        const collisionMode = defaultCollisionModeForKind(asset.kind);
        const targetZ = stackOnTop ?
            this.placementSurfaceZAtGrid(sceneState, grid) :
            Math.round(Number((existing && this.objectToGrid(existing).z) || 0));

        if (stackOnTop && terrainNames.length >= MAX_TERRAIN_HEIGHT_LEVELS) {
            return {
                changed: false,
                sceneState,
                selectedObject: existingName || null,
                reason: `当前格已达到最高 ${MAX_TERRAIN_HEIGHT_LEVELS} 层，无法继续升高`
            };
        }

        if (existing && !replaceExisting && !stackOnTop) {
            return {
                changed: false,
                sceneState,
                selectedObject: existingName,
                reason: '当前格已有地形；可使用“换材质”或“叠放”'
            };
        }

        const name = (!stackOnTop && existingName) ?
            existingName :
            this.nextObjectNameForObjects(asset.label, objects);
        objects[name] = {
            ...((!stackOnTop && existing) || {}),
            id: name,
            name,
            kind: asset.kind,
            assetId: asset.id,
            color: asset.color,
            topColor: asset.topColor,
            sideColor: asset.sideColor,
            accentColor: asset.accentColor,
            programmable: false,
            interactive: false,
            collision: collisionMode === COLLISION_MODES.SOLID,
            collisionMode,
            behavior: {
                programmable: false,
                clickable: false,
                collisionMode,
                collidable: collisionMode === COLLISION_MODES.SOLID,
                sensorEnabled: collisionMode !== COLLISION_MODES.DECORATION,
                triggerEnabled: collisionMode === COLLISION_MODES.TRIGGER
            },
            image: asset.dataUrl || asset.image,
            size: asset.size,
            position: {
                x: grid.x,
                y: grid.y,
                z: targetZ
            },
            scale: {
                x: 1,
                y: 1,
                z: 1
            },
            layer: 0,
            state: {value: 'normal'},
            variables: this.defaultVariablesForScene(sceneState),
            program: createObjectProgramState(),
            scripts: [],
            blockWorkspaceXml: '',
            blockScriptVersion: 1,
            animations: this.animationOptionsForAsset(asset),
            currentCostume: 'default',
            animationState: 'idle',
            animation: null
        };
        this.alignNonTerrainObjectsToSurface(sceneState, objects, grid);

        return {
            changed: true,
            sceneState: {
                ...sceneState,
                enabled: true,
                objects
            },
            selectedObject: name
        };
    }

    eraseTopObjectAtGrid (sceneState, grid) {
        const objects = {...(sceneState.objects || {})};
        const name = this.findTopObjectNameAtGrid(sceneState, grid);
        if (!name) {
            return {
                changed: false,
                sceneState,
                selectedObject: null,
                reason: '当前格没有可擦除对象'
            };
        }

        delete objects[name];
        this.alignNonTerrainObjectsToSurface(sceneState, objects, grid);
        return {
            changed: true,
            sceneState: {
                ...sceneState,
                enabled: true,
                objects
            },
            selectedObject: null
        };
    }

    lowerTerrainAtGrid (sceneState, grid) {
        const objects = {...(sceneState.objects || {})};
        const name = this.findTerrainNameAtGrid(sceneState, grid);
        if (!name) {
            return {
                changed: false,
                sceneState,
                selectedObject: null,
                reason: '当前格没有可降低的地形'
            };
        }

        delete objects[name];
        this.alignNonTerrainObjectsToSurface(sceneState, objects, grid);
        return {
            changed: true,
            sceneState: {
                ...sceneState,
                enabled: true,
                objects
            },
            selectedObject: null
        };
    }

    fillTerrainRect (startGrid, endGrid) {
        const asset = this.getAssetById(this.state.selectedAssetId);
        if (!this.isTerrainAsset(asset)) return;

        let sceneState = normalizeSceneState(this.state.sceneState);
        let selectedObject = null;
        let changed = false;
        const minX = Math.min(startGrid.x, endGrid.x);
        const maxX = Math.max(startGrid.x, endGrid.x);
        const minY = Math.min(startGrid.y, endGrid.y);
        const maxY = Math.max(startGrid.y, endGrid.y);

        for (let x = minX; x <= maxX; x++) {
            for (let y = minY; y <= maxY; y++) {
                const result = this.applyTerrainAssetToScene(sceneState, asset, {x, y, z: 0}, true);
                if (result.changed) {
                    sceneState = result.sceneState;
                    selectedObject = result.selectedObject;
                    changed = true;
                }
            }
        }

        if (changed) {
            this.updateSceneState(sceneState, selectedObject);
            this.setOperationStatus('已填充地形区域', 'success');
        } else {
            this.setOperationStatus('填充区域没有变化', 'warning');
        }
    }

    createClipboardFromBounds (sceneState, bounds) {
        const normalizedBounds = this.normalizeSelectionBounds(bounds);
        const objects = (sceneState && sceneState.objects) || {};
        const names = this.objectNamesInBounds(sceneState, normalizedBounds);
        if (!normalizedBounds || !names.length) return null;

        return {
            bounds: normalizedBounds,
            objects: names.map(name => {
                const object = clone(objects[name]);
                const grid = this.objectToGrid(object);
                return {
                    originalName: name,
                    object,
                    offset: {
                        x: Math.round(grid.x) - normalizedBounds.minX,
                        y: Math.round(grid.y) - normalizedBounds.minY,
                        z: Math.round(grid.z || 0)
                    }
                };
            })
        };
    }

    copySelectionToClipboard (cut = false) {
        const sceneState = normalizeSceneState(this.state.sceneState);
        const bounds = this.normalizeSelectionBounds(this.state.selectionBounds);
        const clipboard = this.createClipboardFromBounds(sceneState, bounds);
        if (!clipboard) {
            this.setOperationStatus('当前选区为空，无法复制', 'warning');
            return;
        }

        if (!cut) {
            this.setState({
                areaClipboard: clipboard,
                localSaveStatus: `已复制 ${clipboard.objects.length} 个对象`,
                localSaveStatusKind: 'success'
            });
            return;
        }

        const objects = {...sceneState.objects};
        const affectedGrids = clipboard.objects.map(entry => this.objectToGrid(entry.object));
        clipboard.objects.forEach(entry => {
            delete objects[entry.originalName];
        });
        affectedGrids.forEach(grid => this.alignNonTerrainObjectsToSurface(sceneState, objects, grid));

        this.updateSceneState({
            ...sceneState,
            enabled: true,
            objects
        }, null);
        this.setState({
            areaClipboard: clipboard,
            selectionBounds: null,
            activeTool: EDIT_TOOLS.BOX_SELECT,
            localSaveStatus: `已剪切 ${clipboard.objects.length} 个对象`,
            localSaveStatusKind: 'success'
        }, this.draw);
    }

    copiedObjectLabel (object, fallbackName) {
        const asset = this.getAssetById(object && object.assetId);
        const fallback = String((object && object.name) || fallbackName || '对象').replace(/\d+$/, '');
        return (asset && asset.label) || fallback || '对象';
    }

    clipboardFitsAtGrid (clipboard, grid) {
        return this.validateClipboardPlacement(
            normalizeSceneState(this.state.sceneState),
            clipboard,
            grid
        ).ok;
    }

    validateClipboardPlacement (sceneState, clipboard, grid) {
        if (!clipboard || !clipboard.bounds || !Array.isArray(clipboard.objects) || !clipboard.objects.length) {
            return {
                ok: false,
                message: '剪贴板为空，请先复制或剪切选区'
            };
        }

        const maxGrid = {
            x: Math.round(grid.x + (clipboard.bounds.maxX - clipboard.bounds.minX)),
            y: Math.round(grid.y + (clipboard.bounds.maxY - clipboard.bounds.minY)),
            z: 0
        };
        if (!this.isInsideGrid(grid) || !this.isInsideGrid(maxGrid)) {
            return {
                ok: false,
                message: '粘贴区域超出场景边界'
            };
        }

        const outsideEntry = clipboard.objects.find(entry => (
            !this.isInsideGrid({
                x: Math.round(grid.x + entry.offset.x),
                y: Math.round(grid.y + entry.offset.y),
                z: 0
            })
        ));
        if (outsideEntry) {
            return {
                ok: false,
                message: '粘贴对象超出场景边界'
            };
        }

        const conflictName = this.firstNonTerrainConflictForClipboard(sceneState, clipboard, grid);
        if (conflictName) {
            return {
                ok: false,
                message: `目标位置已有素材“${conflictName}”，请换位置或先删除`
            };
        }

        return {ok: true};
    }

    firstNonTerrainConflictForClipboard (sceneState, clipboard, grid) {
        const objects = (sceneState && sceneState.objects) || {};
        const entries = (clipboard && clipboard.objects) || [];
        for (const entry of entries) {
            if (this.isTerrainObject(entry.object)) continue;
            const targetGrid = {
                x: Math.round(grid.x + entry.offset.x),
                y: Math.round(grid.y + entry.offset.y),
                z: 0
            };
            const conflictName = this.firstNonTerrainObjectAtGrid(objects, targetGrid);
            if (conflictName) return conflictName;
        }
        return null;
    }

    firstNonTerrainObjectAtGrid (objects, grid, ignoredNames = new Set()) {
        return Object.keys(objects || {}).find(name => (
            !ignoredNames.has(name) &&
            !this.isTerrainObject(objects[name]) &&
            this.objectAtGrid(objects[name], grid)
        )) || null;
    }

    pastedBoundsAtGrid (clipboard, grid) {
        if (!clipboard || !clipboard.bounds) return null;
        return this.normalizeSelectionBounds({
            minX: grid.x,
            minY: grid.y,
            maxX: grid.x + (clipboard.bounds.maxX - clipboard.bounds.minX),
            maxY: grid.y + (clipboard.bounds.maxY - clipboard.bounds.minY)
        });
    }

    pasteClipboardAtGrid (grid) {
        const clipboard = this.state.areaClipboard;
        const sceneState = normalizeSceneState(this.state.sceneState);
        const placementValidation = this.validateClipboardPlacement(sceneState, clipboard, grid);
        if (!placementValidation.ok) {
            this.setOperationStatus(placementValidation.message, 'warning');
            return;
        }

        const objects = {...sceneState.objects};
        const sortedEntries = [...clipboard.objects].sort((a, b) => {
            const aTerrain = this.isTerrainObject(a.object) ? 0 : 1;
            const bTerrain = this.isTerrainObject(b.object) ? 0 : 1;
            return aTerrain - bTerrain;
        });
        const affectedGrids = [];
        let replacedTerrainCount = 0;

        sortedEntries.forEach(entry => {
            const object = clone(entry.object);
            const targetGrid = {
                x: Math.round(grid.x + entry.offset.x),
                y: Math.round(grid.y + entry.offset.y),
                z: Math.round(entry.offset.z || 0)
            };
            const name = this.nextObjectNameForObjects(
                this.copiedObjectLabel(object, entry.originalName),
                objects
            );

            if (this.isTerrainObject(object)) {
                const existingTerrainName = this.findTerrainNameAtGrid({objects}, targetGrid, targetGrid.z);
                if (existingTerrainName) {
                    delete objects[existingTerrainName];
                    replacedTerrainCount += 1;
                }
            }

            objects[name] = {
                ...object,
                id: name,
                name,
                position: {
                    ...(object.position || {}),
                    x: targetGrid.x,
                    y: targetGrid.y,
                    z: this.isTerrainObject(object) ? targetGrid.z : 0
                }
            };
            affectedGrids.push(targetGrid);
        });

        affectedGrids.forEach(gridCell => this.alignNonTerrainObjectsToSurface(sceneState, objects, gridCell));
        const nextBounds = this.pastedBoundsAtGrid(clipboard, grid);

        this.updateSceneState({
            ...sceneState,
            enabled: true,
            objects
        }, null);
        this.setState({
            selectedObject: null,
            selectionBounds: nextBounds,
            activeTool: EDIT_TOOLS.PASTE,
            localSaveStatus: replacedTerrainCount ?
                `已粘贴 ${clipboard.objects.length} 个对象，替换 ${replacedTerrainCount} 层地形` :
                `已粘贴 ${clipboard.objects.length} 个对象`,
            localSaveStatusKind: 'success'
        }, this.draw);
    }

    moveSelectionToGrid (grid) {
        const sceneState = normalizeSceneState(this.state.sceneState);
        const bounds = this.normalizeSelectionBounds(this.state.selectionBounds);
        const names = this.objectNamesInBounds(sceneState, bounds);
        if (!bounds || !names.length) {
            this.setOperationStatus('当前没有可移动的选区', 'warning');
            return;
        }

        const deltaX = Math.round(grid.x - bounds.minX);
        const deltaY = Math.round(grid.y - bounds.minY);
        if (deltaX === 0 && deltaY === 0) {
            this.setOperationStatus('选区已经在目标位置', 'warning');
            return;
        }

        const objects = {...sceneState.objects};
        const affectedGrids = [];
        const selectedNameSet = new Set(names);
        const targetMaxGrid = {
            x: Math.round(grid.x + (bounds.maxX - bounds.minX)),
            y: Math.round(grid.y + (bounds.maxY - bounds.minY)),
            z: 0
        };

        const canMove = this.isInsideGrid(grid) && this.isInsideGrid(targetMaxGrid) && names.every(name => {
            const objectGrid = this.objectToGrid(objects[name]);
            return this.isInsideGrid({
                x: Math.round(objectGrid.x + deltaX),
                y: Math.round(objectGrid.y + deltaY),
                z: 0
            });
        });

        if (!canMove) {
            this.setOperationStatus('移动区域超出场景边界', 'warning');
            return;
        }

        const moveConflictName = this.firstNonTerrainMoveConflict(objects, names, deltaX, deltaY);
        if (moveConflictName) {
            this.setOperationStatus(`目标位置已有素材“${moveConflictName}”，无法移动选区`, 'warning');
            return;
        }

        let replacedTerrainCount = 0;
        names.forEach(name => {
            const object = objects[name];
            const objectGrid = this.objectToGrid(object);
            const nextGrid = {
                x: Math.round(objectGrid.x + deltaX),
                y: Math.round(objectGrid.y + deltaY),
                z: Math.round(objectGrid.z || 0)
            };
            affectedGrids.push(objectGrid, nextGrid);
            if (this.isTerrainObject(object)) {
                const conflictName = this.findTerrainNameAtGrid({objects}, nextGrid, nextGrid.z);
                if (conflictName && !selectedNameSet.has(conflictName)) {
                    delete objects[conflictName];
                    replacedTerrainCount += 1;
                }
            }
            objects[name] = {
                ...object,
                position: {
                    ...(object.position || {}),
                    ...nextGrid
                }
            };
        });
        affectedGrids.forEach(gridCell => this.alignNonTerrainObjectsToSurface(sceneState, objects, gridCell));

        const nextBounds = this.normalizeSelectionBounds({
            minX: bounds.minX + deltaX,
            maxX: bounds.maxX + deltaX,
            minY: bounds.minY + deltaY,
            maxY: bounds.maxY + deltaY
        });

        this.updateSceneState({
            ...sceneState,
            enabled: true,
            objects
        }, null);
        this.setState({
            selectedObject: null,
            selectionBounds: nextBounds,
            activeTool: EDIT_TOOLS.BOX_SELECT,
            localSaveStatus: replacedTerrainCount ?
                `已移动 ${names.length} 个对象，替换 ${replacedTerrainCount} 层地形` :
                `已移动 ${names.length} 个对象`,
            localSaveStatusKind: 'success'
        }, this.draw);
    }

    firstNonTerrainMoveConflict (objects, names, deltaX, deltaY) {
        const selectedNameSet = new Set(names);
        for (const name of names) {
            const object = objects[name];
            if (this.isTerrainObject(object)) continue;
            const objectGrid = this.objectToGrid(object);
            const targetGrid = {
                x: Math.round(objectGrid.x + deltaX),
                y: Math.round(objectGrid.y + deltaY),
                z: 0
            };
            const conflictName = this.firstNonTerrainObjectAtGrid(objects, targetGrid, selectedNameSet);
            if (conflictName) return conflictName;
        }
        return null;
    }

    findTerrainNameAtGrid (sceneState, grid, exactZ = null) {
        const terrainNames = this.terrainNamesAtGrid(sceneState, grid);
        if (typeof exactZ === 'number') {
            const objects = (sceneState && sceneState.objects) || {};
            return terrainNames.find(name => this.objectAtGridAndZ(objects[name], {
                ...grid,
                z: exactZ
            })) || null;
        }
        return terrainNames[0] || null;
    }

    terrainNamesAtGrid (sceneState, grid) {
        const objects = (sceneState && sceneState.objects) || {};
        return Object.keys(objects)
            .filter(name => this.isTerrainObject(objects[name]) &&
                this.objectAtGrid(objects[name], grid))
            .sort((a, b) => {
                const aGrid = this.objectToGrid(objects[a]);
                const bGrid = this.objectToGrid(objects[b]);
                if (aGrid.z !== bGrid.z) return bGrid.z - aGrid.z;
                return b.localeCompare(a);
            });
    }

    findTopObjectNameAtGrid (sceneState, grid) {
        const objects = (sceneState && sceneState.objects) || {};
        return Object.keys(objects)
            .map(name => ({
                name,
                ...objects[name]
            }))
            .filter(object => this.objectAtGrid(object, grid))
            .sort((a, b) => {
                const aGrid = this.objectToGrid(a);
                const bGrid = this.objectToGrid(b);
                const aLayer = aGrid.x + aGrid.y + aGrid.z + (a.layer || 0);
                const bLayer = bGrid.x + bGrid.y + bGrid.z + (b.layer || 0);
                if (aLayer !== bLayer) return bLayer - aLayer;
                return b.name.localeCompare(a.name);
            })[0]?.name || null;
    }

    handleImportClick () {
        if (this.importInput) {
            this.importInput.click();
        }
    }

    handleImportChange (event) {
        const input = event.target;
        const file = input.files && input.files[0];
        if (!file) return;
        if (!file.type || !file.type.startsWith('image/')) {
            input.value = '';
            return;
        }

        const reader = new window.FileReader();
        reader.onload = () => {
            const sceneState = normalizeSceneState(this.state.sceneState);
            const id = `custom-${Date.now()}-${Math.round(Math.random() * 100000)}`;
            const label = file.name.replace(/\.[^.]+$/, '').trim() || '素材';
            const asset = {
                id,
                label,
                kind: 'image',
                dataUrl: reader.result,
                color: '#4E97FF',
                size: 1.25,
                animations: ['idle', 'jump', 'spin', 'blink']
            };
            const nextState = {
                ...sceneState,
                enabled: true,
                customAssets: [...sceneState.customAssets, asset]
            };
            this.updateSceneState(nextState, null);
            this.setState({selectedAssetId: id});
            input.value = '';
        };
        reader.readAsDataURL(file);
    }

    deleteSelectedObject () {
        const selected = this.state.selectedObject;
        if (!selected) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const objects = {...(sceneState.objects || {})};
        delete objects[selected];

        this.updateSceneState({
            ...sceneState,
            objects
        }, null);
    }

    undo () {
        this.setState(previousState => {
            if (!previousState.undoStack.length) return null;
            const undoStack = previousState.undoStack.slice();
            const sceneState = undoStack.pop();
            const currentState = normalizeSceneState(previousState.sceneState);
            const selectedObject = this.resolveSelectedObject(sceneState, previousState.selectedObject);

            this.syncRuntimeSceneState(sceneState);
            return {
                sceneState,
                selectedObject,
                undoStack,
                redoStack: [currentState, ...previousState.redoStack].slice(0, HISTORY_LIMIT)
            };
        });
    }

    redo () {
        this.setState(previousState => {
            if (!previousState.redoStack.length) return null;
            const redoStack = previousState.redoStack.slice();
            const sceneState = redoStack.shift();
            const currentState = normalizeSceneState(previousState.sceneState);
            const selectedObject = this.resolveSelectedObject(sceneState, previousState.selectedObject);

            this.syncRuntimeSceneState(sceneState);
            return {
                sceneState,
                selectedObject,
                undoStack: [...previousState.undoStack, currentState].slice(-HISTORY_LIMIT),
                redoStack
            };
        });
    }

    openSceneTemplateBrowser () {
        this.setState({
            activeWorkbenchTab: WORKBENCH_TABS.SCENE,
            sceneTemplateBrowserOpen: true
        });
    }

    closeSceneTemplateBrowser () {
        this.setState({
            sceneTemplateBrowserOpen: false
        });
    }

    updateSceneTemplateSearch (event) {
        this.setState({
            sceneTemplateSearch: event.target.value
        });
    }

    selectSceneTemplateCategory (categoryId) {
        this.setState({
            sceneTemplateCategory: categoryId
        });
    }

    filteredSceneTemplates () {
        const category = this.state.sceneTemplateCategory;
        const search = String(this.state.sceneTemplateSearch || '').trim().toLowerCase();

        return SCENE_TEMPLATES.filter(template => {
            const categoryMatched = category === 'all' || template.category === category;
            if (!categoryMatched) return false;
            if (!search) return true;

            const searchableText = [
                template.label,
                template.description,
                ...(template.tags || [])
            ].join(' ').toLowerCase();
            return searchableText.includes(search);
        });
    }

    loadSceneTemplate (templateId) {
        const template = SCENE_TEMPLATES.find(item => item.id === templateId);
        if (!template) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        if (
            Object.keys(sceneState.objects || {}).length &&
            typeof window !== 'undefined' &&
            !window.confirm(`加载“${template.label}”会替换当前场景，是否继续？`)
        ) {
            return;
        }

        const nextState = {
            ...sceneState,
            enabled: true,
            background: template.background,
            objects: clone(template.objects)
        };
        const selectedObject = template.defaultSelectedObject &&
            nextState.objects &&
            nextState.objects[template.defaultSelectedObject] ?
                template.defaultSelectedObject :
                null;

        this.updateSceneState(nextState, selectedObject);
        this.setState({
            activeTool: EDIT_TOOLS.SELECT,
            activeWorkbenchTab: WORKBENCH_TABS.SCENE,
            selectionBounds: null,
            areaClipboard: null,
            sceneTemplateBrowserOpen: false,
            localSaveStatus: `已加载“${template.label}”`,
            localSaveStatusKind: 'success'
        }, () => {
            window.requestAnimationFrame(() => {
                this.resetBestView();
            });
        });
    }

    updateWeather (field, rawValue) {
        const sceneState = normalizeSceneState(this.state.sceneState);
        const value = field === 'temperature' ? Number(rawValue) : rawValue;
        const nextState = {
            ...sceneState,
            enabled: true,
            weather: {
                ...sceneState.weather,
                [field]: field === 'temperature' && Number.isNaN(value) ? 0 : value
            }
        };
        this.updateSceneState(nextState, this.state.selectedObject);
    }

    updateSelectedPosition (axis, rawValue) {
        const selected = this.state.selectedObject;
        if (!selected) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const objects = {...sceneState.objects};
        const object = {
            ...objects[selected],
            position: {
                ...(objects[selected].position || {})
            }
        };

        const numericValue = Number(rawValue);
        object.position[axis] = axis === 'z' ?
            (Number.isNaN(numericValue) ? 0 : numericValue) :
            clamp(Math.round(Number.isNaN(numericValue) ? 0 : numericValue), -GRID_SIZE / 2, GRID_SIZE / 2 - 1);

        if (!this.isTerrainObject(object) && axis !== 'z') {
            object.position.z = this.placementSurfaceZAtGrid(sceneState, object.position, selected);
        }
        object.position = this.normalizedObjectGrid(object.position);
        if (this.gridIsBlockedForObject(sceneState, object.position, selected)) {
            return;
        }
        objects[selected] = object;

        this.updateSceneState({
            ...sceneState,
            objects
        }, selected);
    }

    updateSelectedColor (rawValue) {
        const selected = this.state.selectedObject;
        if (!selected) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const objects = {...sceneState.objects};
        objects[selected] = {
            ...objects[selected],
            color: rawValue
        };

        this.updateSceneState({
            ...sceneState,
            objects
        }, selected);
    }

    updateSelectedScale (rawValue) {
        const selected = this.state.selectedObject;
        if (!selected) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const objects = {...sceneState.objects};
        if (!objects[selected]) return;

        objects[selected] = this.objectWithScalePercent(objects[selected], rawValue);

        this.updateSceneState({
            ...sceneState,
            objects
        }, selected);
    }

    updateSelectedState (rawValue) {
        const selected = this.state.selectedObject;
        if (!selected) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const objects = {...sceneState.objects};
        objects[selected] = {
            ...objects[selected],
            state: {
                ...(objects[selected].state || {}),
                value: rawValue
            }
        };

        this.updateSceneState({
            ...sceneState,
            objects
        }, selected);
    }

    updateSelectedVariable (variableId, rawValue) {
        const selected = this.state.selectedObject;
        if (!selected) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const objects = {...sceneState.objects};
        const variable = this.programmingVariables(sceneState).find(item => item.value === variableId);
        if (!variable || !objects[selected]) return;

        const value = Number(rawValue);
        objects[selected] = {
            ...objects[selected],
            variables: {
                ...DEFAULT_OBJECT_VARIABLES,
                ...(objects[selected].variables || {}),
                [variable.value]: Number.isNaN(value) ? 0 : value
            }
        };

        this.updateSceneState({
            ...sceneState,
            objects
        }, selected);
    }

    programmingVariables (sceneState = this.state.sceneState) {
        return normalizeSceneState(sceneState).programming.variables;
    }

    defaultVariablesForScene (sceneState = this.state.sceneState) {
        return this.programmingVariables(sceneState).reduce((variables, variable) => ({
            ...variables,
            [variable.value]: 0
        }), {});
    }

    programmingMessages (sceneState = this.state.sceneState) {
        return normalizeSceneState(sceneState).programming.messages;
    }

    variableMenuOptionsForBlocks () {
        return this.programmingVariables().map(variable => [variable.label, variable.value]);
    }

    messageMenuOptionsForBlocks () {
        return this.programmingMessages().map(message => [message.label, message.value]);
    }

    createProgrammingItemId (label, prefix, items) {
        const normalizedLabel = String(label || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');
        const base = normalizedLabel || `${prefix}_${Date.now().toString(36)}`;
        const usedIds = new Set(items.map(item => item.value));
        let candidate = base;
        let suffix = 2;
        while (usedIds.has(candidate)) {
            candidate = `${base}_${suffix}`;
            suffix++;
        }
        return candidate;
    }

    promptProgrammingItem (items, itemName, actionName) {
        if (typeof window === 'undefined' || !items.length) return null;
        const itemLabels = items.map(item => item.label).join('、');
        const rawName = window.prompt(`请输入要${actionName}的${itemName}名称：${itemLabels}`, items[0].label);
        if (!rawName) return null;
        const normalizedName = rawName.trim();
        return items.find(item => item.label === normalizedName || item.value === normalizedName) || null;
    }

    addProgrammingVariable () {
        if (typeof window === 'undefined') return;
        const rawLabel = window.prompt('请输入新变量名称：', '新变量');
        const label = rawLabel && rawLabel.trim();
        if (!label) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const variables = this.programmingVariables(sceneState);
        const value = this.createProgrammingItemId(label, 'variable', variables);
        const nextObjects = Object.keys(sceneState.objects || {}).reduce((objects, name) => {
            const object = sceneState.objects[name];
            objects[name] = {
                ...object,
                variables: {
                    ...(object.variables || {}),
                    [value]: 0
                }
            };
            return objects;
        }, {});

        this.updateSceneState({
            ...sceneState,
            programming: {
                ...sceneState.programming,
                variables: [...variables, {value, label}]
            },
            objects: nextObjects
        }, this.state.selectedObject);
    }

    renameProgrammingVariable () {
        if (typeof window === 'undefined') return;
        const sceneState = normalizeSceneState(this.state.sceneState);
        const variables = this.programmingVariables(sceneState);
        const variable = this.promptProgrammingItem(variables, '变量', '重命名');
        if (!variable) return;

        const rawLabel = window.prompt('请输入新的变量名称：', variable.label);
        const label = rawLabel && rawLabel.trim();
        if (!label) return;

        this.updateSceneState({
            ...sceneState,
            programming: {
                ...sceneState.programming,
                variables: variables.map(item => (
                    item.value === variable.value ? {...item, label} : item
                ))
            }
        }, this.state.selectedObject);
    }

    deleteProgrammingVariable () {
        if (typeof window === 'undefined') return;
        const sceneState = normalizeSceneState(this.state.sceneState);
        const variables = this.programmingVariables(sceneState);
        const variable = this.promptProgrammingItem(variables, '变量', '删除');
        if (!variable) return;
        if (!window.confirm(`删除变量“${variable.label}”后，对应积木会保留但运行时值会变为 0。确定删除吗？`)) {
            return;
        }

        const nextObjects = Object.keys(sceneState.objects || {}).reduce((objects, name) => {
            const object = sceneState.objects[name];
            const nextVariables = {...(object.variables || {})};
            delete nextVariables[variable.value];
            objects[name] = {
                ...object,
                variables: nextVariables
            };
            return objects;
        }, {});

        this.updateSceneState({
            ...sceneState,
            programming: {
                ...sceneState.programming,
                variables: variables.filter(item => item.value !== variable.value)
            },
            objects: nextObjects
        }, this.state.selectedObject);
    }

    addProgrammingMessage () {
        if (typeof window === 'undefined') return;
        const rawLabel = window.prompt('请输入新消息名称：', '新消息');
        const label = rawLabel && rawLabel.trim();
        if (!label) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const messages = this.programmingMessages(sceneState);
        const value = this.createProgrammingItemId(label, 'message', messages);
        this.updateSceneState({
            ...sceneState,
            programming: {
                ...sceneState.programming,
                messages: [...messages, {value, label}]
            }
        }, this.state.selectedObject);
    }

    renameProgrammingMessage () {
        if (typeof window === 'undefined') return;
        const sceneState = normalizeSceneState(this.state.sceneState);
        const messages = this.programmingMessages(sceneState);
        const message = this.promptProgrammingItem(messages, '消息', '重命名');
        if (!message) return;

        const rawLabel = window.prompt('请输入新的消息名称：', message.label);
        const label = rawLabel && rawLabel.trim();
        if (!label) return;

        this.updateSceneState({
            ...sceneState,
            programming: {
                ...sceneState.programming,
                messages: messages.map(item => (
                    item.value === message.value ? {...item, label} : item
                ))
            }
        }, this.state.selectedObject);
    }

    deleteProgrammingMessage () {
        if (typeof window === 'undefined') return;
        const sceneState = normalizeSceneState(this.state.sceneState);
        const messages = this.programmingMessages(sceneState);
        const message = this.promptProgrammingItem(messages, '消息', '删除');
        if (!message) return;
        if (!window.confirm(`删除消息“${message.label}”后，对应积木会保留但无法触发。确定删除吗？`)) {
            return;
        }

        this.updateSceneState({
            ...sceneState,
            programming: {
                ...sceneState.programming,
                messages: messages.filter(item => item.value !== message.value)
            }
        }, this.state.selectedObject);
    }

    setLocalSaveStatus (message, kind = 'info') {
        this.setState({
            localSaveStatus: message,
            localSaveStatusKind: kind
        });
    }

    setOperationStatus (message, kind = 'info') {
        this.setState({
            localSaveStatus: message,
            localSaveStatusKind: kind
        });
    }

    statusClassName (baseClassName) {
        return classNames(baseClassName, {
            [styles.statusSuccess]: this.state.localSaveStatusKind === 'success',
            [styles.statusWarning]: this.state.localSaveStatusKind === 'warning'
        });
    }

    createEditorStateSnapshot () {
        return {
            selectedObject: this.state.selectedObject,
            selectedAssetId: this.state.selectedAssetId,
            activeTool: this.state.activeTool,
            brushSize: this.normalizeBrushSize(this.state.brushSize),
            activeWorkbenchTab: this.state.activeWorkbenchTab,
            activeCodeCategory: this.state.activeCodeCategory,
            pan: {
                x: this.pan.x,
                y: this.pan.y
            },
            zoom: this.zoom
        };
    }

    normalizeEditorStateForScene (editorState, sceneState) {
        const sceneObjectNames = Object.keys((sceneState && sceneState.objects) || {});
        const customAssets = (sceneState && sceneState.customAssets) || [];
        const allAssets = [
            ...ASSETS,
            ...customAssets
        ];
        const assetIds = new Set([
            ...allAssets.map(asset => asset.id)
        ]);
        const readNumber = value => (
            value === null || typeof value === 'undefined' || value === '' ?
                NaN :
                Number(value)
        );

        const selectedObject = editorState &&
            sceneObjectNames.includes(editorState.selectedObject) ?
                editorState.selectedObject :
                null;
        const selectedAssetId = editorState &&
            assetIds.has(editorState.selectedAssetId) ?
                editorState.selectedAssetId :
                ASSETS[0].id;
        const selectedAsset = allAssets.find(asset => asset.id === selectedAssetId);
        let activeTool = editorState &&
            Object.values(EDIT_TOOLS).includes(editorState.activeTool) ?
                editorState.activeTool :
                (this.isTerrainAsset(selectedAsset) ? EDIT_TOOLS.PAINT : EDIT_TOOLS.PLACE);
        if (activeTool === EDIT_TOOLS.MOVE_SELECTION || activeTool === EDIT_TOOLS.PASTE) {
            activeTool = EDIT_TOOLS.BOX_SELECT;
        }
        const activeWorkbenchTab = editorState &&
            Object.values(WORKBENCH_TABS).includes(editorState.activeWorkbenchTab) ?
                editorState.activeWorkbenchTab :
                WORKBENCH_TABS.SCENE;
        const activeCodeCategory = editorState &&
            THREE_D_BLOCK_PALETTE.some(group => group.id === editorState.activeCodeCategory) ?
                editorState.activeCodeCategory :
                THREE_D_BLOCK_PALETTE[0].id;
        const brushSize = editorState ?
            this.normalizeBrushSize(editorState.brushSize) :
            BRUSH_SIZE_OPTIONS[0];

        return {
            selectedObject,
            selectedAssetId,
            activeTool,
            brushSize,
            activeWorkbenchTab,
            activeCodeCategory,
            pan: {
                x: readNumber(editorState && editorState.pan && editorState.pan.x),
                y: readNumber(editorState && editorState.pan && editorState.pan.y)
            },
            zoom: readNumber(editorState && editorState.zoom)
        };
    }

    restoreSceneFromLocalPayload (payload, fallbackStatus) {
        const sceneState = sceneStateFromPayload(payload);
        if (!sceneState) {
            throw new Error('Invalid local Scratch 3D draft');
        }

        const nextSceneState = normalizeSceneState(sceneState);
        const editorState = this.normalizeEditorStateForScene(
            editorStateFromPayload(payload),
            nextSceneState
        );

        if (Number.isFinite(editorState.pan.x) && Number.isFinite(editorState.pan.y)) {
            this.pan = editorState.pan;
        }
        if (Number.isFinite(editorState.zoom)) {
            this.zoom = clamp(editorState.zoom, MIN_ZOOM, this.maximumZoom());
        }
        this.syncRuntimeSceneState(nextSceneState);
        this.setState({
            sceneState: nextSceneState,
            selectedObject: editorState.selectedObject,
            selectedAssetId: editorState.selectedAssetId,
            activeTool: editorState.activeTool,
            brushSize: editorState.brushSize,
            activeWorkbenchTab: editorState.activeWorkbenchTab,
            activeCodeCategory: editorState.activeCodeCategory,
            undoStack: [],
            redoStack: []
        }, () => {
            this.draw();
        });
        this.setLocalSaveStatus(fallbackStatus, 'success');
    }

    saveSceneToLocalDraft () {
        if (typeof window === 'undefined' || !window.localStorage) {
            this.setLocalSaveStatus('当前环境不支持本地草稿', 'warning');
            return;
        }

        const payload = createScratch3DProjectPayload(normalizeSceneState(this.state.sceneState), {
            title: '3D作品',
            editorState: this.createEditorStateSnapshot()
        });
        try {
            window.localStorage.setItem(LOCAL_SCRATCH3D_PROJECT_STORAGE_KEY, JSON.stringify(payload));
            this.setLocalSaveStatus(`本地草稿已保存 ${new Date(payload.savedAt).toLocaleTimeString()}`, 'success');
        } catch (error) {
            this.setLocalSaveStatus('本地草稿保存失败', 'warning');
        }
    }

    loadSceneFromLocalDraft () {
        if (typeof window === 'undefined' || !window.localStorage) {
            this.setLocalSaveStatus('当前环境不支持本地草稿', 'warning');
            return;
        }

        const storageKeys = [
            LOCAL_SCRATCH3D_PROJECT_STORAGE_KEY,
            ...LEGACY_LOCAL_3D_SCENE_STORAGE_KEYS
        ];
        const raw = storageKeys
            .map(key => window.localStorage.getItem(key))
            .find(Boolean);
        if (!raw) {
            this.setLocalSaveStatus('暂无本地草稿', 'warning');
            return;
        }

        try {
            const payload = JSON.parse(raw);
            this.restoreSceneFromLocalPayload(payload, '本地草稿已读取');
        } catch (error) {
            this.setLocalSaveStatus('本地草稿读取失败', 'warning');
        }
    }

    updateSelectedCollisionMode (collisionMode) {
        const selected = this.state.selectedObject;
        if (!selected || !Object.keys(COLLISION_MODE_LABELS).includes(collisionMode)) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const objects = {...sceneState.objects};
        const object = objects[selected];
        objects[selected] = this.objectWithCollisionMode(object, collisionMode);

        this.updateSceneState({
            ...sceneState,
            objects
        }, selected);
    }

    interactionTemplatesForObject (object) {
        if (!object || object.programmable === false) return [];
        return INTERACTION_TEMPLATES.filter(template => (
            !template.kinds || template.kinds.includes(object.kind)
        ));
    }

    applyInteractionTemplate (templateId) {
        const selected = this.state.selectedObject;
        if (!selected) return;

        const template = INTERACTION_TEMPLATES.find(item => item.id === templateId);
        if (!template) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const objects = {...sceneState.objects};
        const object = objects[selected];
        if (!object) return;

        if (
            scratch3DWorkspaceHasBlocks(object.blockWorkspaceXml) &&
            typeof window !== 'undefined' &&
            !window.confirm('应用交互模板会替换当前对象已有的积木程序，是否继续？')
        ) {
            return;
        }

        let nextObject = {...object};
        if (template.collisionMode) {
            nextObject = this.objectWithCollisionMode(nextObject, template.collisionMode);
        }
        if (template.stateValue) {
            nextObject.state = {
                ...(nextObject.state || {}),
                value: template.stateValue
            };
        }

        const program = createObjectProgramState({
            ...(nextObject.program || {}),
            source: 'blocks',
            blockWorkspaceXml: template.buildWorkspaceXml(nextObject),
            version: 1
        });

        objects[selected] = {
            ...nextObject,
            program,
            blockWorkspaceXml: program.blockWorkspaceXml,
            blockScriptVersion: program.version,
            scripts: program.scripts
        };

        this.updateSceneState({
            ...sceneState,
            objects
        }, selected);
        this.selectWorkbenchTab(WORKBENCH_TABS.CODE);
    }

    playSelectedAnimation (animationId) {
        const selected = this.state.selectedObject;
        if (!selected) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const objects = {...sceneState.objects};
        const definition = this.animationDefinitionForObject(objects[selected], animationId);
        objects[selected] = {
            ...objects[selected],
            animationState: animationId,
            animation: {
                id: animationId,
                playing: animationId !== 'idle',
                startedAt: Date.now(),
                loop: definition ? Boolean(definition.loop) : false
            }
        };

        this.updateSceneState({
            ...sceneState,
            objects
        }, selected);
    }

    stopSelectedAnimation () {
        const selected = this.state.selectedObject;
        if (!selected) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const objects = {...sceneState.objects};
        objects[selected] = {
            ...objects[selected],
            animationState: 'idle',
            animation: null
        };

        this.updateSceneState({
            ...sceneState,
            objects
        }, selected);
    }

    addObjectScript () {
        const selected = this.state.selectedObject;
        if (!selected) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const objects = {...sceneState.objects};
        const object = objects[selected];
        const script = this.createObjectScript(this.state.scriptDraft);
        const program = createObjectProgramState({
            ...(object.program || {}),
            source: 'hybrid',
            scripts: [...(object.scripts || []), script]
        });
        objects[selected] = {
            ...object,
            program,
            scripts: program.scripts,
            blockWorkspaceXml: program.blockWorkspaceXml,
            blockScriptVersion: program.version
        };

        this.updateSceneState({
            ...sceneState,
            objects
        }, selected);
    }

    deleteObjectScript (scriptId) {
        const selected = this.state.selectedObject;
        if (!selected) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const objects = {...sceneState.objects};
        const object = objects[selected];
        const program = createObjectProgramState({
            ...(object.program || {}),
            scripts: (object.scripts || []).filter(script => script.id !== scriptId)
        });
        objects[selected] = {
            ...object,
            program,
            scripts: program.scripts,
            blockWorkspaceXml: program.blockWorkspaceXml,
            blockScriptVersion: program.version
        };

        this.updateSceneState({
            ...sceneState,
            objects
        }, selected);
    }

    runSelectedObjectScript (script) {
        const selected = this.state.selectedObject;
        if (!selected || !script) return;

        const sceneState = normalizeSceneState(this.state.sceneState);
        const nextState = this.applyObjectScriptRepeats(sceneState, selected, script);
        this.updateSceneState(nextState, selected, false, false);
        this.scheduleObjectScriptRepeats(selected, script);
    }

    createObjectScript (draft) {
        return {
            id: `script-${Date.now()}-${Math.round(Math.random() * 100000)}`,
            name: this.describeScript(draft),
            enabled: true,
            event: draft.event,
            action: draft.action,
            animationId: draft.animationId,
            stateValue: draft.stateValue,
            condition: draft.condition || 'always',
            conditionState: draft.conditionState || 'normal',
            conditionTarget: draft.conditionTarget || '',
            distance: Math.max(1, Number(draft.distance) || 1),
            direction: draft.direction,
            steps: Math.max(1, Number(draft.steps) || 1),
            height: Math.max(1, Number(draft.height) || 1),
            targetX: Math.round(Number(draft.targetX) || 0),
            targetY: Math.round(Number(draft.targetY) || 0),
            targetZ: Math.round(Number(draft.targetZ) || 0),
            repeatTimes: Math.max(1, Math.min(30, Number(draft.repeatTimes) || 1)),
            waitSeconds: Math.max(0, Math.min(10, Number(draft.waitSeconds) || 0)),
            blocks: []
        };
    }

    describeScript (script) {
        const event = SCRIPT_EVENTS.find(item => item.value === script.event);
        const action = SCRIPT_ACTIONS.find(item => item.value === script.action);
        const prefix = this.describeScriptCondition(script);
        const repeat = Number(script.repeatTimes) > 1 ? `，重复 ${script.repeatTimes} 次` : '';
        const wait = Number(script.waitSeconds) > 0 ? `，间隔 ${script.waitSeconds} 秒` : '';
        if (script.action === 'playAnimation') {
            const animation = COMMON_ANIMATIONS.find(item => item.id === script.animationId);
            return `${event?.label || '当触发'}${prefix} -> 播放 ${animation?.label || script.animationId}${repeat}${wait}`;
        }
        if (script.action === 'setState') {
            const state = OBJECT_STATES.find(item => item.value === script.stateValue);
            return `${event?.label || '当触发'}${prefix} -> 状态 ${state?.label || script.stateValue}${repeat}${wait}`;
        }
        if (script.action === 'moveBy') {
            const direction = SCRIPT_DIRECTIONS.find(item => item.value === script.direction);
            return `${event?.label || '当触发'}${prefix} -> ${direction?.label || '移动'} ${script.steps || 1} 格${repeat}${wait}`;
        }
        if (script.action === 'moveTo') {
            return `${event?.label || '当触发'}${prefix} -> 移到 (${script.targetX || 0}, ${script.targetY || 0}, ${script.targetZ || 0})${repeat}${wait}`;
        }
        if (script.action === 'jump') {
            return `${event?.label || '当触发'}${prefix} -> 跳起 ${script.height || 1} 格${repeat}${wait}`;
        }
        return `${event?.label || '当触发'}${prefix} -> ${action?.label || script.action}${repeat}${wait}`;
    }

    describeScriptCondition (script) {
        if (!script || !script.condition || script.condition === 'always') {
            return '';
        }
        if (script.condition === 'state') {
            const state = OBJECT_STATES.find(item => item.value === script.conditionState);
            return `，如果状态是 ${state?.label || script.conditionState}`;
        }
        if (script.condition === 'touching') {
            return `，如果碰到 ${script.conditionTarget || '对象'}`;
        }
        if (script.condition === 'near') {
            return `，如果距离 ${script.conditionTarget || '对象'} 小于 ${script.distance || 1}`;
        }
        return '';
    }

    childrenByLocalName (element, localName) {
        if (!element || !element.children) return [];
        return Array.from(element.children).filter(child => child.localName === localName);
    }

    fieldValueFromBlock (blockElement, fieldName, fallback = '') {
        const field = this.childrenByLocalName(blockElement, 'field')
            .find(item => item.getAttribute('name') === fieldName);
        return field ? field.textContent : fallback;
    }

    numberFieldValueFromBlock (blockElement, fieldName, fallback = 0) {
        const value = Number(this.fieldValueFromBlock(blockElement, fieldName, fallback));
        return Number.isNaN(value) ? fallback : value;
    }

    numberValueFromBlock (blockElement, valueName, fallback = 0) {
        const valueElement = this.childrenByLocalName(blockElement, 'value')
            .find(item => item.getAttribute('name') === valueName);
        if (!valueElement) return fallback;

        const childBlock = this.childrenByLocalName(valueElement, 'block')[0] ||
            this.childrenByLocalName(valueElement, 'shadow')[0];
        const numberField = childBlock && this.childrenByLocalName(childBlock, 'field')[0];
        const value = Number(numberField ? numberField.textContent : fallback);
        return Number.isNaN(value) ? fallback : value;
    }

    nextBlockElement (blockElement) {
        const nextElement = this.childrenByLocalName(blockElement, 'next')[0];
        return nextElement ? this.childrenByLocalName(nextElement, 'block')[0] : null;
    }

    statementInputBlockElement (blockElement, statementName) {
        const statementElement = this.childrenByLocalName(blockElement, 'statement')
            .find(item => item.getAttribute('name') === statementName);
        return statementElement ? this.childrenByLocalName(statementElement, 'block')[0] : null;
    }

    valueBlockElement (blockElement, valueName) {
        const valueElement = this.childrenByLocalName(blockElement, 'value')
            .find(item => item.getAttribute('name') === valueName);
        if (!valueElement) return null;
        return this.childrenByLocalName(valueElement, 'block')[0] ||
            this.childrenByLocalName(valueElement, 'shadow')[0] ||
            null;
    }

    expressionFromValue (blockElement, valueName, fallback = 0) {
        return this.expressionFromBlock(this.valueBlockElement(blockElement, valueName), fallback);
    }

    expressionFromBlock (blockElement, fallback = 0) {
        if (!blockElement) {
            return {type: 'number', value: fallback};
        }

        const blockType = blockElement.getAttribute('type');
        if (blockType === 'math_number') {
            return {
                type: 'number',
                value: this.numberFieldValueFromBlock(blockElement, 'NUM', fallback)
            };
        }
        if (blockType === 'scratch3d_sensing_distance_to') {
            return {
                type: 'distanceTo',
                target: this.fieldValueFromBlock(blockElement, 'OBJECT', '对象')
            };
        }
        if (blockType === 'scratch3d_sensing_position_value') {
            return {
                type: 'positionValue',
                target: this.fieldValueFromBlock(blockElement, 'OBJECT', 'self'),
                axis: this.fieldValueFromBlock(blockElement, 'AXIS', 'x')
            };
        }
        if (blockType === 'scratch3d_sensing_rotation_value') {
            return {
                type: 'rotationValue',
                target: this.fieldValueFromBlock(blockElement, 'OBJECT', 'self'),
                axis: this.fieldValueFromBlock(blockElement, 'AXIS', 'z')
            };
        }
        if (blockType === 'scratch3d_sensing_weather_value') {
            return {type: 'weatherValue'};
        }
        if (blockType === 'scratch3d_sensing_weather_property') {
            return {
                type: 'weatherProperty',
                property: this.fieldValueFromBlock(blockElement, 'PROPERTY', 'type')
            };
        }
        if (blockType === 'scratch3d_sensing_terrain_under') {
            return {type: 'terrainUnderValue'};
        }
        if (blockType === 'scratch3d_sensing_surface_z') {
            return {type: 'surfaceZValue'};
        }
        if (blockType === 'scratch3d_sensing_state_value') {
            return {type: 'stateValue'};
        }
        if (blockType === 'scratch3d_sensing_collision_mode') {
            return {type: 'collisionModeValue'};
        }
        if (blockType === 'scratch3d_looks_size_value') {
            return {type: 'sizeValue'};
        }
        if (blockType === 'scratch3d_variable_value') {
            return {
                type: 'variableValue',
                variableId: this.fieldValueFromBlock(blockElement, 'VARIABLE', 'score')
            };
        }
        if (blockType === 'scratch3d_operator_math') {
            return {
                type: 'math',
                operator: this.fieldValueFromBlock(blockElement, 'OPERATOR', 'add'),
                left: this.expressionFromValue(blockElement, 'LEFT', 0),
                right: this.expressionFromValue(blockElement, 'RIGHT', 0)
            };
        }
        if (
            blockType === 'scratch3d_operator_compare' ||
            blockType === 'scratch3d_operator_and' ||
            blockType === 'scratch3d_operator_or' ||
            blockType === 'scratch3d_operator_not' ||
            blockType === 'scratch3d_sensing_touching_object' ||
            blockType === 'scratch3d_sensing_front_blocked' ||
            blockType === 'scratch3d_sensing_distance_less_than' ||
            blockType === 'scratch3d_sensing_visible_is' ||
            blockType === 'scratch3d_sensing_object_visible_is' ||
            blockType === 'scratch3d_sensing_state_is' ||
            blockType === 'scratch3d_sensing_object_state_is' ||
            blockType === 'scratch3d_sensing_collision_is' ||
            blockType === 'scratch3d_sensing_object_collision_is' ||
            blockType === 'scratch3d_sensing_terrain_under_is' ||
            blockType === 'scratch3d_sensing_front_terrain_is' ||
            blockType === 'scratch3d_sensing_weather_is'
        ) {
            return {
                type: 'condition',
                condition: this.conditionFromBlock(blockElement)
            };
        }

        return {
            type: 'number',
            value: fallback
        };
    }

    conditionFromBlock (blockElement) {
        if (!blockElement) {
            return {type: 'empty'};
        }

        const blockType = blockElement.getAttribute('type');
        if (blockType === 'scratch3d_operator_compare') {
            return {
                type: 'compare',
                operator: this.fieldValueFromBlock(blockElement, 'OPERATOR', 'eq'),
                left: this.expressionFromValue(blockElement, 'LEFT', 0),
                right: this.expressionFromValue(blockElement, 'RIGHT', 0)
            };
        }
        if (blockType === 'scratch3d_operator_and') {
            return {
                type: 'and',
                left: this.conditionFromBlock(this.valueBlockElement(blockElement, 'LEFT')),
                right: this.conditionFromBlock(this.valueBlockElement(blockElement, 'RIGHT'))
            };
        }
        if (blockType === 'scratch3d_operator_or') {
            return {
                type: 'or',
                left: this.conditionFromBlock(this.valueBlockElement(blockElement, 'LEFT')),
                right: this.conditionFromBlock(this.valueBlockElement(blockElement, 'RIGHT'))
            };
        }
        if (blockType === 'scratch3d_operator_not') {
            return {
                type: 'not',
                condition: this.conditionFromBlock(this.valueBlockElement(blockElement, 'CONDITION'))
            };
        }
        if (blockType === 'scratch3d_sensing_touching_object') {
            return {
                type: 'touching',
                target: this.fieldValueFromBlock(blockElement, 'OBJECT', '对象')
            };
        }
        if (blockType === 'scratch3d_sensing_front_blocked') {
            return {
                type: 'frontBlocked',
                direction: this.fieldValueFromBlock(blockElement, 'DIRECTION', '')
            };
        }
        if (blockType === 'scratch3d_sensing_distance_less_than') {
            return {
                type: 'distanceLessThan',
                target: this.fieldValueFromBlock(blockElement, 'OBJECT', '对象'),
                distance: this.expressionFromValue(blockElement, 'DISTANCE', 1)
            };
        }
        if (blockType === 'scratch3d_sensing_visible_is') {
            return {
                type: 'visible'
            };
        }
        if (blockType === 'scratch3d_sensing_object_visible_is') {
            return {
                type: 'objectVisible',
                target: this.fieldValueFromBlock(blockElement, 'OBJECT', '*')
            };
        }
        if (blockType === 'scratch3d_sensing_state_is') {
            return {
                type: 'state',
                stateValue: this.fieldValueFromBlock(blockElement, 'STATE', 'normal')
            };
        }
        if (blockType === 'scratch3d_sensing_object_state_is') {
            return {
                type: 'objectState',
                target: this.fieldValueFromBlock(blockElement, 'OBJECT', '*'),
                stateValue: this.fieldValueFromBlock(blockElement, 'STATE', 'normal')
            };
        }
        if (blockType === 'scratch3d_sensing_collision_is') {
            return {
                type: 'collisionMode',
                collisionMode: this.fieldValueFromBlock(blockElement, 'COLLISION', COLLISION_MODES.SOLID)
            };
        }
        if (blockType === 'scratch3d_sensing_object_collision_is') {
            return {
                type: 'objectCollisionMode',
                target: this.fieldValueFromBlock(blockElement, 'OBJECT', '*'),
                collisionMode: this.fieldValueFromBlock(blockElement, 'COLLISION', COLLISION_MODES.SOLID)
            };
        }
        if (blockType === 'scratch3d_sensing_terrain_under_is') {
            return {
                type: 'terrainUnder',
                terrainId: this.fieldValueFromBlock(blockElement, 'TERRAIN', 'grass-block')
            };
        }
        if (blockType === 'scratch3d_sensing_front_terrain_is') {
            return {
                type: 'frontTerrain',
                direction: this.fieldValueFromBlock(blockElement, 'DIRECTION', ''),
                terrainId: this.fieldValueFromBlock(blockElement, 'TERRAIN', 'grass-block')
            };
        }
        if (blockType === 'scratch3d_sensing_weather_is') {
            return {
                type: 'weather',
                weatherType: this.fieldValueFromBlock(blockElement, 'WEATHER', 'sunny')
            };
        }
        if (
            blockType === 'math_number' ||
            blockType === 'scratch3d_operator_math' ||
            blockType === 'scratch3d_sensing_distance_to' ||
            blockType === 'scratch3d_sensing_position_value' ||
            blockType === 'scratch3d_sensing_rotation_value' ||
            blockType === 'scratch3d_sensing_weather_value' ||
            blockType === 'scratch3d_sensing_weather_property' ||
            blockType === 'scratch3d_sensing_terrain_under' ||
            blockType === 'scratch3d_sensing_surface_z' ||
            blockType === 'scratch3d_sensing_state_value' ||
            blockType === 'scratch3d_sensing_collision_mode' ||
            blockType === 'scratch3d_looks_size_value' ||
            blockType === 'scratch3d_variable_value'
        ) {
            return {
                type: 'truthy',
                expression: this.expressionFromBlock(blockElement, 0)
            };
        }

        return {type: 'unsupported', blockType};
    }

    blockElementToInstruction (blockElement) {
        if (!blockElement) return null;
        const type = blockElement.getAttribute('type');
        const baseInstruction = {
            id: `block-instruction-${blockElement.getAttribute('id') || Date.now()}`,
            blocks: [{type}]
        };

        if (type === 'scratch3d_motion_face_direction') {
            return {
                ...baseInstruction,
                action: 'faceDirection',
                direction: this.fieldValueFromBlock(blockElement, 'DIRECTION', 'rightUp')
            };
        }

        if (type === 'scratch3d_motion_face_object') {
            return {
                ...baseInstruction,
                action: 'faceObject',
                targetName: this.fieldValueFromBlock(blockElement, 'OBJECT', '*')
            };
        }

        if (type === 'scratch3d_motion_move_forward') {
            return {
                ...baseInstruction,
                action: 'moveForward',
                steps: this.expressionFromValue(blockElement, 'STEPS', 1)
            };
        }

        if (type === 'scratch3d_motion_move_grid') {
            return {
                ...baseInstruction,
                action: 'moveBy',
                direction: this.fieldValueFromBlock(blockElement, 'DIRECTION', 'rightUp'),
                steps: this.expressionFromValue(blockElement, 'STEPS', 1)
            };
        }

        if (type === 'scratch3d_motion_move_to_object') {
            return {
                ...baseInstruction,
                action: 'moveToObject',
                targetName: this.fieldValueFromBlock(blockElement, 'OBJECT', '*')
            };
        }

        if (type === 'scratch3d_motion_move_toward_object') {
            return {
                ...baseInstruction,
                action: 'moveTowardObject',
                targetName: this.fieldValueFromBlock(blockElement, 'OBJECT', '*'),
                steps: this.expressionFromValue(blockElement, 'STEPS', 1)
            };
        }

        if (type === 'scratch3d_motion_move_away_object') {
            return {
                ...baseInstruction,
                action: 'moveAwayFromObject',
                targetName: this.fieldValueFromBlock(blockElement, 'OBJECT', '*'),
                steps: this.expressionFromValue(blockElement, 'STEPS', 1)
            };
        }

        if (type === 'scratch3d_motion_move_to') {
            return {
                ...baseInstruction,
                action: 'moveTo',
                targetX: this.expressionFromValue(blockElement, 'X', 0),
                targetY: this.expressionFromValue(blockElement, 'Y', 0),
                targetZ: this.expressionFromValue(blockElement, 'Z', 0)
            };
        }

        if (type === 'scratch3d_motion_change_position') {
            return {
                ...baseInstruction,
                action: 'changePositionAxis',
                axis: this.fieldValueFromBlock(blockElement, 'AXIS', 'x'),
                value: this.expressionFromValue(blockElement, 'VALUE', 1)
            };
        }

        if (type === 'scratch3d_motion_set_position_axis') {
            return {
                ...baseInstruction,
                action: 'setPositionAxis',
                axis: this.fieldValueFromBlock(blockElement, 'AXIS', 'x'),
                value: this.expressionFromValue(blockElement, 'VALUE', 0)
            };
        }

        if (type === 'scratch3d_motion_change_z') {
            return {
                ...baseInstruction,
                action: 'changeZBy',
                z: this.expressionFromValue(blockElement, 'Z', 1)
            };
        }

        if (type === 'scratch3d_motion_turn_axis') {
            return {
                ...baseInstruction,
                action: 'changeRotationAxis',
                axis: this.fieldValueFromBlock(blockElement, 'AXIS', 'z'),
                degrees: this.expressionFromValue(blockElement, 'DEGREES', 15)
            };
        }

        if (type === 'scratch3d_motion_set_rotation_axis') {
            return {
                ...baseInstruction,
                action: 'setRotationAxis',
                axis: this.fieldValueFromBlock(blockElement, 'AXIS', 'z'),
                degrees: this.expressionFromValue(blockElement, 'DEGREES', 0)
            };
        }

        if (type === 'scratch3d_motion_jump') {
            return {
                ...baseInstruction,
                action: 'jump',
                height: this.expressionFromValue(blockElement, 'HEIGHT', 1)
            };
        }

        if (type === 'scratch3d_motion_snap_to_surface') {
            return {
                ...baseInstruction,
                action: 'snapToSurface'
            };
        }

        if (type === 'scratch3d_control_wait') {
            return {
                ...baseInstruction,
                action: 'wait',
                seconds: this.expressionFromValue(blockElement, 'SECONDS', 1)
            };
        }

        if (type === 'scratch3d_control_wait_until') {
            return {
                ...baseInstruction,
                action: 'waitUntil',
                condition: this.conditionFromBlock(this.valueBlockElement(blockElement, 'CONDITION'))
            };
        }

        if (type === 'scratch3d_control_repeat') {
            return {
                ...baseInstruction,
                action: 'repeat',
                times: this.expressionFromValue(blockElement, 'TIMES', 10),
                substack: this.instructionsFromBlock(this.statementInputBlockElement(blockElement, 'SUBSTACK'))
            };
        }

        if (type === 'scratch3d_control_repeat_until') {
            return {
                ...baseInstruction,
                action: 'repeatUntil',
                condition: this.conditionFromBlock(this.valueBlockElement(blockElement, 'CONDITION')),
                substack: this.instructionsFromBlock(this.statementInputBlockElement(blockElement, 'SUBSTACK'))
            };
        }

        if (type === 'scratch3d_control_if') {
            return {
                ...baseInstruction,
                action: 'if',
                condition: this.conditionFromBlock(this.valueBlockElement(blockElement, 'CONDITION')),
                substack: this.instructionsFromBlock(this.statementInputBlockElement(blockElement, 'SUBSTACK'))
            };
        }

        if (type === 'scratch3d_control_if_else') {
            return {
                ...baseInstruction,
                action: 'ifElse',
                condition: this.conditionFromBlock(this.valueBlockElement(blockElement, 'CONDITION')),
                substack: this.instructionsFromBlock(this.statementInputBlockElement(blockElement, 'SUBSTACK')),
                substack2: this.instructionsFromBlock(this.statementInputBlockElement(blockElement, 'SUBSTACK2'))
            };
        }

        if (type === 'scratch3d_control_forever') {
            return {
                ...baseInstruction,
                action: 'forever',
                substack: this.instructionsFromBlock(this.statementInputBlockElement(blockElement, 'SUBSTACK'))
            };
        }

        if (type === 'scratch3d_control_stop_script') {
            return {
                ...baseInstruction,
                action: 'stopScript'
            };
        }

        if (type === 'scratch3d_event_broadcast_message') {
            return {
                ...baseInstruction,
                action: 'broadcastMessage',
                messageType: this.fieldValueFromBlock(blockElement, 'MESSAGE', 'startQuest')
            };
        }

        if (type === 'scratch3d_event_send_message_to_object') {
            return {
                ...baseInstruction,
                action: 'sendMessageToObject',
                messageType: this.fieldValueFromBlock(blockElement, 'MESSAGE', 'startQuest'),
                targetName: this.fieldValueFromBlock(blockElement, 'OBJECT', '*')
            };
        }

        if (type === 'scratch3d_looks_play_animation') {
            return {
                ...baseInstruction,
                action: 'playAnimation',
                animationId: this.fieldValueFromBlock(blockElement, 'ANIMATION', 'idle')
            };
        }

        if (type === 'scratch3d_looks_wait_animation_done') {
            return {
                ...baseInstruction,
                action: 'waitAnimationDone'
            };
        }

        if (type === 'scratch3d_looks_stop_animation') {
            return {
                ...baseInstruction,
                action: 'stopAnimation'
            };
        }

        if (type === 'scratch3d_looks_set_state') {
            return {
                ...baseInstruction,
                action: 'setState',
                stateValue: this.fieldValueFromBlock(blockElement, 'STATE', 'normal')
            };
        }

        if (type === 'scratch3d_looks_set_collision') {
            return {
                ...baseInstruction,
                action: 'setCollisionMode',
                collisionMode: this.fieldValueFromBlock(blockElement, 'COLLISION', COLLISION_MODES.SOLID)
            };
        }

        if (type === 'scratch3d_looks_set_size') {
            return {
                ...baseInstruction,
                action: 'setSize',
                sizePercent: this.expressionFromValue(blockElement, 'SIZE', 100)
            };
        }

        if (type === 'scratch3d_looks_change_size') {
            return {
                ...baseInstruction,
                action: 'changeSize',
                sizeDelta: this.expressionFromValue(blockElement, 'CHANGE', 10)
            };
        }

        if (type === 'scratch3d_looks_show') {
            return {
                ...baseInstruction,
                action: 'show'
            };
        }

        if (type === 'scratch3d_looks_hide') {
            return {
                ...baseInstruction,
                action: 'hide'
            };
        }

        if (type === 'scratch3d_variable_set') {
            return {
                ...baseInstruction,
                action: 'setVariable',
                variableId: this.fieldValueFromBlock(blockElement, 'VARIABLE', 'score'),
                value: this.expressionFromValue(blockElement, 'VALUE', 0)
            };
        }

        if (type === 'scratch3d_variable_change') {
            return {
                ...baseInstruction,
                action: 'changeVariable',
                variableId: this.fieldValueFromBlock(blockElement, 'VARIABLE', 'score'),
                value: this.expressionFromValue(blockElement, 'VALUE', 1)
            };
        }

        if (type === 'scratch3d_scene_set_weather') {
            return {
                ...baseInstruction,
                action: 'setWeather',
                weatherType: this.fieldValueFromBlock(blockElement, 'WEATHER', 'sunny')
            };
        }

        return null;
    }

    instructionsFromBlock (firstBlockElement) {
        const instructions = [];
        let statementBlock = firstBlockElement;
        while (statementBlock) {
            const instruction = this.blockElementToInstruction(statementBlock);
            if (instruction) instructions.push(instruction);
            statementBlock = this.nextBlockElement(statementBlock);
        }
        return instructions;
    }

    scriptsFromBlockWorkspace (blockWorkspaceXml, eventName) {
        if (
            !blockWorkspaceXml ||
            typeof window === 'undefined' ||
            typeof window.DOMParser !== 'function'
        ) {
            return [];
        }

        let documentElement = null;
        try {
            const parser = new window.DOMParser();
            const xmlDocument = parser.parseFromString(blockWorkspaceXml, 'text/xml');
            if (xmlDocument.getElementsByTagName('parsererror').length) {
                return [];
            }
            documentElement = xmlDocument.documentElement;
        } catch (error) {
            return [];
        }

        const eventBlockTypes = {
            click: 'scratch3d_event_when_click_self',
            message: 'scratch3d_event_when_receive_message',
            start: 'scratch3d_event_when_start',
            touching: 'scratch3d_event_when_touching_object'
        };
        const eventBlockType = eventBlockTypes[eventName] || eventBlockTypes.start;

        return this.childrenByLocalName(documentElement, 'block')
            .filter(blockElement => blockElement.getAttribute('type') === eventBlockType)
            .map(eventBlock => ({
                id: `block-script-${eventBlock.getAttribute('id') || Date.now()}`,
                source: 'blocks',
                enabled: true,
                event: eventName,
                eventTarget: this.fieldValueFromBlock(eventBlock, 'OBJECT', '*'),
                messageType: this.fieldValueFromBlock(eventBlock, 'MESSAGE', 'startQuest'),
                instructions: this.instructionsFromBlock(this.nextBlockElement(eventBlock))
            }));
    }

    handleProjectRunStart () {
        if (this.is3DRunActive) return;

        this.clearScheduledScripts();
        this.is3DRunActive = true;
        this.activeTouchEventKeys = new Set();
        this.runPreviewBaseSceneState = normalizeSceneState(this.state.sceneState);
        this.runObjectScripts('start');
        this.updateAnimationLoop();
    }

    handleProjectRunStop () {
        this.clearScheduledScripts();
        this.activeTouchEventKeys = new Set();
        const sceneState = normalizeSceneState(this.state.sceneState);

        if (this.is3DRunActive && this.runPreviewBaseSceneState) {
            const restoredSceneState = this.restoreRuntimeFieldsFromSnapshot(
                sceneState,
                this.runPreviewBaseSceneState
            );
            this.is3DRunActive = false;
            this.runPreviewBaseSceneState = null;
            this.updateSceneState(restoredSceneState, this.state.selectedObject, false, false);
            return;
        }

        this.is3DRunActive = false;
        this.runPreviewBaseSceneState = null;
        const objects = Object.keys(sceneState.objects || {}).reduce((nextObjects, name) => {
            nextObjects[name] = {
                ...sceneState.objects[name],
                animationState: 'idle',
                animation: null
            };
            return nextObjects;
        }, {});
        this.updateSceneState({
            ...sceneState,
            objects
        }, this.state.selectedObject, false, false);
    }

    restoreRuntimeFieldsFromSnapshot (sceneState, snapshotSceneState) {
        const snapshot = normalizeSceneState(snapshotSceneState);
        const objects = Object.keys(sceneState.objects || {}).reduce((nextObjects, name) => {
            const object = sceneState.objects[name];
            const snapshotObject = snapshot.objects && snapshot.objects[name];
            if (!snapshotObject) {
                nextObjects[name] = {
                    ...object,
                    animationState: 'idle',
                    animation: null
                };
                return nextObjects;
            }

            nextObjects[name] = {
                ...object,
                position: clone(snapshotObject.position || object.position || {x: 0, y: 0, z: 0}),
                rotation: clone(snapshotObject.rotation || object.rotation || {x: 0, y: 0, z: 0}),
                scale: clone(snapshotObject.scale || object.scale || {x: 1, y: 1, z: 1}),
                facingDirection: snapshotObject.facingDirection || object.facingDirection,
                collision: snapshotObject.collision,
                collisionMode: snapshotObject.collisionMode,
                behavior: clone(snapshotObject.behavior || object.behavior || {}),
                variables: clone(snapshotObject.variables || object.variables || DEFAULT_OBJECT_VARIABLES),
                visible: snapshotObject.visible !== false,
                state: clone(snapshotObject.state || {value: 'normal'}),
                animationState: snapshotObject.animationState || 'idle',
                animation: clone(snapshotObject.animation)
            };
            return nextObjects;
        }, {});

        return {
            ...sceneState,
            weather: clone(snapshot.weather || sceneState.weather || DEFAULT_STATE.weather),
            objects
        };
    }

    currentRuntimeSceneState () {
        return normalizeSceneState(this.props.vm.runtime.scratch3DState || this.state.sceneState);
    }

    isScriptRunActive (runToken) {
        return this.is3DRunActive && this.scriptRunToken === runToken;
    }

    reportRuntimeWarning (message) {
        const now = Date.now();
        if (now - this.lastRuntimeWarningAt < RUNTIME_WARNING_COOLDOWN_MS) return;
        this.lastRuntimeWarningAt = now;
        this.setOperationStatus(`运行提示：${message}`, 'warning');
    }

    consumeRuntimeStep (runToken, objectName) {
        const currentSteps = this.scriptStepCountByToken.get(runToken) || 0;
        const nextSteps = currentSteps + 1;
        this.scriptStepCountByToken.set(runToken, nextSteps);
        if (nextSteps <= MAX_SCRIPT_STEPS_PER_RUN) return true;

        const object = (this.currentRuntimeSceneState().objects || {})[objectName];
        this.reportRuntimeWarning(`对象“${(object && object.name) || objectName}”脚本执行过多，已停止以避免卡住。`);
        return false;
    }

    waitForSeconds (seconds, runToken) {
        if (!this.isScriptRunActive(runToken)) return Promise.resolve();
        const duration = Math.max(0, Math.min(30, Number(seconds) || 0));
        if (duration <= 0) return Promise.resolve();

        return new Promise(resolve => {
            let timer = null;
            const timerId = window.setTimeout(() => {
                this.scriptTimers = this.scriptTimers.filter(item => item !== timer);
                resolve();
            }, duration * 1000);
            timer = {id: timerId, resolve};
            this.scriptTimers.push(timer);
        });
    }

    waitForAnimationDone (objectName, runToken) {
        if (!this.isScriptRunActive(runToken)) return Promise.resolve();

        const sceneState = this.currentRuntimeSceneState();
        const object = sceneState.objects && sceneState.objects[objectName];
        const animation = object && object.animation;
        if (!animation || !animation.playing) {
            return Promise.resolve();
        }

        const definition = this.animationDefinitionForObject(object, animation.id);
        if (!definition || definition.loop || animation.loop) {
            return Promise.resolve();
        }

        const duration = Math.max(1, Number(definition.duration || 0));
        const elapsed = Math.max(0, Date.now() - Number(animation.startedAt || Date.now()));
        return this.waitForSeconds(Math.max(0, (duration - elapsed) / 1000), runToken);
    }

    async executeBlockScript (objectName, script, runToken) {
        if (!script || !Array.isArray(script.instructions)) return;
        await this.executeInstructions(objectName, script.instructions, runToken);
    }

    async executeInstructions (objectName, instructions, runToken) {
        for (const instruction of instructions || []) {
            if (!this.isScriptRunActive(runToken)) return true;
            const shouldStop = await this.executeInstruction(objectName, instruction, runToken);
            if (shouldStop) return true;
        }
        return false;
    }

    async executeInstruction (objectName, instruction, runToken) {
        if (!instruction || !this.isScriptRunActive(runToken)) return true;
        if (!this.consumeRuntimeStep(runToken, objectName)) return true;

        if (instruction.action === 'wait') {
            const sceneState = this.currentRuntimeSceneState();
            const seconds = this.evaluateNumberExpression(sceneState, objectName, instruction.seconds, 1, 0, 30);
            await this.waitForSeconds(seconds, runToken);
            return !this.isScriptRunActive(runToken);
        }

        if (instruction.action === 'waitUntil') {
            while (this.isScriptRunActive(runToken)) {
                const sceneState = this.currentRuntimeSceneState();
                if (this.runtimeConditionPasses(sceneState, objectName, instruction.condition)) {
                    return false;
                }
                if (!this.consumeRuntimeStep(runToken, objectName)) return true;
                await this.waitForSeconds(0.05, runToken);
            }
            return true;
        }

        if (instruction.action === 'waitAnimationDone') {
            await this.waitForAnimationDone(objectName, runToken);
            return !this.isScriptRunActive(runToken);
        }

        if (instruction.action === 'repeat') {
            const sceneState = this.currentRuntimeSceneState();
            const times = Math.round(this.evaluateNumberExpression(sceneState, objectName, instruction.times, 1, 0, 100));
            for (let index = 0; index < times; index++) {
                if (!this.isScriptRunActive(runToken)) return true;
                const shouldStop = await this.executeInstructions(objectName, instruction.substack, runToken);
                if (shouldStop) return true;
            }
            return false;
        }

        if (instruction.action === 'repeatUntil') {
            while (this.isScriptRunActive(runToken)) {
                const sceneState = this.currentRuntimeSceneState();
                if (this.runtimeConditionPasses(sceneState, objectName, instruction.condition)) {
                    return false;
                }
                const shouldStop = await this.executeInstructions(objectName, instruction.substack, runToken);
                if (shouldStop) return true;
                if (!this.consumeRuntimeStep(runToken, objectName)) return true;
                await this.waitForSeconds(0.05, runToken);
            }
            return true;
        }

        if (instruction.action === 'forever') {
            while (this.isScriptRunActive(runToken)) {
                const shouldStop = await this.executeInstructions(objectName, instruction.substack, runToken);
                if (shouldStop) return true;
                await this.waitForSeconds(0.05, runToken);
            }
            return true;
        }

        if (instruction.action === 'if' || instruction.action === 'ifElse') {
            const sceneState = this.currentRuntimeSceneState();
            const conditionPasses = this.runtimeConditionPasses(sceneState, objectName, instruction.condition);
            const branch = conditionPasses ? instruction.substack : instruction.substack2;
            return this.executeInstructions(objectName, branch, runToken);
        }

        if (instruction.action === 'stopScript') {
            return true;
        }

        if (instruction.action === 'broadcastMessage') {
            this.runObjectScripts('message', null, {
                messageType: instruction.messageType || 'startQuest'
            });
            return false;
        }

        if (instruction.action === 'sendMessageToObject') {
            const sceneState = this.currentRuntimeSceneState();
            this.resolveMessageTargetNames(sceneState, objectName, instruction.targetName).forEach(targetName => {
                this.runObjectScripts('message', targetName, {
                    messageType: instruction.messageType || 'startQuest'
                });
            });
            return false;
        }

        const sceneState = this.currentRuntimeSceneState();
        const nextState = this.applyObjectScript(sceneState, objectName, instruction);
        if (nextState !== sceneState) {
            this.updateSceneState(nextState, this.state.selectedObject, false, false);
        }
        return false;
    }

    runObjectScripts (eventName, objectName = null, eventOptions = {}) {
        const runToken = this.scriptRunToken;
        const sceneState = this.currentRuntimeSceneState();
        let nextSceneState = sceneState;
        const scheduledScripts = [];
        Object.keys(sceneState.objects || {}).forEach(name => {
            if (objectName && name !== objectName) return;
            const object = nextSceneState.objects[name];
            const blockScripts = this.scriptsFromBlockWorkspace(object.blockWorkspaceXml, eventName);
            blockScripts
                .filter(script => this.scriptMatchesEvent(script, eventName, name, eventOptions))
                .forEach(script => {
                    this.executeBlockScript(name, script, runToken).catch(error => {
                        // Keep the scene editor responsive if an experimental 3D script fails.
                        // eslint-disable-next-line no-console
                        console.warn('Failed to run 3D block script', error);
                        this.reportRuntimeWarning(`对象“${object.name || name}”脚本运行出错，已跳过。`);
                    });
                });

            (object.scripts || [])
                .filter(script => script.enabled !== false && script.event === eventName)
                .forEach(script => {
                    nextSceneState = this.applyObjectScriptRepeats(nextSceneState, name, script);
                    scheduledScripts.push({name, script});
                });
        });
        if (nextSceneState !== sceneState) {
            this.updateSceneState(nextSceneState, objectName || this.state.selectedObject, false, false);
        }
        scheduledScripts.forEach(({name, script}) => this.scheduleObjectScriptRepeats(name, script));
    }

    scriptMatchesEvent (script, eventName, objectName, eventOptions = {}) {
        if (!script || script.enabled === false || script.event !== eventName) return false;
        if (eventName === 'message') {
            return script.messageType === eventOptions.messageType;
        }
        if (eventName !== 'touching') return true;
        return this.eventTargetMatches(script.eventTarget || '*', eventOptions.targetName, objectName);
    }

    eventTargetMatches (targetName, actualTargetName, objectName) {
        const normalizedTarget = String(targetName || '*').trim();
        if (
            !normalizedTarget ||
            normalizedTarget === '*' ||
            normalizedTarget === '对象' ||
            normalizedTarget === '任意对象' ||
            normalizedTarget === '任意实体'
        ) {
            return Boolean(actualTargetName);
        }
        if (normalizedTarget === '自己' || normalizedTarget === 'self') {
            return actualTargetName === objectName;
        }
        return normalizedTarget === actualTargetName;
    }

    processTouchingEventScripts () {
        const sceneState = this.currentRuntimeSceneState();
        const objects = sceneState.objects || {};
        const nextTouchEventKeys = new Set();

        Object.keys(objects).forEach(name => {
            const object = objects[name];
            if (!this.objectParticipatesInSensing(object)) return;

            this.resolveSensingTargets(sceneState, name, '*')
                .filter(target => this.objectsTouching(object, target))
                .forEach(target => {
                    const key = `${name}:${target.name || ''}`;
                    nextTouchEventKeys.add(key);
                    if (!this.activeTouchEventKeys.has(key)) {
                        this.runObjectScripts('touching', name, {
                            targetName: target.name
                        });
                    }
                });
        });

        this.activeTouchEventKeys = nextTouchEventKeys;
    }

    applyObjectScriptRepeats (sceneState, objectName, script) {
        const waitSeconds = Math.max(0, Number(script.waitSeconds) || 0);
        const repeatValue = Number(script.repeatTimes);
        const repeatTimes = Math.max(0, Math.min(30, Number.isFinite(repeatValue) ? repeatValue : 1));
        if (repeatTimes <= 0) {
            return sceneState;
        }
        if (waitSeconds > 0) {
            return this.applyObjectScript(sceneState, objectName, script);
        }
        let nextSceneState = sceneState;
        for (let index = 0; index < repeatTimes; index++) {
            nextSceneState = this.applyObjectScript(nextSceneState, objectName, script);
        }
        return nextSceneState;
    }

    scheduleObjectScriptRepeats (objectName, script) {
        const waitSeconds = Math.max(0, Number(script.waitSeconds) || 0);
        const repeatValue = Number(script.repeatTimes);
        const repeatTimes = Math.max(0, Math.min(30, Number.isFinite(repeatValue) ? repeatValue : 1));
        if (waitSeconds <= 0 || repeatTimes <= 1) return;

        for (let index = 1; index < repeatTimes; index++) {
            const timerId = window.setTimeout(() => {
                const sceneState = normalizeSceneState(this.state.sceneState);
                const nextState = this.applyObjectScript(sceneState, objectName, script);
                if (nextState !== sceneState) {
                    this.updateSceneState(nextState, this.state.selectedObject, false, false);
                }
            }, waitSeconds * 1000 * index);
            this.scriptTimers.push({id: timerId});
        }
    }

    resolveSensingTargets (sceneState, objectName, targetName) {
        const objects = (sceneState && sceneState.objects) || {};
        const normalizedTarget = String(targetName || '').trim();
        if (normalizedTarget === '自己' || normalizedTarget === 'self') {
            return objects[objectName] ? [objects[objectName]] : [];
        }

        const anyTarget = !normalizedTarget ||
            normalizedTarget === '*' ||
            normalizedTarget === '对象' ||
            normalizedTarget === '任意对象' ||
            normalizedTarget === '任意实体';
        if (anyTarget) {
            return Object.keys(objects)
                .filter(name => name !== objectName)
                .map(name => objects[name])
                .filter(object => this.objectParticipatesInSensing(object));
        }

        const target = objects[normalizedTarget];
        return target && this.objectParticipatesInSensing(target) ? [target] : [];
    }

    objectsTouching (object, target) {
        if (!this.objectParticipatesInSensing(object) || !this.objectParticipatesInSensing(target)) {
            return false;
        }
        return this.objectsShareGridCell(object, target) ||
            this.objectsTouchOnBlockingFaces(object, target);
    }

    distanceBetweenObjects (object, target) {
        const objectPosition = object.position || {};
        const targetPosition = target.position || {};
        const dx = Math.round((objectPosition.x || 0) - (targetPosition.x || 0));
        const dy = Math.round((objectPosition.y || 0) - (targetPosition.y || 0));
        const dz = Math.round((objectPosition.z || 0) - (targetPosition.z || 0));
        return Math.sqrt((dx * dx) + (dy * dy) + (dz * dz));
    }

    terrainUnderObject (sceneState, object) {
        const objects = (sceneState && sceneState.objects) || {};
        const objectGrid = this.objectToGrid(object);
        const objectZ = Math.round(Number(objectGrid.z) || 0);
        return Object.keys(objects)
            .map(name => objects[name])
            .filter(candidate => (
                this.isTerrainObject(candidate) &&
                candidate.visible !== false &&
                this.objectAtGrid(candidate, objectGrid)
            ))
            .sort((a, b) => {
                const aTop = Math.round(Number(this.objectToGrid(a).z) || 0) + TERRAIN_BLOCK_HEIGHT;
                const bTop = Math.round(Number(this.objectToGrid(b).z) || 0) + TERRAIN_BLOCK_HEIGHT;
                return bTop - aTop;
            })
            .find(candidate => {
                const terrainTop = Math.round(Number(this.objectToGrid(candidate).z) || 0) + TERRAIN_BLOCK_HEIGHT;
                return terrainTop <= objectZ;
            }) || null;
    }

    evaluateExpression (sceneState, objectName, expression, fallback = 0) {
        if (!expression || typeof expression !== 'object') {
            return expression === undefined || expression === null ? fallback : expression;
        }

        const objects = (sceneState && sceneState.objects) || {};
        const object = objects[objectName];
        if (expression.type === 'number') {
            return expression.value;
        }
        if (expression.type === 'condition') {
            return this.runtimeConditionPasses(sceneState, objectName, expression.condition);
        }
        if (expression.type === 'distanceTo') {
            if (!object || !this.objectParticipatesInSensing(object)) return 0;
            const distances = this.resolveSensingTargets(sceneState, objectName, expression.target)
                .map(target => this.distanceBetweenObjects(object, target));
            return distances.length ? Math.min(...distances) : 0;
        }
        if (expression.type === 'positionValue') {
            const target = this.resolveObjectTarget(sceneState, objectName, expression.target || 'self');
            if (!target) return 0;
            const position = target.position || {};
            return Number(position[this.normalizeAxis(expression.axis)] || 0);
        }
        if (expression.type === 'rotationValue') {
            const target = this.resolveObjectTarget(sceneState, objectName, expression.target || 'self');
            if (!target) return 0;
            const rotation = target.rotation || {};
            return Number(rotation[this.normalizeAxis(expression.axis, 'z')] || 0);
        }
        if (expression.type === 'weatherValue') {
            return (((sceneState && sceneState.weather) && sceneState.weather.type) || 'sunny');
        }
        if (expression.type === 'weatherProperty') {
            const property = WEATHER_PROPERTY_KEYS.includes(expression.property) ? expression.property : 'type';
            const weather = (sceneState && sceneState.weather) || DEFAULT_STATE.weather;
            return weather[property];
        }
        if (expression.type === 'terrainUnderValue') {
            if (!object) return '';
            const terrain = this.terrainUnderObject(sceneState, object);
            return terrain ? terrain.assetId : '';
        }
        if (expression.type === 'surfaceZValue') {
            if (!object) return 0;
            const objectGrid = this.objectToGrid(object);
            return this.placementSurfaceZAtGrid(sceneState, objectGrid, objectName);
        }
        if (expression.type === 'stateValue') {
            return object ? ((object.state && object.state.value) || 'normal') : '';
        }
        if (expression.type === 'collisionModeValue') {
            return object ? normalizeCollisionMode(object) : '';
        }
        if (expression.type === 'sizeValue') {
            return object ? this.objectScalePercent(object) : 100;
        }
        if (expression.type === 'variableValue') {
            return object ? Number((object.variables || {})[expression.variableId] || 0) : 0;
        }
        if (expression.type === 'math') {
            const left = this.evaluateNumberExpression(sceneState, objectName, expression.left, 0);
            const right = this.evaluateNumberExpression(sceneState, objectName, expression.right, 0);
            if (expression.operator === 'subtract') return left - right;
            if (expression.operator === 'multiply') return left * right;
            if (expression.operator === 'divide') return right === 0 ? 0 : left / right;
            return left + right;
        }

        return fallback;
    }

    evaluateNumberExpression (sceneState, objectName, expression, fallback = 0, min = null, max = null) {
        const value = Number(this.evaluateExpression(sceneState, objectName, expression, fallback));
        let numberValue = Number.isNaN(value) ? fallback : value;
        if (typeof min === 'number') {
            numberValue = Math.max(min, numberValue);
        }
        if (typeof max === 'number') {
            numberValue = Math.min(max, numberValue);
        }
        return numberValue;
    }

    compareExpressionValues (left, operator, right) {
        const leftNumber = Number(left);
        const rightNumber = Number(right);
        const bothNumeric = String(left).trim() !== '' &&
            String(right).trim() !== '' &&
            !Number.isNaN(leftNumber) &&
            !Number.isNaN(rightNumber);

        if (operator === 'eq') {
            return bothNumeric ? leftNumber === rightNumber : String(left) === String(right);
        }
        if (operator === 'ne') {
            return bothNumeric ? leftNumber !== rightNumber : String(left) !== String(right);
        }
        if (!bothNumeric) return false;
        if (operator === 'lt') return leftNumber < rightNumber;
        if (operator === 'lte') return leftNumber <= rightNumber;
        if (operator === 'gt') return leftNumber > rightNumber;
        if (operator === 'gte') return leftNumber >= rightNumber;
        return false;
    }

    runtimeConditionPasses (sceneState, objectName, condition) {
        const objects = (sceneState && sceneState.objects) || {};
        const object = objects[objectName];
        if (!object || !condition || condition.type === 'empty' || condition.type === 'unsupported') {
            return false;
        }
        if (condition.type === 'compare') {
            return this.compareExpressionValues(
                this.evaluateExpression(sceneState, objectName, condition.left, 0),
                condition.operator,
                this.evaluateExpression(sceneState, objectName, condition.right, 0)
            );
        }
        if (condition.type === 'and') {
            return this.runtimeConditionPasses(sceneState, objectName, condition.left) &&
                this.runtimeConditionPasses(sceneState, objectName, condition.right);
        }
        if (condition.type === 'or') {
            return this.runtimeConditionPasses(sceneState, objectName, condition.left) ||
                this.runtimeConditionPasses(sceneState, objectName, condition.right);
        }
        if (condition.type === 'not') {
            return !this.runtimeConditionPasses(sceneState, objectName, condition.condition);
        }
        if (condition.type === 'truthy') {
            return Boolean(this.evaluateExpression(sceneState, objectName, condition.expression, 0));
        }
        if (condition.type === 'state') {
            return ((object.state && object.state.value) || 'normal') === (condition.stateValue || 'normal');
        }
        if (condition.type === 'collisionMode') {
            return normalizeCollisionMode(object) === (condition.collisionMode || COLLISION_MODES.SOLID);
        }
        if (condition.type === 'weather') {
            return ((sceneState.weather && sceneState.weather.type) || 'sunny') === (condition.weatherType || 'sunny');
        }
        if (condition.type === 'visible') {
            return object.visible !== false;
        }
        if (condition.type === 'objectVisible') {
            const target = this.resolveObjectTarget(sceneState, objectName, condition.target);
            return Boolean(target && target.visible !== false);
        }
        if (condition.type === 'objectState') {
            const target = this.resolveObjectTarget(sceneState, objectName, condition.target);
            return Boolean(target &&
                ((target.state && target.state.value) || 'normal') === (condition.stateValue || 'normal'));
        }
        if (condition.type === 'objectCollisionMode') {
            const target = this.resolveObjectTarget(sceneState, objectName, condition.target);
            return Boolean(target &&
                normalizeCollisionMode(target) === (condition.collisionMode || COLLISION_MODES.SOLID));
        }
        if (condition.type === 'terrainUnder') {
            const terrain = this.terrainUnderObject(sceneState, object);
            return Boolean(terrain && terrain.assetId === condition.terrainId);
        }
        if (!this.objectParticipatesInSensing(object)) {
            return false;
        }
        if (condition.type === 'touching') {
            return this.resolveSensingTargets(sceneState, objectName, condition.target)
                .some(target => this.objectsTouching(object, target));
        }
        if (condition.type === 'distanceLessThan') {
            const maxDistance = this.evaluateNumberExpression(sceneState, objectName, condition.distance, 1, 1);
            return this.resolveSensingTargets(sceneState, objectName, condition.target)
                .some(target => this.distanceBetweenObjects(object, target) < maxDistance);
        }
        if (condition.type === 'frontBlocked') {
            const direction = SCRIPT_DIRECTIONS.find(item => item.value === condition.direction) ||
                SCRIPT_DIRECTIONS.find(item => item.value === object.facingDirection) ||
                SCRIPT_DIRECTIONS[0];
            const position = object.position || {};
            const candidateGrid = {
                x: Math.round((position.x || 0) + direction.delta.x),
                y: Math.round((position.y || 0) + direction.delta.y),
                z: Math.round(position.z || 0)
            };
            const candidate = {
                ...candidateGrid,
                z: this.placementSurfaceZAtGrid(sceneState, candidateGrid, objectName)
            };
            if (!this.isInsideGrid(candidate)) return true;
            return this.gridIsBlockedForObject(sceneState, candidate, objectName);
        }
        if (condition.type === 'frontTerrain') {
            const direction = SCRIPT_DIRECTIONS.find(item => item.value === condition.direction) ||
                SCRIPT_DIRECTIONS.find(item => item.value === object.facingDirection) ||
                SCRIPT_DIRECTIONS[0];
            const position = object.position || {};
            const candidateGrid = {
                x: Math.round((position.x || 0) + direction.delta.x),
                y: Math.round((position.y || 0) + direction.delta.y),
                z: 0
            };
            if (!this.isInsideGrid(candidateGrid)) return false;
            const terrainName = this.findTerrainNameAtGrid(sceneState, candidateGrid);
            const terrain = terrainName && sceneState.objects[terrainName];
            return Boolean(terrain && terrain.assetId === condition.terrainId);
        }
        return false;
    }

    scriptConditionPasses (sceneState, objectName, script) {
        const object = sceneState.objects[objectName];
        if (!object || !script || !script.condition || script.condition === 'always') {
            return true;
        }
        if (script.condition === 'state') {
            return ((object.state && object.state.value) || 'normal') === (script.conditionState || 'normal');
        }

        const target = sceneState.objects[script.conditionTarget];
        if (!target) {
            return false;
        }
        if (!this.objectParticipatesInSensing(object) || !this.objectParticipatesInSensing(target)) {
            return false;
        }
        if (script.condition === 'touching') {
            return this.objectsTouching(object, target);
        }
        if (script.condition === 'near') {
            return this.distanceBetweenObjects(object, target) <= Math.max(1, Number(script.distance) || 1);
        }
        return true;
    }

    applyObjectScript (sceneState, objectName, script) {
        if (!this.scriptConditionPasses(sceneState, objectName, script)) {
            return sceneState;
        }
        const objects = {...sceneState.objects};
        const object = objects[objectName];
        if (!object) return sceneState;

        const nextObject = {...object};
        if (script.action === 'playAnimation') {
            const definition = this.animationDefinitionForObject(object, script.animationId);
            nextObject.animationState = script.animationId || 'idle';
            nextObject.animation = {
                id: script.animationId || 'idle',
                playing: script.animationId !== 'idle',
                startedAt: Date.now(),
                loop: definition ? Boolean(definition.loop) : false
            };
        } else if (script.action === 'stopAnimation') {
            nextObject.animationState = 'idle';
            nextObject.animation = null;
        } else if (script.action === 'setState') {
            nextObject.state = {
                ...(nextObject.state || {}),
                value: script.stateValue || 'normal'
            };
        } else if (script.action === 'setVariable') {
            const variable = this.programmingVariables(sceneState).find(item => item.value === script.variableId) ||
                {value: script.variableId || 'score'};
            nextObject.variables = {
                ...DEFAULT_OBJECT_VARIABLES,
                ...(nextObject.variables || {}),
                [variable.value]: this.evaluateNumberExpression(sceneState, objectName, script.value, 0)
            };
        } else if (script.action === 'changeVariable') {
            const variable = this.programmingVariables(sceneState).find(item => item.value === script.variableId) ||
                {value: script.variableId || 'score'};
            const currentValue = Number((nextObject.variables || {})[variable.value] || 0);
            const delta = this.evaluateNumberExpression(sceneState, objectName, script.value, 1);
            nextObject.variables = {
                ...DEFAULT_OBJECT_VARIABLES,
                ...(nextObject.variables || {}),
                [variable.value]: (Number.isNaN(currentValue) ? 0 : currentValue) + delta
            };
        } else if (script.action === 'faceDirection') {
            const direction = SCRIPT_DIRECTIONS.find(item => item.value === script.direction) || SCRIPT_DIRECTIONS[0];
            nextObject.facingDirection = direction.value;
        } else if (script.action === 'faceObject') {
            const target = this.resolveObjectTarget(sceneState, objectName, script.targetName);
            if (target) {
                const objectPosition = nextObject.position || {};
                const targetPosition = target.position || {};
                nextObject.facingDirection = this.directionForDelta(
                    Math.round((targetPosition.x || 0) - (objectPosition.x || 0)),
                    Math.round((targetPosition.y || 0) - (objectPosition.y || 0))
                ).value;
            }
        } else if (script.action === 'moveForward') {
            const direction = SCRIPT_DIRECTIONS.find(item => item.value === nextObject.facingDirection) ||
                SCRIPT_DIRECTIONS[0];
            const steps = Math.round(this.evaluateNumberExpression(sceneState, objectName, script.steps, 1, 1, 100));
            nextObject.position = {
                ...(nextObject.position || {}),
                ...this.nextMovePosition(sceneState, objectName, nextObject, direction, steps)
            };
        } else if (script.action === 'moveBy') {
            const direction = SCRIPT_DIRECTIONS.find(item => item.value === script.direction) || SCRIPT_DIRECTIONS[0];
            const steps = Math.round(this.evaluateNumberExpression(sceneState, objectName, script.steps, 1, 1, 100));
            nextObject.position = {
                ...(nextObject.position || {}),
                ...this.nextMovePosition(sceneState, objectName, nextObject, direction, steps)
            };
            nextObject.facingDirection = direction.value;
        } else if (script.action === 'moveToObject') {
            const target = this.resolveObjectTarget(sceneState, objectName, script.targetName);
            if (target) {
                const targetPosition = this.normalizedObjectGrid(target.position || {});
                if (!this.gridIsBlockedForObject(sceneState, targetPosition, objectName)) {
                    nextObject.position = {
                        ...(nextObject.position || {}),
                        ...targetPosition
                    };
                }
            }
        } else if (script.action === 'moveTowardObject' || script.action === 'moveAwayFromObject') {
            const target = this.resolveObjectTarget(sceneState, objectName, script.targetName);
            if (target) {
                const objectPosition = nextObject.position || {};
                const targetPosition = target.position || {};
                const directionMultiplier = script.action === 'moveAwayFromObject' ? -1 : 1;
                const direction = this.directionForDelta(
                    Math.round(((targetPosition.x || 0) - (objectPosition.x || 0)) * directionMultiplier),
                    Math.round(((targetPosition.y || 0) - (objectPosition.y || 0)) * directionMultiplier)
                );
                const steps = Math.round(this.evaluateNumberExpression(sceneState, objectName, script.steps, 1, 1, 100));
                nextObject.position = {
                    ...(nextObject.position || {}),
                    ...this.nextMovePosition(sceneState, objectName, nextObject, direction, steps)
                };
                nextObject.facingDirection = direction.value;
            }
        } else if (script.action === 'moveTo') {
            const targetX = this.evaluateNumberExpression(sceneState, objectName, script.targetX, 0);
            const targetY = this.evaluateNumberExpression(sceneState, objectName, script.targetY, 0);
            const targetZ = this.evaluateNumberExpression(sceneState, objectName, script.targetZ, 0);
            const candidatePosition = this.normalizedObjectGrid({
                ...(nextObject.position || {}),
                x: targetX,
                y: targetY,
                z: targetZ
            });
            if (!this.gridIsBlockedForObject(sceneState, candidatePosition, objectName)) {
                nextObject.position = candidatePosition;
            }
        } else if (script.action === 'changePositionAxis') {
            const axis = this.normalizeAxis(script.axis);
            const value = this.evaluateNumberExpression(sceneState, objectName, script.value, 1, -100, 100);
            nextObject.position = this.nextPositionByAxis(sceneState, objectName, nextObject, axis, value, true);
        } else if (script.action === 'setPositionAxis') {
            const axis = this.normalizeAxis(script.axis);
            const value = this.evaluateNumberExpression(sceneState, objectName, script.value, 0, -GRID_SIZE, GRID_SIZE);
            nextObject.position = this.nextPositionByAxis(sceneState, objectName, nextObject, axis, value, false);
        } else if (script.action === 'changeZBy') {
            const zDelta = this.evaluateNumberExpression(sceneState, objectName, script.z, 1, -100, 100);
            const candidatePosition = this.normalizedObjectGrid({
                ...(nextObject.position || {}),
                z: Math.round(Number((nextObject.position || {}).z) || 0) + zDelta
            });
            if (!this.gridIsBlockedForObject(sceneState, candidatePosition, objectName)) {
                nextObject.position = candidatePosition;
            }
        } else if (script.action === 'changeRotationAxis') {
            const axis = this.normalizeAxis(script.axis, 'z');
            const degrees = this.evaluateNumberExpression(sceneState, objectName, script.degrees, 15, -360, 360);
            const currentRotation = nextObject.rotation || {x: 0, y: 0, z: 0};
            nextObject.rotation = {
                ...currentRotation,
                [axis]: (Number(currentRotation[axis]) || 0) + degrees
            };
        } else if (script.action === 'setRotationAxis') {
            const axis = this.normalizeAxis(script.axis, 'z');
            const degrees = this.evaluateNumberExpression(sceneState, objectName, script.degrees, 0, -3600, 3600);
            nextObject.rotation = {
                ...(nextObject.rotation || {x: 0, y: 0, z: 0}),
                [axis]: degrees
            };
        } else if (script.action === 'jump') {
            const definition = this.animationDefinitionForObject(object, 'jump');
            nextObject.animationState = 'jump';
            nextObject.animation = {
                id: 'jump',
                playing: true,
                startedAt: Date.now(),
                loop: definition ? Boolean(definition.loop) : false,
                height: this.evaluateNumberExpression(sceneState, objectName, script.height, 1, 1, 10)
            };
        } else if (script.action === 'snapToSurface') {
            const objectGrid = this.objectToGrid(nextObject);
            const candidatePosition = this.normalizedObjectGrid({
                ...(nextObject.position || {}),
                z: this.placementSurfaceZAtGrid(sceneState, objectGrid, objectName)
            });
            if (!this.gridIsBlockedForObject(sceneState, candidatePosition, objectName)) {
                nextObject.position = candidatePosition;
            }
        } else if (script.action === 'show') {
            nextObject.visible = true;
        } else if (script.action === 'hide') {
            nextObject.visible = false;
        } else if (script.action === 'setWeather') {
            objects[objectName] = nextObject;
            return {
                ...sceneState,
                enabled: true,
                weather: {
                    ...(sceneState.weather || {}),
                    type: script.weatherType || 'sunny'
                },
                objects
            };
        } else if (script.action === 'setCollisionMode') {
            Object.assign(nextObject, this.objectWithCollisionMode(nextObject, script.collisionMode));
        } else if (script.action === 'setSize') {
            nextObject.scale = this.objectWithScalePercent(
                nextObject,
                this.evaluateNumberExpression(sceneState, objectName, script.sizePercent, 100, 10, 300)
            ).scale;
        } else if (script.action === 'changeSize') {
            const currentSize = this.objectScalePercent(nextObject);
            const sizeDelta = this.evaluateNumberExpression(sceneState, objectName, script.sizeDelta, 10, -300, 300);
            nextObject.scale = this.objectWithScalePercent(nextObject, currentSize + sizeDelta).scale;
        }

        objects[objectName] = nextObject;
        return {
            ...sceneState,
            enabled: true,
            objects
        };
    }

    updateSceneState (sceneState, selectedObject, recordHistory = true, markProjectChanged = true) {
        const nextSceneState = normalizeSceneState(sceneState);
        this.syncRuntimeSceneState(nextSceneState, markProjectChanged);
        this.setState(previousState => {
            const historyState = normalizeSceneState(previousState.sceneState);
            const objects = nextSceneState.objects || {};
            return {
                sceneState: nextSceneState,
                selectedObject,
                hoveredObject: objects[previousState.hoveredObject] &&
                        objects[previousState.hoveredObject].visible !== false ?
                    previousState.hoveredObject :
                    null,
                undoStack: recordHistory ?
                    [...previousState.undoStack, historyState].slice(-HISTORY_LIMIT) :
                    previousState.undoStack,
                redoStack: recordHistory ? [] : previousState.redoStack
            };
        });
    }

    syncRuntimeSceneState (sceneState, markProjectChanged = true) {
        this.props.vm.runtime.scratch3DState = clone(sceneState);
        this.isSyncingSceneState = true;
        this.props.vm.runtime.emit('SCRATCH3D_UPDATE', this.props.vm.runtime.scratch3DState);
        this.isSyncingSceneState = false;
        if (markProjectChanged && this.props.vm.runtime.emitProjectChanged) {
            this.props.vm.runtime.emitProjectChanged();
        }
    }

    resolveSelectedObject (sceneState, selectedObject) {
        return sceneState.objects && sceneState.objects[selectedObject] ? selectedObject : null;
    }

    nextObjectName (label) {
        const objects = (this.state.sceneState && this.state.sceneState.objects) || {};
        return this.nextObjectNameForObjects(label, objects);
    }

    nextObjectNameForObjects (label, objects) {
        let index = Object.keys(objects).length + 1;
        let name = `${label}${index}`;
        while (objects[name]) {
            index++;
            name = `${label}${index}`;
        }
        return name;
    }

    gridIndexToScreen (xIndex, yIndex, z) {
        const centered = {
            x: xIndex - GRID_SIZE / 2,
            y: yIndex - GRID_SIZE / 2,
            z
        };
        return this.worldToScreen(centered);
    }

    gridToIsoPoint (grid) {
        return {
            x: (grid.x - grid.y) * TILE_WIDTH / 2,
            y: (grid.x + grid.y) * TILE_HEIGHT / 2 - (grid.z * Z_HEIGHT / CELL_UNIT)
        };
    }

    worldToScreen (world) {
        const center = {
            x: this.canvas.width / 2,
            y: this.canvas.height / 2
        };
        const iso = this.gridToIsoPoint(world);
        return {
            x: center.x + this.pan.x + iso.x * this.zoom,
            y: center.y + this.pan.y + iso.y * this.zoom
        };
    }

    screenToWorld (screen) {
        const center = {
            x: this.canvas.width / 2,
            y: this.canvas.height / 2
        };
        return {
            x: (screen.x - center.x - this.pan.x) / this.zoom,
            y: (screen.y - center.y - this.pan.y) / this.zoom
        };
    }

    eventToGrid (event) {
        const rect = this.canvas.getBoundingClientRect();
        const screen = {
            x: (event.clientX - rect.left) * (this.dpr || 1),
            y: (event.clientY - rect.top) * (this.dpr || 1)
        };
        return this.screenToGrid(screen);
    }

    screenToGrid (screen) {
        const projected = this.screenToWorld(screen);
        const x = projected.x / TILE_WIDTH + projected.y / TILE_HEIGHT;
        const y = projected.y / TILE_HEIGHT - projected.x / TILE_WIDTH;
        return {
            x: clamp(Math.round(x), -GRID_SIZE / 2, GRID_SIZE / 2 - 1),
            y: clamp(Math.round(y), -GRID_SIZE / 2, GRID_SIZE / 2 - 1),
            z: 0
        };
    }

    isInsideGrid (grid) {
        return grid.x >= -GRID_SIZE / 2 &&
            grid.x < GRID_SIZE / 2 &&
            grid.y >= -GRID_SIZE / 2 &&
            grid.y < GRID_SIZE / 2;
    }

    selectedObjectInfo () {
        const selected = this.state.selectedObject;
        const objects = (this.state.sceneState && this.state.sceneState.objects) || {};
        if (!selected || !objects[selected]) return null;
        return {
            name: selected,
            ...objects[selected]
        };
    }

    objectMenuOptionsForBlocks (selectedObjectName) {
        const objects = (this.state.sceneState && this.state.sceneState.objects) || {};
        const objectOptions = Object.keys(objects)
            .filter(name => name !== selectedObjectName)
            .sort((a, b) => a.localeCompare(b))
            .map(name => [name, name]);
        return [
            ['任意对象', '*'],
            ...objectOptions,
            ['自己', 'self']
        ];
    }

    getImage (src) {
        if (!src) return null;
        if (!this.imageCache[src]) {
            const image = new window.Image();
            image.onload = this.draw;
            image.src = src;
            this.imageCache[src] = image;
        }
        return this.imageCache[src];
    }

    shade (hex, amount) {
        const color = hex.replace('#', '');
        const number = parseInt(color.length === 3 ?
            color.split('').map(value => value + value).join('') :
            color, 16);
        const red = clamp((number >> 16) + amount, 0, 255);
        const green = clamp(((number >> 8) & 0x00FF) + amount, 0, 255);
        const blue = clamp((number & 0x0000FF) + amount, 0, 255);
        return `rgb(${red}, ${green}, ${blue})`;
    }

    assetMetaLabel (asset) {
        if (asset.kind === 'terrain') return '方块地形';
        return {
            tree: '自然素材',
            grass: '地表植物',
            flower: '植物装饰',
            bush: '灌木遮挡',
            role: '可编程角色',
            crate: '交互道具',
            door: '机关组件',
            house: '建筑素材',
            bench: '休憩设施',
            lamp: '照明设施',
            fountain: '景观设施',
            sign: '导览设施',
            trashBin: '公共设施',
            image: '导入素材'
        }[asset.kind] || '场景素材';
    }

    renderAssetIcon (asset, extraClassName) {
        const assetStyle = {
            '--asset-color': asset.color,
            '--asset-top-color': asset.topColor || asset.color,
            '--asset-side-color': asset.sideColor || asset.color,
            '--asset-accent-color': asset.accentColor || '#facc15',
            ...(asset.dataUrl ? {backgroundImage: `url(${asset.dataUrl})`} : {})
        };
        const kindClassName = {
            terrain: styles.assetIconTerrain,
            tree: styles.assetIconTree,
            grass: styles.assetIconGrass,
            flower: styles.assetIconFlower,
            bush: styles.assetIconBush,
            role: styles.assetIconRole,
            crate: styles.assetIconCrate,
            door: styles.assetIconDoor,
            house: styles.assetIconHouse,
            bench: styles.assetIconBench,
            lamp: styles.assetIconLamp,
            fountain: styles.assetIconFountain,
            sign: styles.assetIconSign,
            trashBin: styles.assetIconTrashBin,
            image: styles.assetIconImage
        }[asset.kind] || styles.assetIconGeneric;
        const className = classNames(styles.assetIcon, kindClassName, extraClassName);

        if (asset.dataUrl) {
            return (
                <span
                    className={className}
                    style={assetStyle}
                />
            );
        }

        if (asset.kind === 'terrain') {
            return (
                <span
                    className={className}
                    style={assetStyle}
                >
                    <span className={styles.assetIconCubeTop} />
                    <span className={styles.assetIconCubeLeft} />
                    <span className={styles.assetIconCubeRight} />
                </span>
            );
        }

        if (asset.kind === 'tree') {
            return (
                <span
                    className={className}
                    style={assetStyle}
                >
                    <span className={styles.assetIconTreeTrunk} />
                    <span className={styles.assetIconTreeCanopy} />
                </span>
            );
        }

        if (asset.kind === 'grass') {
            return (
                <span
                    className={className}
                    style={assetStyle}
                >
                    <span className={styles.assetIconGrassBlade} />
                    <span className={styles.assetIconGrassBlade} />
                    <span className={styles.assetIconGrassBlade} />
                    <span className={styles.assetIconGrassBlade} />
                </span>
            );
        }

        if (asset.kind === 'flower') {
            return (
                <span
                    className={className}
                    style={assetStyle}
                >
                    <span className={styles.assetIconFlowerStem} />
                    <span className={styles.assetIconFlowerHead} />
                </span>
            );
        }

        if (asset.kind === 'bush') {
            return (
                <span
                    className={className}
                    style={assetStyle}
                >
                    <span className={styles.assetIconBushBlob} />
                    <span className={styles.assetIconBushBlob} />
                    <span className={styles.assetIconBushBlob} />
                </span>
            );
        }

        if (asset.kind === 'role') {
            return (
                <span
                    className={className}
                    style={assetStyle}
                >
                    <span className={styles.assetIconRoleHead} />
                    <span className={styles.assetIconRoleBody} />
                    <span className={styles.assetIconRoleLeg} />
                </span>
            );
        }

        if (asset.kind === 'door') {
            return (
                <span
                    className={className}
                    style={assetStyle}
                >
                    <span className={styles.assetIconDoorPanel} />
                    <span className={styles.assetIconDoorKnob} />
                </span>
            );
        }

        if (asset.kind === 'house') {
            return (
                <span
                    className={className}
                    style={assetStyle}
                >
                    <span className={styles.assetIconHouseRoof} />
                    <span className={styles.assetIconHouseBody} />
                    <span className={styles.assetIconHouseDoor} />
                </span>
            );
        }

        if (asset.kind === 'bench') {
            return (
                <span
                    className={className}
                    style={assetStyle}
                >
                    <span className={styles.assetIconBenchBack} />
                    <span className={styles.assetIconBenchSeat} />
                    <span className={styles.assetIconBenchLeg} />
                    <span className={styles.assetIconBenchLeg} />
                </span>
            );
        }

        if (asset.kind === 'lamp') {
            return (
                <span
                    className={className}
                    style={assetStyle}
                >
                    <span className={styles.assetIconLampGlow} />
                    <span className={styles.assetIconLampHead} />
                    <span className={styles.assetIconLampPole} />
                    <span className={styles.assetIconLampBase} />
                </span>
            );
        }

        if (asset.kind === 'fountain') {
            return (
                <span
                    className={className}
                    style={assetStyle}
                >
                    <span className={styles.assetIconFountainSpray} />
                    <span className={styles.assetIconFountainBowl} />
                    <span className={styles.assetIconFountainBase} />
                </span>
            );
        }

        if (asset.kind === 'sign') {
            return (
                <span
                    className={className}
                    style={assetStyle}
                >
                    <span className={styles.assetIconSignPost} />
                    <span className={styles.assetIconSignBoard} />
                </span>
            );
        }

        if (asset.kind === 'trashBin') {
            return (
                <span
                    className={className}
                    style={assetStyle}
                >
                    <span className={styles.assetIconTrashBinLid} />
                    <span className={styles.assetIconTrashBinBody} />
                </span>
            );
        }

        return (
            <span
                className={className}
                style={assetStyle}
            />
        );
    }

    renderSceneTemplateThumbnail (template) {
        const objectEntries = Object.entries(template.objects || {})
            .filter(([, object]) => object && object.position);
        const positions = objectEntries.map(([, object]) => object.position);
        const minX = positions.length ? Math.min(...positions.map(position => position.x || 0)) : -4;
        const maxX = positions.length ? Math.max(...positions.map(position => position.x || 0)) : 4;
        const minY = positions.length ? Math.min(...positions.map(position => position.y || 0)) : -4;
        const maxY = positions.length ? Math.max(...positions.map(position => position.y || 0)) : 4;
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;
        const terrainEntries = objectEntries.filter(([, object]) => {
            const asset = assetDefinitionForTemplateObject(object);
            return asset.kind === 'terrain' || object.kind === 'ground';
        });
        const propEntries = objectEntries
            .filter(([, object]) => {
                const asset = assetDefinitionForTemplateObject(object);
                return asset.kind !== 'terrain' && object.kind !== 'ground';
            })
            .slice(0, 8);
        const terrainStep = Math.max(1, Math.ceil(terrainEntries.length / 42));
        const sampledTerrain = terrainEntries
            .filter((entry, index) => index % terrainStep === 0)
            .slice(0, 42);
        const project = object => {
            const position = object.position || {x: 0, y: 0, z: 0};
            const x = (position.x || 0) - centerX;
            const y = (position.y || 0) - centerY;
            const zLevel = Math.max(0, Math.round((position.z || 0) / TERRAIN_BLOCK_HEIGHT));
            return {
                left: `${clamp(50 + ((x - y) * 5.4), 9, 91)}%`,
                top: `${clamp(58 + ((x + y) * 2.9) - (zLevel * 5.5), 13, 87)}%`,
                zIndex: 10 + Math.round((position.x || 0) + (position.y || 0)) + zLevel
            };
        };

        return (
            <div
                className={styles.sceneTemplateThumbnail}
                style={{'--template-background': template.background || '#dbeafe'}}
            >
                <span className={styles.sceneTemplateThumbnailGrid} />
                {sampledTerrain.map(([name, object]) => {
                    const asset = assetDefinitionForTemplateObject(object);
                    return (
                        <span
                            className={styles.sceneTemplateTerrainTile}
                            key={name}
                            style={{
                                ...project(object),
                                '--template-tile-color': object.topColor || asset.topColor || object.color || asset.color
                            }}
                        />
                    );
                })}
                {propEntries.map(([name, object]) => {
                    const asset = assetDefinitionForTemplateObject(object);
                    return (
                        <span
                            className={styles.sceneTemplateThumbnailObject}
                            key={name}
                            style={project(object)}
                            title={name}
                        >
                            {this.renderAssetIcon(asset, styles.sceneTemplateThumbnailAssetIcon)}
                        </span>
                    );
                })}
            </div>
        );
    }

    renderSceneTemplateBrowser () {
        const enabled = this.props.stageViewMode === STAGE_VIEW_MODES.threeD;
        if (!enabled || !this.state.sceneTemplateBrowserOpen || typeof document === 'undefined') {
            return null;
        }

        const filteredTemplates = this.filteredSceneTemplates();
        const activeCategory = SCENE_TEMPLATE_CATEGORIES.find(
            category => category.id === this.state.sceneTemplateCategory
        ) || SCENE_TEMPLATE_CATEGORIES[0];

        return ReactDOM.createPortal((
            <div
                className={styles.sceneTemplateBrowserOverlay}
                onMouseDown={event => event.stopPropagation()}
            >
                <div className={styles.sceneTemplateBrowserHeader}>
                    <button
                        className={styles.sceneTemplateBrowserBack}
                        onClick={this.closeSceneTemplateBrowser}
                        type="button"
                    >
                        返回
                    </button>
                    <div className={styles.sceneTemplateBrowserTitle}>选择一个场景</div>
                    <div className={styles.sceneTemplateBrowserCount}>
                        {filteredTemplates.length} 个模板
                    </div>
                </div>
                <div className={styles.sceneTemplateBrowserControls}>
                    <label className={styles.sceneTemplateSearchBox}>
                        <span>搜索</span>
                        <input
                            onChange={this.updateSceneTemplateSearch}
                            placeholder="输入场景、素材或教学关键词"
                            type="search"
                            value={this.state.sceneTemplateSearch}
                        />
                    </label>
                    <div
                        aria-label="场景分类"
                        className={styles.sceneTemplateCategoryList}
                        role="tablist"
                    >
                        {SCENE_TEMPLATE_CATEGORIES.map(category => (
                            <button
                                aria-selected={category.id === activeCategory.id}
                                className={classNames(styles.sceneTemplateCategoryPill, {
                                    [styles.sceneTemplateCategoryPillActive]: category.id === activeCategory.id
                                })}
                                key={category.id}
                                onClick={() => this.selectSceneTemplateCategory(category.id)}
                                role="tab"
                                type="button"
                            >
                                {category.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className={styles.sceneTemplateBrowserBody}>
                    {filteredTemplates.length ? (
                        <div className={styles.sceneTemplateBrowserGrid}>
                            {filteredTemplates.map(template => (
                                <button
                                    className={styles.sceneTemplateCard}
                                    key={template.id}
                                    onClick={() => this.loadSceneTemplate(template.id)}
                                    type="button"
                                >
                                    {this.renderSceneTemplateThumbnail(template)}
                                    <span className={styles.sceneTemplateCardName}>{template.label}</span>
                                    <span className={styles.sceneTemplateCardDescription}>
                                        {template.description}
                                    </span>
                                    <span className={styles.sceneTemplateCardTags}>
                                        {(template.tags || []).slice(0, 3).join(' / ')}
                                    </span>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className={styles.sceneTemplateEmpty}>
                            没有找到匹配的场景模板
                        </div>
                    )}
                </div>
            </div>
        ), document.body);
    }

    renderSceneQuickToolbar () {
        const enabled = this.props.stageViewMode === STAGE_VIEW_MODES.threeD;
        if (!enabled || !this.quickToolbarHost) {
            return null;
        }

        const selectedAsset = this.getAssetById(this.state.selectedAssetId);
        const canUseTerrainTools = this.isTerrainAsset(selectedAsset);
        const quickTools = [
            EDIT_TOOLS.SELECT,
            EDIT_TOOLS.PLACE,
            EDIT_TOOLS.PAINT,
            EDIT_TOOLS.ERASE
        ];

        return ReactDOM.createPortal((
            <div
                className={styles.stageQuickToolbar}
                onMouseDown={event => event.stopPropagation()}
            >
                <button
                    className={styles.stageQuickAsset}
                    onClick={() => this.selectWorkbenchTab(WORKBENCH_TABS.ASSETS)}
                    type="button"
                >
                    {this.renderAssetIcon(selectedAsset, styles.stageQuickAssetIcon)}
                    <span>{selectedAsset.label}</span>
                </button>
                {quickTools.map(tool => {
                    const terrainTool = tool === EDIT_TOOLS.PAINT ||
                        tool === EDIT_TOOLS.RAISE_TERRAIN ||
                        tool === EDIT_TOOLS.REPLACE ||
                        tool === EDIT_TOOLS.FILL;
                    const disabled = terrainTool && !canUseTerrainTools;
                    return (
                        <button
                            className={classNames(styles.stageQuickButton, {
                                [styles.stageQuickButtonActive]: tool === this.state.activeTool
                            })}
                            disabled={disabled}
                            key={tool}
                            onClick={() => this.selectTool(tool)}
                            type="button"
                        >
                            {EDIT_TOOL_LABELS[tool]}
                        </button>
                    );
                })}
                <div className={styles.stageQuickBrushGroup}>
                    <span className={styles.stageQuickBrushLabel}>画笔</span>
                    {BRUSH_SIZE_OPTIONS.map(size => (
                        <button
                            className={classNames(styles.stageQuickBrushButton, {
                                [styles.stageQuickBrushButtonActive]: size === this.normalizeBrushSize(this.state.brushSize)
                            })}
                            key={size}
                            onClick={() => this.selectBrushSize(size)}
                            type="button"
                        >
                            {size}格
                        </button>
                    ))}
                </div>
                <span className={styles.stageQuickDivider} />
                {this.state.localSaveStatus ? (
                    <div className={this.statusClassName(styles.stageQuickStatus)}>
                        {this.state.localSaveStatus}
                    </div>
                ) : null}
                <button
                    className={styles.stageQuickButton}
                    onClick={() => {
                        this.activateSceneWorkbench();
                        this.resetBestView();
                    }}
                    type="button"
                >
                    最佳视图
                </button>
                <button
                    className={styles.stageQuickButton}
                    disabled={!this.state.undoStack.length}
                    onClick={() => {
                        this.activateSceneWorkbench();
                        this.undo();
                    }}
                    type="button"
                >
                    撤回
                </button>
                <button
                    className={styles.stageQuickButton}
                    disabled={!this.state.redoStack.length}
                    onClick={() => {
                        this.activateSceneWorkbench();
                        this.redo();
                    }}
                    type="button"
                >
                    恢复
                </button>
            </div>
        ), this.quickToolbarHost);
    }

    renderEditorPanel () {
        const enabled = this.props.stageViewMode === STAGE_VIEW_MODES.threeD;
        if (!enabled || !this.controlsHost) return null;

        const selectedObject = this.selectedObjectInfo();
        const selectedGrid = selectedObject ? this.objectToGrid(selectedObject) : null;
        const selectedAnimations = selectedObject ? this.animationOptionsForObject(selectedObject) : [];
        const interactionTemplates = selectedObject ? this.interactionTemplatesForObject(selectedObject) : [];
        const assetGroups = this.getAssetGroups();
        const selectedAsset = this.getAssetById(this.state.selectedAssetId);
        const canUseTerrainTools = this.isTerrainAsset(selectedAsset);
        const selectionCount = this.selectionObjectCount();
        const clipboardCount = this.clipboardObjectCount();
        const programmingVariables = this.programmingVariables();
        const programmingMessages = this.programmingMessages();
        const activeCodeGroup = THREE_D_BLOCK_PALETTE.find(
            group => group.id === this.state.activeCodeCategory
        ) || THREE_D_BLOCK_PALETTE[0];
        const commonToolButtons = [
            EDIT_TOOLS.SELECT,
            EDIT_TOOLS.PLACE,
            EDIT_TOOLS.PAINT,
            EDIT_TOOLS.ERASE
        ];
        const terrainToolButtons = [
            EDIT_TOOLS.RAISE_TERRAIN,
            EDIT_TOOLS.LOWER_TERRAIN,
            EDIT_TOOLS.REPLACE,
            EDIT_TOOLS.FILL
        ];
        const activeToolLabel = EDIT_TOOL_LABELS[this.state.activeTool] || '选择';
        const clipboardCountText = clipboardCount ? `${clipboardCount} 个` : '空';

        return ReactDOM.createPortal((
            <div className={styles.editorPanel}>
                <div className={styles.workbenchTabs}>
                    {Object.keys(WORKBENCH_TABS).map(key => {
                        const tab = WORKBENCH_TABS[key];
                        return (
                            <button
                                className={classNames(styles.workbenchTab, {
                                    [styles.workbenchTabActive]: this.state.activeWorkbenchTab === tab
                                })}
                                key={tab}
                                onClick={() => this.selectWorkbenchTab(tab)}
                                type="button"
                            >
                                {WORKBENCH_TAB_LABELS[tab]}
                            </button>
                        );
                    })}
                </div>
                {this.state.activeWorkbenchTab === WORKBENCH_TABS.SCENE ? (
                    <div
                        className={styles.sceneEditorPanel}
                        onMouseDown={event => event.stopPropagation()}
                    >
                        <div className={styles.editorPanelHeader}>
                            <div className={styles.panelTitle}>场景编辑</div>
                            <div className={styles.panelMeta}>50 x 50 网格</div>
                        </div>
                        <div className={styles.sceneStatusCard}>
                            <div>
                                <span>当前素材</span>
                                <strong>{selectedAsset.label}</strong>
                            </div>
                            <div>
                                <span>当前工具</span>
                                <strong>{activeToolLabel}</strong>
                            </div>
                            <div>
                                <span>选区对象</span>
                                <strong>{selectionCount} 个</strong>
                            </div>
                            <div>
                                <span>剪贴板</span>
                                <strong>{clipboardCountText}</strong>
                            </div>
                        </div>
                        <div className={styles.sceneToolSection}>
                            <div className={styles.sceneToolSectionHeader}>
                                <span>常用工具</span>
                                <em>搭建时最高频</em>
                            </div>
                            <div className={styles.sceneToolGrid}>
                                {commonToolButtons.map(tool => {
                                    const terrainTool = tool === EDIT_TOOLS.PAINT;
                                    const disabled = terrainTool && !canUseTerrainTools;
                                    return (
                                        <button
                                            className={classNames(styles.sceneToolButton, {
                                                [styles.sceneToolButtonActive]: tool === this.state.activeTool
                                            })}
                                            disabled={disabled}
                                            key={tool}
                                            onClick={() => this.selectTool(tool)}
                                            type="button"
                                        >
                                            {EDIT_TOOL_LABELS[tool]}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className={styles.sceneInlineControl}>
                                <span>画笔大小</span>
                                <div className={styles.toolbarBrushGroup}>
                                    {BRUSH_SIZE_OPTIONS.map(size => (
                                        <button
                                            className={classNames(styles.toolbarBrushButton, {
                                                [styles.toolbarBrushButtonActive]: size === this.normalizeBrushSize(this.state.brushSize)
                                            })}
                                            key={size}
                                            onClick={() => this.selectBrushSize(size)}
                                            type="button"
                                        >
                                            {size}格
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <details className={styles.sceneToolSection}>
                            <summary className={styles.sceneToolSummary}>
                                <span>地形工具</span>
                                <em>升高、降低和批量编辑</em>
                            </summary>
                            <div className={styles.sceneToolGrid}>
                                {terrainToolButtons.map(tool => {
                                    const terrainTool = tool === EDIT_TOOLS.RAISE_TERRAIN ||
                                        tool === EDIT_TOOLS.REPLACE ||
                                        tool === EDIT_TOOLS.FILL;
                                    const disabled = terrainTool && !canUseTerrainTools;
                                    return (
                                        <button
                                            className={classNames(styles.sceneToolButton, {
                                                [styles.sceneToolButtonActive]: tool === this.state.activeTool
                                            })}
                                            disabled={disabled}
                                            key={tool}
                                            onClick={() => this.selectTool(tool)}
                                            type="button"
                                        >
                                            {EDIT_TOOL_LABELS[tool]}
                                        </button>
                                    );
                                })}
                            </div>
                        </details>
                        <details className={styles.sceneToolSection}>
                            <summary className={styles.sceneToolSummary}>
                                <span>选区操作</span>
                                <em>复制、移动和粘贴区域</em>
                            </summary>
                            <div className={styles.sceneToolGrid}>
                                <button
                                    className={styles.sceneToolButton}
                                    disabled={!selectionCount}
                                    onClick={() => this.copySelectionToClipboard(false)}
                                    type="button"
                                >
                                    复制
                                </button>
                                <button
                                    className={styles.sceneToolButton}
                                    disabled={!selectionCount}
                                    onClick={() => this.copySelectionToClipboard(true)}
                                    type="button"
                                >
                                    剪切
                                </button>
                                <button
                                    className={classNames(styles.sceneToolButton, {
                                        [styles.sceneToolButtonActive]: this.state.activeTool === EDIT_TOOLS.MOVE_SELECTION
                                    })}
                                    disabled={!selectionCount}
                                    onClick={() => this.selectSelectionMoveTool()}
                                    type="button"
                                >
                                    移动
                                </button>
                                <button
                                    className={classNames(styles.sceneToolButton, {
                                        [styles.sceneToolButtonActive]: this.state.activeTool === EDIT_TOOLS.PASTE
                                    })}
                                    disabled={!clipboardCount}
                                    onClick={() => this.selectPasteTool()}
                                    type="button"
                                >
                                    粘贴
                                </button>
                                <button
                                    className={styles.sceneToolButton}
                                    disabled={!this.state.selectionBounds}
                                    onClick={() => this.clearSelectionBounds()}
                                    type="button"
                                >
                                    清除
                                </button>
                            </div>
                        </details>
                        <details className={styles.sceneToolSection}>
                            <summary className={styles.sceneToolSummary}>
                                <span>视图和草稿</span>
                                <em>保存、读取和历史操作</em>
                            </summary>
                            <div className={styles.sceneToolGrid}>
                                <button
                                    className={styles.sceneToolButton}
                                    onClick={this.resetBestView}
                                    type="button"
                                >
                                    最佳视图
                                </button>
                                <button
                                    className={styles.sceneToolButton}
                                    disabled={!this.state.undoStack.length}
                                    onClick={this.undo}
                                    type="button"
                                >
                                    撤回
                                </button>
                                <button
                                    className={styles.sceneToolButton}
                                    disabled={!this.state.redoStack.length}
                                    onClick={this.redo}
                                    type="button"
                                >
                                    恢复
                                </button>
                                <button
                                    className={styles.sceneToolButton}
                                    onClick={this.saveSceneToLocalDraft}
                                    type="button"
                                >
                                    保存草稿
                                </button>
                                <button
                                    className={styles.sceneToolButton}
                                    onClick={this.loadSceneFromLocalDraft}
                                    type="button"
                                >
                                    读取草稿
                                </button>
                            </div>
                        </details>
                        {this.state.localSaveStatus ? (
                            <div className={this.statusClassName(styles.localSaveStatus)}>
                                {this.state.localSaveStatus}
                            </div>
                        ) : null}
                        <div className={styles.propertySectionTitle}>场景模板</div>
                        <button
                            className={styles.sceneTemplateOpenButton}
                            onClick={this.openSceneTemplateBrowser}
                            type="button"
                        >
                            <span className={styles.sceneTemplateOpenPreview}>
                                {this.renderSceneTemplateThumbnail(SCENE_TEMPLATES[3] || SCENE_TEMPLATES[0])}
                            </span>
                            <span className={styles.sceneTemplateOpenText}>
                                <strong>选择场景模板</strong>
                                <em>{SCENE_TEMPLATES.length} 个模板，可搜索和按分类筛选</em>
                            </span>
                        </button>
                    </div>
                ) : null}
                {this.state.activeWorkbenchTab === WORKBENCH_TABS.ASSETS ? (
                    <div
                        className={styles.assetPanel}
                        onMouseDown={event => event.stopPropagation()}
                    >
                        <div className={styles.panelTitle}>地形与素材</div>
                        {assetGroups.map(group => (
                            <div
                                className={styles.assetGroup}
                                key={group.id}
                            >
                                <div className={styles.assetGroupTitle}>
                                    <span>{group.label}</span>
                                    <em>{group.assets.length} 个</em>
                                </div>
                                <div className={styles.assetGrid}>
                                    {group.assets.map(asset => (
                                        <button
                                            className={classNames(styles.assetButton, {
                                                [styles.assetButtonSelected]: asset.id === this.state.selectedAssetId
                                            })}
                                            key={asset.id}
                                            onClick={() => this.selectAsset(asset.id)}
                                            style={{
                                                '--asset-color': asset.color,
                                                '--asset-top-color': asset.topColor || asset.color,
                                                '--asset-side-color': asset.sideColor || asset.color
                                            }}
                                            type="button"
                                        >
                                            <span
                                                aria-hidden="true"
                                                className={styles.assetPreview}
                                            >
                                                <span className={styles.assetPreviewGrid} />
                                                <span className={styles.assetPreviewShadow} />
                                                {this.renderAssetIcon(asset)}
                                            </span>
                                            <span className={styles.assetButtonInfo}>
                                                <span className={styles.assetButtonLabel}>{asset.label}</span>
                                                <span className={styles.assetButtonMeta}>
                                                    {this.assetMetaLabel(asset)}
                                                </span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                        <button
                            className={styles.importButton}
                            onClick={this.handleImportClick}
                            type="button"
                        >
                            导入素材
                        </button>
                        <input
                            accept="image/*"
                            className={styles.importInput}
                            onChange={this.handleImportChange}
                            ref={this.setImportInput}
                            type="file"
                        />
                    </div>
                ) : null}
                {this.state.activeWorkbenchTab === WORKBENCH_TABS.PROPERTIES && selectedObject ? (
                    <div
                        className={styles.propertyPanel}
                        onMouseDown={event => event.stopPropagation()}
                    >
                        <div className={styles.panelTitle}>{selectedObject.name}</div>
                        <div className={styles.propertyGrid}>
                            <label>
                                <span>x</span>
                                <input
                                    onChange={event => this.updateSelectedPosition('x', event.target.value)}
                                    type="number"
                                    value={selectedGrid.x}
                                />
                            </label>
                            <label>
                                <span>y</span>
                                <input
                                    onChange={event => this.updateSelectedPosition('y', event.target.value)}
                                    type="number"
                                    value={selectedGrid.y}
                                />
                            </label>
                            <label>
                                <span>z</span>
                                <input
                                    onChange={event => this.updateSelectedPosition('z', event.target.value)}
                                    type="number"
                                    value={selectedGrid.z}
                                />
                            </label>
	                            <label>
	                                <span>颜色</span>
	                                <input
	                                    onChange={event => this.updateSelectedColor(event.target.value)}
	                                    type="color"
	                                    value={selectedObject.color || '#4E97FF'}
	                                />
	                            </label>
	                            <label>
	                                <span>大小%</span>
	                                <input
	                                    max="300"
	                                    min="10"
	                                    onChange={event => this.updateSelectedScale(event.target.value)}
	                                    type="number"
	                                    value={this.objectScalePercent(selectedObject)}
	                                />
	                            </label>
	                            <label>
	                                <span>状态</span>
	                                <select
	                                    onChange={event => this.updateSelectedState(event.target.value)}
	                                    value={(selectedObject.state && selectedObject.state.value) || 'normal'}
	                                >
	                                    {OBJECT_STATES.map(state => (
	                                        <option
	                                            key={state.value}
	                                            value={state.value}
	                                        >
	                                            {state.label}
	                                        </option>
	                                    ))}
	                                </select>
	                            </label>
	                            <label>
	                                <span>碰撞</span>
	                                <select
	                                    onChange={event => this.updateSelectedCollisionMode(event.target.value)}
	                                    value={selectedObject.collisionMode || normalizeCollisionMode(selectedObject)}
	                                >
	                                    {Object.keys(COLLISION_MODE_LABELS).map(mode => (
	                                        <option
	                                            key={mode}
	                                            value={mode}
	                                        >
	                                            {COLLISION_MODE_LABELS[mode]}
	                                        </option>
	                                    ))}
	                                </select>
	                            </label>
	                            <label>
	                                <span>动画</span>
	                                <select
	                                    onChange={event => this.playSelectedAnimation(event.target.value)}
	                                    value={selectedObject.animationState || 'idle'}
	                                >
	                                    {selectedAnimations.map(animation => (
	                                        <option
	                                            key={animation.id}
	                                            value={animation.id}
	                                        >
	                                            {animation.label || animation.id}
	                                        </option>
	                                    ))}
	                                </select>
	                            </label>
	                        </div>
		                        <div className={styles.propertyHint}>
		                            {COLLISION_MODE_DESCRIPTIONS[
		                                selectedObject.collisionMode || normalizeCollisionMode(selectedObject)
		                            ]}
		                        </div>
                            {interactionTemplates.length ? (
                                <React.Fragment>
                                    <div className={styles.propertySectionTitle}>交互模板</div>
                                    <div className={styles.interactionTemplateList}>
                                        {interactionTemplates.map(template => (
                                            <button
                                                aria-label={template.description || template.label}
                                                className={styles.interactionTemplateButton}
                                                key={template.id}
                                                onClick={() => this.applyInteractionTemplate(template.id)}
                                                title={template.description}
                                                type="button"
                                            >
                                                {template.label}
                                            </button>
                                        ))}
                                    </div>
                                </React.Fragment>
                            ) : null}
		                        <div className={styles.propertySectionTitle}>变量</div>
		                        <div className={styles.propertyGrid}>
			                            {programmingVariables.map(variable => (
			                                <label key={variable.value}>
			                                    <span>{variable.label}</span>
			                                    <input
		                                        onChange={
		                                            event => this.updateSelectedVariable(variable.value, event.target.value)
		                                        }
			                                        type="number"
			                                        value={
			                                            (selectedObject.variables || DEFAULT_OBJECT_VARIABLES)[variable.value] || 0
			                                        }
			                                    />
			                                </label>
			                            ))}
			                        </div>
			                        <div className={styles.propertyActions}>
			                            <button
			                                className={styles.toolbarButton}
			                                onClick={this.addProgrammingVariable}
			                                type="button"
			                            >
			                                新建变量
			                            </button>
			                            <button
			                                className={styles.toolbarButton}
			                                onClick={this.renameProgrammingVariable}
			                                type="button"
			                            >
			                                重命名变量
			                            </button>
			                            <button
			                                className={styles.toolbarButton}
			                                onClick={this.deleteProgrammingVariable}
			                                type="button"
			                            >
			                                删除变量
			                            </button>
			                        </div>
			                        <div className={styles.propertyActions}>
			                            <button
		                                className={styles.toolbarButton}
	                                onClick={() => this.playSelectedAnimation(selectedObject.animationState || 'idle')}
	                                type="button"
	                            >
	                                播放动画
	                            </button>
	                            <button
	                                className={styles.toolbarButton}
	                                onClick={this.stopSelectedAnimation}
	                                type="button"
	                            >
	                                停止动画
	                            </button>
	                        </div>
	                        <button
	                            className={styles.deleteButton}
                            onClick={this.deleteSelectedObject}
                            onMouseDown={event => event.stopPropagation()}
                            type="button"
                        >
                            删除
                        </button>
                    </div>
                ) : null}
                {this.state.activeWorkbenchTab === WORKBENCH_TABS.PROPERTIES && !selectedObject ? (
                    <div className={styles.emptyPanel}>请选择一个素材后编辑属性。</div>
                ) : null}
                {this.state.activeWorkbenchTab === WORKBENCH_TABS.CODE ? (
                    <div
                        className={styles.codePanel}
                        onMouseDown={event => event.stopPropagation()}
                    >
	                        <div className={styles.panelTitle}>对象编程</div>
	                        {selectedObject ? (
	                            <div className={styles.codePalettePanel}>
	                                <div className={styles.codePaletteLayout}>
                                    <div className={styles.blockWorkspace}>
                                        <div className={styles.blockGroup}>
                                            <div className={styles.blockGroupTitle}>{activeCodeGroup.label}</div>
                                            {activeCodeGroup.blocks.map(block => (
                                                <button
                                                    className={classNames(
                                                        styles.scratchBlock,
                                                        styles[activeCodeGroup.blockClassName],
                                                        styles[block.shapeClassName || 'statementBlock']
                                                    )}
                                                    aria-label={block.label}
                                                    key={`${activeCodeGroup.id}-${block.label}`}
                                                    onMouseDown={
                                                        event => this.handleCodeBlockDragStart(block, activeCodeGroup, event)
                                                    }
                                                    type="button"
                                                >
                                                    {this.renderCodeBlockParts(block)}
                                                </button>
                                            ))}
                                            {activeCodeGroup.id === 'messages' ? (
                                                <div className={styles.messageDefinitionPanel}>
                                                    <div className={styles.blockGroupTitle}>可选消息</div>
                                                    <div className={styles.messageBlockList}>
                                                        {programmingMessages.map(message => (
                                                            <span
                                                                className={classNames(
                                                                    styles.scratchBlock,
                                                                    styles.eventBlock,
                                                                    styles.statementBlock,
                                                                    styles.messagePreviewBlock
                                                                )}
                                                                key={message.value}
                                                            >
                                                                <span className={styles.scratchBlockText}>消息</span>
                                                                <span className={styles.scratchBlockValue}>{message.label}</span>
                                                            </span>
                                                        ))}
                                                    </div>
                                                    <div className={styles.messageActionRow}>
                                                        <button
                                                            className={styles.toolbarButton}
                                                            onClick={this.addProgrammingMessage}
                                                            type="button"
                                                        >
                                                            新建消息
                                                        </button>
                                                        <button
                                                            className={styles.toolbarButton}
                                                            onClick={this.renameProgrammingMessage}
                                                            type="button"
                                                        >
                                                            重命名消息
                                                        </button>
                                                        <button
                                                            className={styles.toolbarButton}
                                                            onClick={this.deleteProgrammingMessage}
                                                            type="button"
                                                        >
                                                            删除消息
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className={styles.codeCategoryRail}>
                                        {THREE_D_BLOCK_PALETTE.map(group => (
                                            <button
                                                aria-pressed={this.state.activeCodeCategory === group.id}
                                                className={classNames(styles.codeCategoryButton, {
                                                    [styles.codeCategoryButtonActive]:
                                                        this.state.activeCodeCategory === group.id
                                                })}
                                                key={group.id}
                                                onClick={() => this.selectCodeCategory(group.id)}
                                                onMouseDown={event => event.stopPropagation()}
                                                type="button"
                                            >
                                                {group.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.emptyPanel}>未选择素材</div>
                        )}
                    </div>
                ) : null}
            </div>
        ), this.controlsHost);
    }

    renderCodeWorkspace () {
        const enabled = this.props.stageViewMode === STAGE_VIEW_MODES.threeD;
        if (!enabled ||
                !this.codeHost ||
                this.props.stage3DWorkspaceMode !== 'code' ||
                this.state.activeWorkbenchTab !== WORKBENCH_TABS.CODE) {
            return null;
        }

        const selectedObject = this.selectedObjectInfo();
        return ReactDOM.createPortal((
            <div
                className={styles.codeWorkspacePanel}
                onMouseDown={event => event.stopPropagation()}
            >
                {selectedObject ? (
                    <Stage3DBlockWorkspace
                        ref={this.blockWorkspaceRef}
                        media="static/blocks-media/default/"
                        selectedObjectId={selectedObject.id || selectedObject.name}
	                        selectedObjectName={selectedObject.name}
	                        showToolbox={false}
	                        vm={this.props.vm}
	                        messageMenuOptions={this.messageMenuOptionsForBlocks()}
	                        objectMenuOptions={this.objectMenuOptionsForBlocks(selectedObject.name)}
	                        variableMenuOptions={this.variableMenuOptionsForBlocks()}
	                        workspaceXml={selectedObject.blockWorkspaceXml}
                        onChange={this.updateSelectedBlocksXml}
                    />
                ) : (
                    <div className={styles.codeWorkspaceEmpty}>未选择素材</div>
                )}
            </div>
        ), this.codeHost);
    }

    renderCodeBlockDragPreview () {
        const drag = this.state.draggingCodeBlock;
        if (!drag || typeof document === 'undefined') return null;

        return ReactDOM.createPortal((
            <div
                className={classNames(
                    styles.scratchBlock,
                    styles[drag.group.blockClassName],
                    styles[drag.block.shapeClassName || 'statementBlock'],
                    styles.draggingScratchBlock
                )}
                style={{
                    left: drag.currentX - (drag.grabOffsetX || 0),
                    top: drag.currentY - (drag.grabOffsetY || 0)
                }}
            >
                {this.renderCodeBlockParts(drag.block)}
            </div>
        ), document.body);
    }

    render () {
        const enabled = this.props.stageViewMode === STAGE_VIEW_MODES.threeD;
        return (
            <div
                className={classNames(styles.stage3d, {
                    [styles.active]: enabled
                })}
            >
                <canvas
                    className={classNames(styles.canvas, {
                        [styles.objectHovering]: Boolean(this.state.hoveredObject) && !this.isPanning,
                        [styles.panning]: this.isPanning
                    })}
                    onClick={this.handleClick}
                    onMouseDown={this.handleMouseDown}
                    onMouseLeave={this.handleMouseLeave}
                    ref={this.setCanvas}
                />
                {this.renderEditorPanel()}
                {this.renderCodeWorkspace()}
                {this.renderCodeBlockDragPreview()}
                {this.renderSceneQuickToolbar()}
                {this.renderSceneTemplateBrowser()}
            </div>
        );
    }
}

Stage3D.propTypes = {
    onSet3DWorkspaceMode: PropTypes.func,
    stage3DWorkspaceMode: PropTypes.string,
    stageViewMode: PropTypes.oneOf(Object.keys(STAGE_VIEW_MODES)).isRequired,
    vm: PropTypes.shape({
        runtime: PropTypes.shape({
            emit: PropTypes.func,
            on: PropTypes.func.isRequired,
            removeListener: PropTypes.func.isRequired,
            scratch3DState: PropTypes.shape({})
        }).isRequired
    }).isRequired
};

export default Stage3D;
